import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useIsAdminUser } from '../hooks/useIsAdminUser';
import { tableService, GameTableData, GameState } from '../services/tableService';
import * as tableServiceFns from '../services/tableService';
import GameStateManager from '../services/gameStateManager';
import userService from '../services/userService';
import gameProgressionService from '../services/gameProgressionService';
import walletService from '../services/walletService';
//  ENTERPRISE: Import secure API service

import gameApiService from '../services/gameApiService';
//  GEO SERVICE: For passing state code to PSS Coins table join/rebuy

import geoService from '../services/geoService';
//  iOS: Block voice/video tables on iOS (Safari, Chrome, native)

import { isIOSVoiceVideoBlocked } from '../utils/iosUtils';
import PokerTable from '../components/PokerTable';
import VoiceChatBar from '../components/game/VoiceChatBar';
import MediaChatBar from '../components/game/MediaChatBar';
import '../styles/PokerTable.css';
import '../styles/GamePage.css';
import '../styles/VoiceChatBar.css';
import '../styles/MediaChatBar.css';
import UserSearch from '../components/UserSearch';
import WatchInviteModal from '../components/WatchInviteModal';
import HandHistoryDrawer from '../components/HandHistoryDrawer';
import RealTimeResultsSheet from '../components/RealTimeResultsSheet';
import { Volume2, VolumeX, Mic, MicOff, Gift, Info, Menu as MenuIcon, History as HistoryIcon } from 'lucide-react';
import BuyInModal from '../components/BuyInModal';
import HouseBotPrompt from '../components/game/HouseBotPrompt';
import { houseBotService } from '../services/houseBotService';
import { RecordingBadge, RecordingButton, RecordingMenuItem, RecordingConsentGate, RecordingCardsToggle } from '../components/recording/TableRecording';

// ============================================================================
//  PREMIUM TABLE - Premium themed table with tip dealer

// ============================================================================
import PremiumThemedTableSVG from '../components/PremiumThemedTableSVG';
import '../components/PremiumThemedTableSVG.css';
import TipDealerModal, { TipAnimationOverlay } from '../components/TipDealerModal';
import tipDealerService, { TipAnimationData, TipCurrency } from '../services/tipDealerService';
import '../components/TipDealerModal.css';
import TournamentSidebar from '../components/tournament/TournamentSidebar';
import TableClosedOverlay from '../components/tournament/TableClosedOverlay';
import SpectatorBanner from '../components/tournament/SpectatorBanner';
import AdminTableControls from '../components/tournament/AdminTableControls';
import TournamentStartCountdown from '../components/tournament/TournamentStartCountdown';
import TournamentBustModal from '../components/tournament/TournamentBustModal';
import TournamentCinematic from '../components/tournament/TournamentCinematic';
import LateRegBadge from '../components/tournament/LateRegBadge';
import PlayerProfileSheet, { getSeatTag } from '../components/PlayerProfileSheet';

// ============================================================================
//  INVITE SERVICE - Process invite tokens on game page load

// ============================================================================
import { 
  consumeInviteToken, 
  getPendingInviteToken, 
  clearPendingInvite 
} from '../services/inviteService';

// ============================================================================
//  SESSION SERVICE - Track buy-ins/cash-outs across table restarts

// ============================================================================
import sessionService, { GameSession, SessionExport } from '../services/sessionService';
import { playTableSfx, stopAllTableSfx } from '../utils/tableSfx';

// ============================================================================
//  SOUND SYSTEM - Uses MP3 files in public/sounds/

// Maps action names to actual filenames (some have long names from downloads)
// ============================================================================
// All table sounds go through utils/tableSfx, which stops the same sound
// stacking on itself (three callers announcing one new hand), trims the
// long deal shuffle to the moment the cards land, and uses a short card
// drop for each street instead of the full shuffle.

//  Premium tables use Sofia voice ONLY - no SFX from GamePage

// The isPremium parameter disables all sounds for premium tables
const playSound = (soundName: string, enabled: boolean, isPremium: boolean = false) => {
  //  PREMIUM: Sofia voice handles ALL audio - skip GamePage sounds entirely

  if (isPremium) return;
  if (!enabled) return;
  playTableSfx(soundName, true);
};

interface PlayerDisplay {
  uid: string;
  username: string;
  isHost: boolean;
  chips?: number;
  isCurrentUser?: boolean;
  isReady?: boolean;
}

const GamePage: React.FC = () => {
  const { tableId } = useParams<{ tableId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); //  For invite token in URL
  // Headless recorder view (?rec=1): renders the table from the recording owner's seat when they chose to show cards
  const isRecorderView = searchParams.get('rec') === '1';

  const currentUserId = auth.currentUser?.uid || '';
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const gameStateManagerRef = useRef<GameStateManager | null>(null);
  const lastRoundRef = useRef<string | null>(null);
  const readyCountdownRef = useRef<NodeJS.Timeout | null>(null);
  //  Track if any player went all-in this hand (p.allIn flag gets cleared by backend)

  const hadAllInThisHandRef = useRef<boolean>(false);
  const lastAllInCheckHandRef = useRef<number | null>(null);
  //  Track the last non-showdown round (to know how many cards were already shown)

  const lastPlayingRoundRef = useRef<string>('preflop');
  //  Track if invite has been processed

  const inviteProcessedRef = useRef<boolean>(false);
  //  Track last hand number we scheduled bot auto-restart for

  const botAutoRestartHandRef = useRef<number | null>(null);
  //  Track whether user has already made their bot choice (play or dismiss)

  const botChoiceMadeRef = useRef<boolean>(false);

  // ===== STATE =====
  const [table, setTable] = useState<GameTableData | null>(null);
  const [playerList, setPlayerList] = useState<PlayerDisplay[]>([]);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [isHost, setIsHost] = useState(false);
  const [gameInProgress, setGameInProgress] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStartingGame, setIsStartingGame] = useState(false);
  const [usernameMap, setUsernameMap] = useState<{ [uid: string]: string }>({});
  const [userPhotosMap, setUserPhotosMap] = useState<{ [uid: string]: string | undefined }>({});  //  NEW: Avatar photos

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showWatchInviteModal, setShowWatchInviteModal] = useState(false);
  const [showHandHistory, setShowHandHistory] = useState(false);
  // Mobile chrome: phones hide the desktop header and get a compact bar
  // whose menu sheet holds History/Reload/sound/mic/Back/Leave.
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  // Real Time Results: per-player session buy-in vs winnings (cash tables)
  const [showRealTimeResults, setShowRealTimeResults] = useState(false);
  // 🏠 HOME GAME (host-banked session chips): host assigns chips in-app
  const isSessionTable = (table as any)?.chipMode === 'session';
  const isTableHost = !!table && (table as any)?.ownerId === currentUserId;
  const [showAssignChips, setShowAssignChips] = useState(false);
  const [assignTarget, setAssignTarget] = useState<string>('');
  // 🏠 Host controls: kick / pause / blinds / end game
  const [showKickSheet, setShowKickSheet] = useState(false);
  const [kickBusy, setKickBusy] = useState(false);
  const [showBlindsSheet, setShowBlindsSheet] = useState(false);
  const [blindsSB, setBlindsSB] = useState<number>(0);
  const [blindsBB, setBlindsBB] = useState<number>(0);
  const [blindsBusy, setBlindsBusy] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [endBusy, setEndBusy] = useState(false);
  // 👥 GROUPS: rally one of my groups to this table (push invite)
  const [showGroupInvite, setShowGroupInvite] = useState(false);
  const [groupInviteList, setGroupInviteList] = useState<any[] | null>(null);
  const [groupInviteBusy, setGroupInviteBusy] = useState<string | null>(null);
  const openGroupInvite = async () => {
    setShowGroupInvite(true);
    if (groupInviteList === null) {
      const r = await gameApiService.getMyGroups();
      setGroupInviteList(r.success ? (r.groups || []) : []);
    }
  };
  const handleGroupInvite = async (gid: string) => {
    if (!tableId) return;
    setGroupInviteBusy(gid);
    const r = await gameApiService.groupInviteNow(gid, tableId);
    setGroupInviteBusy(null);
    if (r.success) {
      setShowGroupInvite(false);
      alert(`Invite sent to ${r.notified ?? 0} member${(r.notified ?? 0) === 1 ? '' : 's'}.`);
    } else {
      alert(r.error || 'Could not send the group invite.');
    }
  };
  const handleHostKick = async (targetId: string) => {
    if (!tableId || kickBusy) return;
    setKickBusy(true);
    try {
      const r = await gameApiService.hostKickPlayer(tableId, targetId);
      if (!r.success) alert(r.error || 'Could not kick player');
      else setShowKickSheet(false);
    } finally { setKickBusy(false); }
  };
  const handleHostPause = async () => {
    if (!tableId) return;
    const paused = (table as any)?.sessionPaused === true;
    const r = await gameApiService.hostPauseGame(tableId, !paused);
    if (!r.success) alert(r.error || 'Could not update pause');
  };
  const handleHostBlinds = async () => {
    if (!tableId || blindsBusy || !(blindsSB > 0) || !(blindsBB > 0)) return;
    setBlindsBusy(true);
    try {
      const r = await gameApiService.hostSetBlinds(tableId, blindsSB, blindsBB);
      if (!r.success) alert(r.error || 'Could not change blinds');
      else setShowBlindsSheet(false);
    } finally { setBlindsBusy(false); }
  };
  const handleHostEnd = async () => {
    if (!tableId || endBusy) return;
    setEndBusy(true);
    try {
      const r = await gameApiService.hostEndGame(tableId);
      if (!r.success) alert(r.error || 'Could not end the game');
      else { setShowEndConfirm(false); setShowRealTimeResults(true); }
    } finally { setEndBusy(false); }
  };
  const [assignAmount, setAssignAmount] = useState<number>(0);
  const [assignBusy, setAssignBusy] = useState(false);
  const handleAssignChips = async (sign: 1 | -1) => {
    if (!tableId || !assignTarget || !assignAmount || assignBusy) return;
    setAssignBusy(true);
    try {
      const r = await gameApiService.hostAssignChips(tableId, assignTarget, sign * Math.abs(assignAmount));
      if (!r.success) alert(r.error || 'Could not assign chips');
    } finally {
      setAssignBusy(false);
    }
  };
  // 🏠 Waitlist for full home games
  const isTableFull = !!table && (table.players || []).length >= (table.maxSeats || 8);
  const isSeatedHere = !!table && (table.players || []).includes(currentUserId);
  const [waitlistPos, setWaitlistPos] = useState<number | null>(null);
  const handleJoinWaitlist = async () => {
    if (!tableId) return;
    const r = await gameApiService.joinWaitlist(tableId);
    if (r.success) setWaitlistPos(r.position || 1);
    else alert(r.error || 'Could not join the waitlist');
  };
  // Phones render the portrait racetrack (PremiumThemedTableSVG) for EVERY
  // table - classic and premium alike - so there is one mobile experience.
  // Desktop keeps the theme-based choice. matchMedia mirrors the CSS
  // breakpoints exactly.
  const [isPhoneLayout, setIsPhoneLayout] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const onChange = () => setIsPhoneLayout(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    window.addEventListener('resize', onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
      window.removeEventListener('resize', onChange);
    };
  }, []);
  const [currentUsername, setCurrentUsername] = useState<string>('Player');
  
  //  Bomb Pot state

  const [isTriggingBombPot, setIsTriggingBombPot] = useState(false);
  const [pendingBombPot, setPendingBombPot] = useState(false);
  const [bombPotAnte, setBombPotAnte] = useState(0);
  
  //  Table Reset state

  const [showResetModal, setShowResetModal] = useState(false);
  // "Reset Table" only appears when the hand has genuinely stalled: nothing
  // has changed for the whole turn clock plus a grace period. It used to show
  // for the host during every hand and sat on top of the dealer's cards.
  const [handStalled, setHandStalled] = useState(false);
  const [stallDismissedFor, setStallDismissedFor] = useState<number | null>(null);
  const lastStateChangeRef = useRef(Date.now());
  const [pendingReset, setPendingReset] = useState<any>(null);
  const [resetCountdown, setResetCountdown] = useState(0);
  const [isInitiatingReset, setIsInitiatingReset] = useState(false);
  
  //  7-2 Bounty state

  const [showBountyDisplay, setShowBountyDisplay] = useState(false);
  const [bountyWinner, setBountyWinner] = useState<string | null>(null);
  const [bountyAmount, setBountyAmount] = useState(0);
  const lastBountyHandRef = useRef<number>(0);
  
  // Buy-in modal state
  const [showBuyInModal, setShowBuyInModal] = useState(false);
  const [hasBuyInConfirmed, setHasBuyInConfirmed] = useState(false);
  const [isLeavingTable, setIsLeavingTable] = useState(false);
  const isLeavingTableRef = useRef(false); //  Ref for synchronous check (state is async)
  // Follow-the-seat guard: one auto-navigation per table view, reset when
  // the route (tableId) actually changes.
  const autoFollowedRef = useRef(false);
  useEffect(() => { autoFollowedRef.current = false; }, [tableId]);

  const isBuyingInRef = useRef(false); //  Guard against double buy-in calls


  //  DUAL SOUND CONTROLS - Sound (SFX) and Voice (TTS) are SEPARATE

  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('pokersweepstars_soundEnabled');
    return saved !== null ? saved === 'true' : true;
  });
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    const saved = localStorage.getItem('pokersweepstars_voiceEnabled');
    return saved !== null ? saved === 'true' : true;
  });

  // AUTO RELOAD (cash tables): opt-in preference stored on the account so
  // it follows the player across devices. When on, busting triggers an
  // automatic top-up at the table minimum through the same /rebuyChips
  // money path as the manual button - no new money code.
  // null = never chosen. Then the default is ON for PlayCoins tables (free
  // chips, so a bust just tops the seat back up) and OFF for PSS Coins,
  // where spending should always be a deliberate tap.
  const [autoReloadPref, setAutoReloadPref] = useState<boolean | null>(null);
  const autoReload = autoReloadPref ?? (table?.currency !== 'pkcash');
  const setAutoReload = (v: boolean) => setAutoReloadPref(v);
  // Bust timer must outlive the bust-detection effect's re-runs (see the
  // comment where it is scheduled). Cleared only on unmount.
  const bustTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (bustTimerRef.current) clearTimeout(bustTimerRef.current);
  }, []);
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    let cancelled = false;
    getDoc(doc(db, 'users', uid, 'settings', 'gameplay')).then(snap => {
      const saved = snap.data()?.autoReloadCash;
      if (!cancelled) setAutoReloadPref(typeof saved === 'boolean' ? saved : null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [currentUserId]);
  const toggleAutoReload = () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const next = !autoReload;
    setAutoReload(next);
    setDoc(doc(db, 'users', uid, 'settings', 'gameplay'), { autoReloadCash: next }, { merge: true })
      .catch(err => console.error('[AUTO_RELOAD] pref save failed:', err));
  };

  //  Persist sound/voice preferences to localStorage

  useEffect(() => {
    localStorage.setItem('pokersweepstars_soundEnabled', String(soundEnabled));
  }, [soundEnabled]);

  useEffect(() => {
    localStorage.setItem('pokersweepstars_voiceEnabled', String(voiceEnabled));
  }, [voiceEnabled]);

  //  Ready system state

  const [readyPlayers, setReadyPlayers] = useState<string[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [readyCountdown, setReadyCountdown] = useState<number | null>(null);

  //  Reload modal state (cash tables only)

  const [showReloadModal, setShowReloadModal] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);

  //  Tournament-specific bust modal - replaces the cash reload UI on

  // tournament tables so busted players see the right info (rebuy cost +
  // window countdown) instead of the wallet-driven NaN reload prompts.
  const [showTournamentBust, setShowTournamentBust] = useState(false);
  const [tournamentRebuying, setTournamentRebuying] = useState(false);

  //  SESSION: Session tracking state

  const [session, setSession] = useState<GameSession | null>(null);
  const [showSessionPanel, setShowSessionPanel] = useState(false);
  const [sessionExport, setSessionExport] = useState<SessionExport | null>(null);
  const [isEndingSession, setIsEndingSession] = useState(false);
  const sessionUnsubscribeRef = useRef<(() => void) | null>(null);
  const [reloadAmount, setReloadAmount] = useState(0);

  // ============================================================================
  //  PREMIUM TABLE STATE

  // ============================================================================
  const [showTipModal, setShowTipModal] = useState(false);
  const [showTournamentSidebar, setShowTournamentSidebar] = useState(false);
  const [spectatorMode, setSpectatorMode] = useState(false);
  // A seated player who declined a recording keeps camera and mic off until it ends
  const [recMediaBlocked, setRecMediaBlocked] = useState(false);
  const [recordingOffer, setRecordingOffer] = useState<{ label: string; enabled: boolean } | null>(null);
  useEffect(() => {
    if (!tableId || !currentUserId || spectatorMode) return;
    let cancelled = false;
    gameApiService.recordingsRules(tableId).then((r: any) => {
      if (cancelled || !r) return;
      setRecordingOffer({ enabled: r.enabled !== false, label: r.free ? 'Free' : `${r.pricePssCoins} PSS Coins` });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [tableId, currentUserId, spectatorMode]);
  const [tableClosedReason, setTableClosedReason] = useState<'merged' | 'ended' | 'eliminated' | 'cancelled' | null>(null);
  // Tournament-scoped state (subscribed when table?.tournamentId is set)
  const [tournamentData, setTournamentData] = useState<any | null>(null);
  const tournamentDataRef = useRef<any | null>(null);
  // Live rank across all tournament tables (CoinPoker felt info: "My Rank:
  // 47/110"). Fed by a lightweight tables subscription below.
  const [myTournamentRank, setMyTournamentRank] = useState<{ rank: number; field: number; avgStack: number } | null>(null);
  // Tournament-wide chip leaders (top 3 ids, best first) - drives the
  // gold/silver/bronze medals on their avatars at EVERY table, CoinPoker
  // style, so the chip race is visible all tournament long.
  const [topLeaders, setTopLeaders] = useState<string[]>([]);
  // "You've been moved" notice: stamped just before the follow-the-seat
  // hard load, shown for ~6s on arrival - a table move should be SEEN,
  // not inferred from the furniture changing.
  const [movedNotice, setMovedNotice] = useState(false);
  useEffect(() => {
    if (!tableId) return;
    try {
      if (sessionStorage.getItem(`pss:movedNotice:${tableId}`) === '1') {
        sessionStorage.removeItem(`pss:movedNotice:${tableId}`);
        setMovedNotice(true);
        const t = setTimeout(() => setMovedNotice(false), 6000);
        return () => clearTimeout(t);
      }
    } catch { /* noop */ }
  }, [tableId]);

  // The moved notice ends the moment I'm DEALT IN - once cards are in
  // front of me the message is stale, whatever the 6s timer says.
  useEffect(() => {
    if (!movedNotice || !currentUserId) return;
    const me: any = (gameState?.players || []).find((p: any) => p && p.id === currentUserId);
    if (me && me.waitingForNextHand !== true
        && gameState?.status === 'playing' && gameState.round
        && gameState.round !== 'ended' && gameState.round !== 'waiting') {
      setMovedNotice(false);
    }
  }, [gameState, movedNotice, currentUserId]);

  // Chip-lead announcement: fires when the tournament chip lead CHANGES
  // hands (CoinPoker-style table notice). Names cached from the rank sweep.
  const leaderNamesRef = useRef<{ [id: string]: string }>({});
  const prevChipLeaderRef = useRef<string | null>(null);
  const [chipLeadBanner, setChipLeadBanner] = useState<string | null>(null);
  useEffect(() => {
    const leader = topLeaders[0] || null;
    const prev = prevChipLeaderRef.current;
    prevChipLeaderRef.current = leader;
    if (!leader || !prev || prev === leader) return; // first observation or no change
    const name = usernameMap[leader] || leaderNamesRef.current[leader] || 'A new player';
    setChipLeadBanner(`${name} takes the chip lead`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topLeaders]);
  useEffect(() => {
    if (!chipLeadBanner) return;
    const t = setTimeout(() => setChipLeadBanner(null), 5000);
    return () => clearTimeout(t);
  }, [chipLeadBanner]);

  // Tap-a-player profile sheet + the viewer's color tags (rings on seats).
  const [profileTarget, setProfileTarget] = useState<{ id: string; name: string } | null>(null);
  const [tagVersion, setTagVersion] = useState(0);
  // Cinematic overlays: FINAL TABLE / IN THE MONEY. Fired once per
  // tournament (sessionStorage-guarded) on the transition, never re-shown.
  const [cinematic, setCinematic] = useState<{ mode: 'finalTable' | 'inTheMoney'; subtitle?: string } | null>(null);
  // Cinematics QUEUE instead of dropping: in a fast sim the FINAL TABLE
  // moment lands seconds after IN THE MONEY - the old `c || next` kept the
  // one already showing and silently discarded the new one (stamped as
  // seen, so it never showed at all).
  const cinematicQueueRef = useRef<Array<{ mode: 'finalTable' | 'inTheMoney'; subtitle?: string }>>([]);
  const enqueueCinematic = useCallback((c: { mode: 'finalTable' | 'inTheMoney'; subtitle?: string }) => {
    setCinematic(prev => {
      if (prev) { cinematicQueueRef.current.push(c); return prev; }
      return c;
    });
  }, []);
  // Rising-edge HAND FOR HAND announcement - shows once when the server
  // flips handForHand on (money bubble or final-table bubble), so players
  // KNOW hand-for-hand engaged even mid-hand.
  const [h4hBanner, setH4hBanner] = useState<{ title: string; subtitle: string } | null>(null);
  const prevH4hRef = useRef(false);
  const prevActiveTablesRef = useRef<number | null>(null);
  const prevAliveRef = useRef<number | null>(null);
  // 1s tick for on-felt countdowns (level timer, break timer, late reg).
  const [feltTick, setFeltTick] = useState<number>(Date.now());

  // Center-felt tournament info lines, rendered INSIDE the table SVG (below
  // the board, above the bottom seats) so cards can never collide with it.
  // Late Reg shows a CoinPoker-style countdown clock to the close of the
  // window: the rest of this level plus every remaining level inside it.
  const tournamentFeltInfo = useMemo(() => {
    const td: any = tournamentData;
    if (!(table as any)?.tournamentId || !td) return undefined;
    const blindLevels: any[] = Array.isArray(td.blindLevels) ? td.blindLevels : [];
    const curLevel = td.currentBlindLevel || 1;
    const lvlCfg = blindLevels.find((l: any) => l?.level === curLevel) || blindLevels[0] || {};
    let levelStart = typeof td.blindLevelStartedAt === 'number' ? td.blindLevelStartedAt : 0;
    if (!levelStart && td.currentLevelStartedAt?.toMillis) levelStart = td.currentLevelStartedAt.toMillis();
    const durMin = lvlCfg.duration || td.blindLevelDuration || 10;
    const levelMsLeft = levelStart > 0 ? Math.max(0, levelStart + durMin * 60000 - feltTick) : 0;
    const inMins = Math.ceil(levelMsLeft / 60000);
    const fmt = (n: number) => (n >= 1000 ? `${Math.round(n / 100) / 10}K` : `${n}`);
    const sb = lvlCfg.smallBlind ?? td.currentSmallBlind ?? 0;
    const bb = lvlCfg.bigBlind ?? td.currentBigBlind ?? 0;
    const ante = lvlCfg.ante || 0;
    const lateRegLevels = td.lateRegLevels || 4;
    const lateRegOpen = curLevel <= lateRegLevels &&
      ['running', 'late_reg', 'active', 'in_progress'].includes(td.status);
    const paidCount = Array.isArray(td.payoutStructure) ? td.payoutStructure.length : 0;

    // Payout helpers: amount for a finishing position, money formatting.
    const pool = td.prizePool || 0;
    const structure: any[] = Array.isArray(td.payoutStructure) ? td.payoutStructure : [];
    const payoutFor = (pos: number): number => {
      const s = structure.find((r: any) => r?.position === pos);
      return s ? Math.round((pool * (s.percentage || 0) / 100) * 100) / 100 : 0;
    };
    const money = (n: number) => td.currency === 'playcoins'
      ? `${n.toLocaleString()} PC` : `$${n.toLocaleString()}`;

    // Order matters: rank sits right under the level line so YOUR OWN
    // hole cards (bottom seat) can never cover it - at the bubble, rank
    // and money info are exactly what you're shoving decisions on. The
    // tournament name is dropped from the felt (it's in the page header).
    const lines: string[] = [];
    if (td.handForHand === true) {
      lines.push(td.h4hReason === 'final_table'
        ? 'HAND FOR HAND · FINAL TABLE BUBBLE'
        : 'HAND FOR HAND · MONEY BUBBLE');
    }
    lines.push(`Level ${curLevel}: ${fmt(sb)}/${fmt(bb)}${ante ? ` (${fmt(ante)})` : ''}${levelMsLeft > 0 ? ` (in ${inMins}m)` : ''}`);
    const alive = myTournamentRank?.field || 0;
    const inTheMoney = paidCount > 0 && alive > 0 && alive <= paidCount;
    if (myTournamentRank) {
      lines.push(`My Rank: ${myTournamentRank.rank}/${myTournamentRank.field}${paidCount > 0 && !inTheMoney ? ` | Paid: ${paidCount}` : ''}`);
    }
    if (inTheMoney) {
      // CoinPoker phrasing: "Next Prize" = what busting right now pays;
      // "Pay Jump" = the next-higher payout and the rank that claims it.
      const lockedNow = payoutFor(alive);
      lines.push(`Next Prize: ${money(lockedNow)}`);
      for (let p = alive - 1; p >= 1; p--) {
        if (payoutFor(p) > lockedNow) {
          lines.push(`Pay Jump: ${money(payoutFor(p))} (Rank ${p})`);
          break;
        }
      }
    }
    if (lateRegOpen) {
      if (td.onBreak === true) {
        // The tournament clock (levels AND late reg) stops during breaks -
        // the server shifts both anchors forward when the break ends.
        lines.push('Late Reg paused (break)');
      } else {
        let ms = levelMsLeft;
        for (let lv = curLevel + 1; lv <= lateRegLevels; lv++) {
          const cfg = blindLevels.find((l: any) => l?.level === lv);
          ms += ((cfg?.duration) || td.blindLevelDuration || 10) * 60000;
        }
        const mm = Math.floor(ms / 60000);
        const ss = Math.floor((ms % 60000) / 1000);
        lines.push(`Late Reg closes in ${mm}:${String(ss).padStart(2, '0')}`);
      }
    } else if (myTournamentRank && myTournamentRank.avgStack > 0) {
      // Late reg over - the slot shows the average stack instead.
      lines.push(`Avg Stack: ${fmt(myTournamentRank.avgStack)}`);
    }
    return lines;
  }, [tournamentData, feltTick, myTournamentRank, table]);

  // In-app confirm for the tournament add-on (replaces the browser-native
  // window.confirm dialog).
  const [addOnConfirm, setAddOnConfirm] = useState<{ cost: number; chips: number } | null>(null);
  const [addOnBusy, setAddOnBusy] = useState(false);
  const [addOnError, setAddOnError] = useState<string | null>(null);
  // In-app confirm for the tournament rebuy - states exactly what the
  // buy-in delivers (fixed price for a fixed stack, never an amount picker).
  const [rebuyConfirm, setRebuyConfirm] = useState<{ cost: number; chips: number; currency: string } | null>(null);
  const [confirmTourneyLeave, setConfirmTourneyLeave] = useState(false);
  const [rebuyBusy, setRebuyBusy] = useState(false);
  const [rebuyError, setRebuyError] = useState<string | null>(null);
  // Whether the current user has admin role. Delegated to useIsAdminUser so
  // the rules match every other admin-gated surface (env REACT_APP_ADMIN_UID
  // fast-path, then users/{uid}.isAdmin / role==='admin').
  const isAdminUser = useIsAdminUser(currentUserId);
  const [tipAnimations, setTipAnimations] = useState<TipAnimationData[]>([]);
  const [currentTipAnimation, setCurrentTipAnimation] = useState<TipAnimationData | null>(null);

  // ============================================================================
  //  SECURE HOLE CARDS - Fetched via API, not from Firestore

  // ============================================================================
  // Cards in Firestore are hidden to prevent cheating via DevTools inspection
  // We fetch our own cards via the secure /getMyCards endpoint
  // ============================================================================
  const [mySecureCards, setMySecureCards] = useState<{rank: string; suit: string}[]>([]);
  const [recOwner, setRecOwner] = useState<{ uid: string | null; cards: any[] }>({ uid: null, cards: [] });
  const lastFetchedHandRef = useRef<number>(0);

  // ===== COMPUTED VALUES =====
  const currentPlayer = gameState?.players?.find((p: any) => p && p.id === currentUserId);
  const isMyTurn = gameState && currentPlayer && 
    gameState.currentPlayerIndex === (gameState.players || []).filter((p: any) => p && p.id).findIndex((p: any) => p.id === currentUserId) &&
    !currentPlayer.folded && !currentPlayer.allIn && !currentPlayer.eliminated &&
    gameState.status === 'playing' && gameState.round !== 'ended' && gameState.round !== 'showdown';

  //  Check if current player is waiting/needs rebuy

  const isWaitingForNextHand = currentPlayer?.waitingForNextHand || false;

  //  CREATE PRE-GAME STATE - Shows players at table before game starts

  const preGameState: GameState | null = useMemo(() => {
    if (gameInProgress || !table || playerList.length === 0) return null;
    
    // Create a "waiting" game state to show players at table
    return {
      players: playerList.map((p, idx) => ({
        id: p.uid,
        name: p.username,
        chips: p.chips || table.minBuyIn || 100,
        currentBet: 0,
        folded: false,
        allIn: false,
        eliminated: false,
        seat: idx,
        holeCards: [],
        isReady: p.isReady || false,
        //  DON'T set waitingForNextHand in pre-game - that's only for mid-game

        waitingForNextHand: false,
        needsRebuy: false,
      })),
      communityCards: [],
      pot: 0,
      currentPlayerIndex: -1, // No one's turn
      round: 'waiting' as any, // Special waiting round
      status: 'waiting',
      smallBlind: table.smallBlind,
      bigBlind: table.bigBlind,
      dealerIndex: 0,
      handNumber: 0,
    };
  }, [gameInProgress, table, playerList]);

  //  Use real gameState or preGameState

  const displayGameState = gameState || preGameState;

  //  SAFETY: Ensure players array has no null values

  //  SECURITY: Inject secure cards for current player

  const safeGameState = useMemo(() => {
    if (!displayGameState) return null;
    
    // The secure-cards-only lockdown applies during the LIVE hand window
    // (that's where Firestore can deliver stale mid-write states that look
    // like a card swap). At showdown/ended the server legitimately reveals
    // cards in the broadcast and mySecureCards has been cleared - forcing
    // [] there would blank the player's own cards during the winner
    // display and kill the folded-cards-stay-visible feature.
    const liveHand = displayGameState.status === 'playing'
      && displayGameState.round !== 'ended'
      && displayGameState.round !== 'showdown';
    const players = (displayGameState.players || [])
      .filter((p: any) => p && p.id)
      .map((p: any) => {
        // Recorder: the owner's cards arrive from the server only while they allow it
        if (isRecorderView && recOwner.uid && p.id === recOwner.uid) {
          return recOwner.cards.length === 2 ? { ...p, holeCards: recOwner.cards } : p;
        }
        if (p.id === currentUserId) {
          if (mySecureCards.length === 2) return { ...p, holeCards: mySecureCards };
          if (liveHand) return { ...p, holeCards: [] };
          return p; // showdown/ended: trust the server's revealed cards
        }
        return p;
      });
    
    return {
      ...displayGameState,
      players,
    };
  }, [displayGameState, currentUserId, mySecureCards]);

  // ============================================================================
  //  SECURE CARD FETCHING - Get own cards via API (not Firestore)

  // ============================================================================
  useEffect(() => {
    const fetchMyCards = async () => {
      if (!tableId || !currentUserId || !gameState) return;
      
      // Only fetch when:
      // 1. Game is playing
      // 2. Hand number changed (new hand dealt)
      // 3. We're in a hand (not waiting/ended)
      const handNumber = gameState.handNumber || 0;
      const isPlaying = gameState.status === 'playing';
      const isActiveHand = gameState.round && gameState.round !== 'waiting' && gameState.round !== 'ended';
      
      // Check if this is a new hand we haven't fetched cards for
      if (isPlaying && isActiveHand && handNumber > 0 && handNumber !== lastFetchedHandRef.current) {
        try {
          console.log(`🔒 [SECURE] Fetching hole cards for hand #${handNumber}`);
          const result = await gameApiService.getMyCards(tableId);
          
          if (result.success && result.holeCards && result.holeCards.length === 2) {
            setMySecureCards(result.holeCards);
            lastFetchedHandRef.current = handNumber;
            console.log(`✅ [SECURE] Got cards for hand #${handNumber}`);
          }
        } catch (err) {
          console.error('Failed to fetch secure cards:', err);
        }
      }
      
      // Clear cards when hand ends
      if (gameState.round === 'ended' || gameState.round === 'waiting' || !isPlaying) {
        if (mySecureCards.length > 0) {
          setMySecureCards([]);
        }
      }
    };
    
    fetchMyCards();
  }, [tableId, currentUserId, gameState?.handNumber, gameState?.status, gameState?.round]);

  //  Inject secure cards into current player's data. Same live-hand-only

  // lockdown as safeGameState above: secure API wins during the live hand
  // (prevents stale-broadcast card swaps), gameState's revealed cards are
  // trusted at showdown/ended (mySecureCards is cleared by then).
  const currentPlayerWithSecureCards = useMemo(() => {
    if (!currentPlayer) return null;
    const liveHand = gameState?.status === 'playing'
      && gameState?.round !== 'ended'
      && gameState?.round !== 'showdown';
    const cards = mySecureCards.length === 2
      ? mySecureCards
      : (liveHand ? [] : (currentPlayer.holeCards || []));

    return {
      ...currentPlayer,
      holeCards: cards,
    };
  }, [currentPlayer, mySecureCards, gameState?.status, gameState?.round]);

  // ============================================================================
  //  PREMIUM TABLE - Subscribe to tip animations

  // ============================================================================
  useEffect(() => {
    if (!tableId || table?.tableTheme !== 'premium') return;

    const unsubscribe = tipDealerService.subscribeToTipAnimations(
      tableId,
      (animations) => {
        setTipAnimations(animations);
        if (animations.length > 0) {
          const newest = animations[0];
          if (Date.now() - newest.startTime < 500) {
            setCurrentTipAnimation(newest);
          }
        }
      }
    );

    return () => {
      unsubscribe();
      tipDealerService.unsubscribeFromTipAnimations(tableId);
    };
  }, [tableId, table?.tableTheme]);

  //  TIP DEALER HANDLERS

  const handleOpenTipModal = () => setShowTipModal(true);
  const handleCloseTipModal = () => setShowTipModal(false);
  const handleTipAnimationComplete = () => setCurrentTipAnimation(null);

  //  Play sound when it's your turn

  useEffect(() => {
    if (isMyTurn) {
      playSound('your-turn', soundEnabled, table?.tableTheme === 'premium');
    }
  }, [isMyTurn, soundEnabled, table?.tableTheme]);

  //  Play sound when round changes (cards dealt) or game ends

  //  Sound Effects for game progression

  //  FIXED: Delay win/lose sound for all-in showdowns (runout animation is ~12s)

  
  //  CRITICAL: Track if ANY player goes all-in DURING the hand (before showdown clears it)

  // Also track the last playing round for board card detection
  useEffect(() => {
    if (!gameState) return;
    
    // Reset for new hand
    if (gameState.handNumber !== lastAllInCheckHandRef.current) {
      lastAllInCheckHandRef.current = gameState.handNumber;
      hadAllInThisHandRef.current = false;
      lastPlayingRoundRef.current = 'preflop';
    }
    
    // Track the last non-showdown round
    if (gameState.round !== 'showdown' && gameState.round !== 'ended') {
      lastPlayingRoundRef.current = gameState.round;
    }
    
    // Only check all-in during normal play rounds (not showdown/ended)
    if (gameState.round === 'showdown' || gameState.round === 'ended') {
      return;
    }
    
    // Check if any non-folded player is all-in
    const hasAllIn = gameState.players?.some(p => p && !p.folded && p.allIn === true);
    if (hasAllIn && !hadAllInThisHandRef.current) {
      hadAllInThisHandRef.current = true;
      console.log('🔊 [SOUND] Detected all-in player during', gameState.round);
    }
  }, [gameState?.players, gameState?.round, gameState?.handNumber]);
  
  useEffect(() => {
    if (gameState?.round && gameState.round !== lastRoundRef.current) {
      const prevRound = lastRoundRef.current;
      lastRoundRef.current = gameState.round;
      
      if (prevRound !== null) {
        if (gameState.round === 'flop' || gameState.round === 'turn' || gameState.round === 'river') {
          playSound('card-deal', soundEnabled, table?.tableTheme === 'premium');
        }
        //  Win/lose sounds are now handled by PokerTable, synced to runoutPhase

      }
    }
  }, [gameState?.round, soundEnabled, table?.tableTheme]);

  //  Play sound when game starts

  useEffect(() => {
    if (gameInProgress && gameState && lastRoundRef.current === null) {
      lastRoundRef.current = gameState.round;
      playSound('hand-start', soundEnabled, table?.tableTheme === 'premium');
    }
  }, [gameInProgress, gameState, soundEnabled, table?.tableTheme]);

  // A new hand dealt while we are watching: one shuffle cue per hand number.
  // Every client hears it from the state change, so the callers that start
  // the hand do not need to play anything themselves.
  const lastDealSoundHandRef = useRef<number>(-1);
  useEffect(() => {
    if (!gameState || gameState.round !== 'preflop') return;
    const n = gameState.handNumber || 0;
    if (lastDealSoundHandRef.current === n) return;
    if (lastDealSoundHandRef.current === -1) { lastDealSoundHandRef.current = n; return; }
    lastDealSoundHandRef.current = n;
    playSound('hand-start', soundEnabled, table?.tableTheme === 'premium');
  }, [gameState?.handNumber, gameState?.round, soundEnabled, table?.tableTheme]);

  // Leaving the page: nothing keeps playing over the lobby.
  useEffect(() => () => stopAllTableSfx(), []);

  //  SESSION: Sync player stacks to session after each hand ends

  const lastSyncedHandRef = useRef<number>(0);
  useEffect(() => {
    const syncSessionStacks = async () => {
      if (!session || session.status !== 'active' || !tableId) return;
      if (!gameState || gameState.round !== 'ended') return;
      
      const handNumber = gameState.handNumber || 0;
      if (handNumber <= lastSyncedHandRef.current) return;
      
      lastSyncedHandRef.current = handNumber;
      
      try {
        // Sync all player stacks from table to session
        await sessionService.syncAllStacks(session.id, tableId);
        
        // Also increment hand count
        await sessionService.incrementHandCount(session.id);
        
        console.log(`📋 [SESSION] Synced stacks after hand #${handNumber}`);
      } catch (err) {
        console.warn('Failed to sync session stacks:', err);
      }
    };
    
    syncSessionStacks();
  }, [session?.id, session?.status, tableId, gameState?.round, gameState?.handNumber]);

  // ============================================================================
  //  INVITE TOKEN PROCESSING

  // ============================================================================
  // Process invite token from URL or localStorage on page load
  // This grants access to private tables and records token usage
  // ============================================================================
  useEffect(() => {
    const processInviteToken = async () => {
      // Only process once
      if (inviteProcessedRef.current) return;
      if (!tableId || !currentUserId) return;

      // Check for invite token in URL or localStorage
      const urlToken = searchParams.get('invite');
      const storedToken = getPendingInviteToken();
      const token = urlToken || storedToken;

      if (!token) {
        inviteProcessedRef.current = true;
        return;
      }

      try {
        console.log('🔗 [GAME] Processing invite token...');
        const result = await consumeInviteToken(token);

        if (result.success) {
          console.log('✅ [GAME] Invite token accepted - access granted');
          
          // For private tables, store the private code in session storage
          // This will allow the user to join without entering a code
          if (result.privateCode) {
            sessionStorage.setItem(`privateCode_${tableId}`, result.privateCode);
            console.log('🔐 [GAME] Private code stored for table access');
          }
        } else {
          console.warn('⚠️ [GAME] Invite token validation failed:', result.error);
          // Don't block - user can still try to join normally
        }
      } catch (error) {
        console.error('❌ [GAME] Error processing invite token:', error);
      } finally {
        // Clear the pending invite regardless of success/failure
        clearPendingInvite();
        inviteProcessedRef.current = true;
      }
    };

    processInviteToken();
  }, [tableId, currentUserId, searchParams]);

  // ===== RECORDER: pull the owner's hole cards while they allow it =====
  useEffect(() => {
    if (!isRecorderView || !tableId) return;
    let stop = false;
    const pull = async () => {
      try {
        const r = await gameApiService.recordingsOwnerCards(tableId);
        if (!stop) setRecOwner({ uid: r.ownerUid, cards: Array.isArray(r.holeCards) ? r.holeCards : [] });
      } catch { /* next tick */ }
    };
    pull();
    const id = setInterval(pull, 2500);
    return () => { stop = true; clearInterval(id); };
  }, [isRecorderView, tableId]);

  // ===== STALL DETECTION for the host's Reset Table button =====
  useEffect(() => {
    lastStateChangeRef.current = Date.now();
    setHandStalled(false);
  }, [gameState?.stateVersion, gameState?.currentPlayerIndex, gameState?.round, gameState?.handNumber, gameState?.pot, (gameState as any)?.turnStartedAt]);
  useEffect(() => {
    if (!isHost || !gameInProgress) { setHandStalled(false); return; }
    const STALL_GRACE_MS = 45_000;
    const tick = () => {
      const gs: any = gameState;
      if (!gs || gs.round === 'ended' || gs.status === 'ended') { setHandStalled(false); return; }
      const turnMs = (Number(gs.turnDuration) || 30) * 1000;
      const raw = gs.turnStartedAt;
      const turnStarted = typeof raw === 'number' ? raw : (raw && typeof raw.toMillis === 'function' ? raw.toMillis() : 0);
      const since = Math.max(turnStarted || 0, lastStateChangeRef.current);
      setHandStalled(Date.now() - since > turnMs + STALL_GRACE_MS);
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [isHost, gameInProgress, gameState]);

  // ===== RECORD ON OPEN - ?record=1 from the create-table form starts the
  // recording as soon as the creator has a seat (charged there, once) =====
  const recordOnOpenRef = useRef(false);
  useEffect(() => {
    if (recordOnOpenRef.current || !tableId || !currentUserId || !currentPlayer) return;
    if (searchParams.get('record') !== '1') return;
    if ((table as any)?.recording?.id) { recordOnOpenRef.current = true; return; }
    recordOnOpenRef.current = true;
    gameApiService.recordingsStart(tableId, searchParams.get('cards') !== '0').catch((err: any) => {
      console.warn('[REC] record-on-open failed:', err?.message || err);
      alert(`Recording did not start: ${err?.message || 'please use the Record button'}`);
    });
  }, [tableId, currentUserId, currentPlayer, searchParams, table]);

  // ===== INVITE ON OPEN - ?host=1 from the "Host a home game" popup opens
  // the invite panel once the host has taken their seat, so the first thing
  // they see at their new table is the link to send to friends =====
  const inviteOnOpenRef = useRef(false);
  useEffect(() => {
    if (inviteOnOpenRef.current || !table || !currentUserId || !currentPlayer) return;
    if (searchParams.get('host') !== '1') return;
    inviteOnOpenRef.current = true;
    if ((table as any)?.ownerId !== currentUserId) return;
    // No cleanup on purpose: table snapshots re-run this effect constantly,
    // and clearing the timer there would cancel the one-time open.
    setTimeout(() => setShowInviteModal(true), 600);
  }, [table, currentUserId, currentPlayer, searchParams]);

  // ===== SPECTATOR MODE - ?spectate=1 enters as observer, skips seat join =====
  useEffect(() => {
    if (!tableId || !currentUserId) return;
    const spectateParam = searchParams.get('spectate');
    if (spectateParam !== '1') return;
    if (spectatorMode) return; // already registered
    setSpectatorMode(true);
    (async () => {
      try {
        const username = auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Spectator';
        const photoURL = auth.currentUser?.photoURL || undefined;
        await gameApiService.joinAsSpectator(tableId, username, photoURL);
        console.log('👁️ [Spectator] Joined as spectator on', tableId);
      } catch (err) {
        console.warn('[Spectator] join failed:', err);
      }
    })();
    return () => {
      gameApiService.leaveSpectator(tableId).catch(() => {});
    };
  }, [tableId, currentUserId, searchParams, spectatorMode]);

  // Admin detection is handled by useIsAdminUser above - no per-component
  // effect needed. The hook covers env REACT_APP_ADMIN_UID + Firestore role.

  // ===== TOURNAMENT DOC SUBSCRIPTION =====
  // Subscribes to the tournament when this table belongs to one so rebuy/
  // add-on buttons can know window status and per-user usage without polling.
  useEffect(() => {
    const tid = (table as any)?.tournamentId;
    if (!tid) {
      setTournamentData(null);
      return;
    }
    let unsub: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      const { doc, onSnapshot } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      if (cancelled) return;
      unsub = onSnapshot(doc(db, 'tournaments', tid), snap => {
        if (cancelled) return;
        const td = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        setTournamentData(td);
        tournamentDataRef.current = td;
      });
    })();
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, [table]);

  // ===== TOURNAMENT RANK SUBSCRIPTION (CoinPoker felt info) =====
  // Subscribes to all tables in this tournament and ranks the current user
  // by committed stack (chips + currentBet). Powers "My Rank: 47/110" on
  // the felt. Cheap: one query keyed on tournamentId, snapshot-driven.
  useEffect(() => {
    const tid = (table as any)?.tournamentId;
    if (!tid || !currentUserId) {
      setMyTournamentRank(null);
      setTopLeaders([]);
      return;
    }
    let unsub: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      const { collection, onSnapshot, query, where } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      if (cancelled) return;
      const q = query(collection(db, 'tables'), where('tournamentId', '==', tid));
      let lastRankUpdate = 0;
      unsub = onSnapshot(q, snap => {
        if (cancelled) return;
        // Throttle: every action at every table fires this snapshot, and a
        // rank that re-sorts mid-pot on 13 tables jitters constantly.
        // Refresh at most every 10s - tournament ranks are a slow stat.
        const nowTick = Date.now();
        if (lastRankUpdate && nowTick - lastRankUpdate < 10_000) return;
        lastRankUpdate = nowTick;
        const td: any = tournamentDataRef.current;
        // Authoritative bust list: a uid in eliminatedPlayers is OUT even
        // if a stale seat row lingers somewhere. Dedupe across tables too -
        // moves/rebuys can leave ghost rows behind, which inflated the
        // field count and skewed ranks.
        const elimSet = new Set<string>(td?.eliminatedPlayers || []);
        const byId = new Map<string, number>();
        snap.docs.forEach(d => {
          const gs: any = (d.data() as any).gameState || {};
          (gs.players || []).forEach((p: any) => {
            if (!p?.id || p.eliminated || elimSet.has(p.id)) return;
            const committed = (p.chips || 0) + (p.currentBet || 0);
            if (committed <= 0 && !p.allIn) return; // ghost/dead seat row
            const prev = byId.get(p.id);
            if (prev === undefined || committed > prev) byId.set(p.id, committed);
            if (p.name) leaderNamesRef.current[p.id] = p.name;
          });
        });
        const stacks: Array<{ id: string; chips: number }> =
          [...byId.entries()].map(([id, chips]) => ({ id, chips }));
        // The FIELD is entrants, not seats: during late reg most of the
        // field is registered but not yet seated. Everyone registered,
        // alive, and unseated ranks at the starting stack - CoinPoker's
        // 'My Rank: 47/110' semantics.
        if (td?.registeredPlayers) {
          const seatedIds = new Set(stacks.map(s => s.id));
          const starting = td.startingChips || 0;
          (td.registeredPlayers as string[]).forEach(uid => {
            if (!uid || seatedIds.has(uid) || elimSet.has(uid)) return;
            stacks.push({ id: uid, chips: starting });
          });
        }
        stacks.sort((a, b) => b.chips - a.chips);
        const idx = stacks.findIndex(s => s.id === currentUserId);
        const totalChips = stacks.reduce((s, p) => s + p.chips, 0);
        const avgStack = stacks.length > 0 ? Math.round(totalChips / stacks.length) : 0;
        setMyTournamentRank(idx >= 0 ? { rank: idx + 1, field: stacks.length, avgStack } : null);
        setTopLeaders(stacks.slice(0, 3).map(s => s.id));
      }, () => { setMyTournamentRank(null); setTopLeaders([]); });
    })();
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, [table, currentUserId]);

  // 1s tick driving the on-felt countdowns; only runs on tournament tables.
  useEffect(() => {
    if (!(table as any)?.tournamentId) return;
    const t = setInterval(() => setFeltTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [table]);

  // ===== TOURNAMENT NEXT-HAND WATCHDOG =====
  // Production has an every-minute rescue cron, but the LOCAL EMULATOR
  // NEVER RUNS SCHEDULED FUNCTIONS - if the schedule chain breaks (or a
  // break just ended), nothing deals and the table sits at 'ended'
  // forever. Any participant whose table shows no new hand for ~15s kicks
  // the server scheduler. The scheduler's atomic claim (with 60s expiry)
  // makes concurrent kicks from every viewer safe, and the break gate
  // makes kicks during a break a harmless no-op. Every table snapshot
  // resets the timer, so it only ever fires on a genuinely quiet table.
  useEffect(() => {
    const t: any = table;
    if (!tableId || !t?.tournamentId) return;
    // Held tables get kicks too: the deal path itself decides whether to
    // keep holding - skipping here left stale holds frozen forever.
    const gs: any = t.gameState || {};
    // A parked/held table can sit at status 'waiting' too - that's still a
    // boundary the watchdog must drive (a waiting table with no kicks was
    // exactly the post-break wedge).
    const atBoundary = (gs.status === 'ended' || gs.status === 'waiting')
      && (gs.round === 'ended' || gs.round === 'waiting' || !gs.round);
    if (!atBoundary) return;
    // 8s cadence: the server refuses kicks within 8s of the hand ending,
    // so the first effective kick lands ~8-16s into a dead boundary and
    // repeats until the deal happens.
    const iv = setInterval(() => {
      // Break state is checked INSIDE the tick with fresh data: the break
      // clock expiring does not produce a snapshot, and onBreak is only
      // cleared BY a deal attempt - skipping while onBreak would deadlock
      // the resume in the emulator. During an active break (pending or
      // clock running) the tick stays quiet; once the clock passes, the
      // kick is what clears the break and deals.
      const td: any = tournamentDataRef.current;
      if (td?.status === 'completed' || td?.status === 'cancelled') return;
      const breakEndsAtMs = typeof td?.breakEndsAt === 'number'
        ? td.breakEndsAt : (td?.breakEndsAt?.toMillis?.() ?? 0);
      // BREAK-PENDING SWEEP: the clock can't start until EVERY table has
      // parked - and an unwatched bot table that stalled has no client to
      // rescue it (and the emulator has no cron). While pending, this
      // client sweeps every active table with both rescue kicks; they're
      // idempotent and no-op on healthy tables.
      const fullSimRunning = !!td?.simPlan?.stage && td.simPlan.stage !== 'done';
      if ((td?.onBreak && td?.breakPending === true) || td?.handForHand === true
          || td?.finalTableForming === true || fullSimRunning) {
        // The all-tables sweeper effect below owns these states.
        return;
      }
      const breakActive = !!td?.onBreak && Date.now() < breakEndsAtMs;
      if (breakActive) return;
      gameApiService.kickTournamentDeal(tableId).catch(() => {});
    }, 8_000);
    return () => clearInterval(iv);
  }, [tableId, table]);

  // ===== ALL-TABLES SWEEPER: break-pending / hand-for-hand / final-table
  // formation / FULL SIM. These states need EVERY table moving, including
  // tables nobody is watching and tables that are MID-HAND with a stalled
  // bot. The boundary watchdog above only runs while THIS table is between
  // hands - during a sim the viewer's table is usually mid-hand, which
  // left the other tables' stalls unrescued (the emulator has no cron).
  // Runs on a fast 4s cadence independent of this table's state; kicks are
  // idempotent no-ops on healthy tables, and hyper tables use shrunken
  // stall deadlines server-side (bot stall = 1.5s).
  const watchedTournamentId = (table as any)?.tournamentId || null;
  useEffect(() => {
    if (!watchedTournamentId) return;
    const iv = setInterval(() => {
      const td: any = tournamentDataRef.current;
      if (!td || td.status === 'completed' || td.status === 'cancelled') return;
      const fullSimRunning = !!td?.simPlan?.stage && td.simPlan.stage !== 'done';
      const act: string[] = Array.isArray(td.activeTables) ? td.activeTables : [];
      // Sweep whenever the tournament is MULTI-TABLE (plus every
      // synchronized-wait state). A held short table resumes only when the
      // over-full tables hit their boundaries - and an unwatched stalled
      // table over there froze everyone ('stuck after the all-in bust').
      // Kicks are idempotent no-ops on healthy tables; this client
      // babysits the whole tournament while it's open.
      const sweepNeeded = (td.onBreak && td.breakPending === true)
        || td.handForHand === true
        || td.finalTableForming === true
        || td.pendingBalanceRequested === true
        || fullSimRunning
        || act.length > 1;
      if (!sweepNeeded) return;
      // Belt: always include the table being viewed and the viewer's own
      // mapped seat - a table orphaned out of activeTables would otherwise
      // never get kicked and could wedge a waiting stage forever.
      const kickSet = new Set(act.slice(0, 20));
      if (tableId) kickSet.add(tableId);
      const myMapped = currentUserId ? td?.playerTableMap?.[currentUserId] : null;
      if (myMapped) kickSet.add(myMapped);
      kickSet.forEach((t2: string) => {
        gameApiService.kickStalledTurn(t2).catch(() => {});
        gameApiService.kickTournamentDeal(t2).catch(() => {});
      });
    }, 4_000);
    return () => clearInterval(iv);
  }, [watchedTournamentId]);

  // ===== CINEMATIC TRIGGERS: FINAL TABLE + IN THE MONEY =====
  // Final table fires when the tournament consolidates from >1 active
  // tables down to 1 while the user is still alive; in-the-money fires
  // when the alive count crosses down into the paid spots (bubble burst).
  // Both are once-per-tournament via sessionStorage so revisits and
  // snapshot replays can't re-trigger them.
  useEffect(() => {
    const td: any = tournamentData;
    const tid = (table as any)?.tournamentId;
    if (!td || !tid || spectatorMode) return;
    const alive = myTournamentRank?.field || 0;
    const paid = Array.isArray(td.payoutStructure) ? td.payoutStructure.length : 0;
    const meAlive = !!currentUserId && !!myTournamentRank
      && !(td.eliminatedPlayers || []).includes(currentUserId);

    const nTables = Array.isArray(td.activeTables) ? td.activeTables.length : 0;
    const prevTables = prevActiveTablesRef.current;
    if (nTables > 0) prevActiveTablesRef.current = nTables;
    // Fires on the live >1→1 collapse OR on arriving at a RECENTLY formed
    // final table: follow-the-seat moves are hard page loads, so the
    // player most entitled to the moment (they were just moved TO the
    // final table) never observed the transition and got no cinematic.
    const formedRecently = td.finalTableFormed === true
      && typeof td.finalTableFormedAt === 'number'
      && Date.now() - td.finalTableFormedAt < 120_000;
    if (((nTables === 1 && prevTables != null && prevTables > 1) || formedRecently) && meAlive) {
      try {
        const k = `pss:cin:ft:${tid}`;
        if (!sessionStorage.getItem(k)) {
          sessionStorage.setItem(k, '1');
          enqueueCinematic({ mode: 'finalTable', subtitle: td.name || 'The last table' });
        }
      } catch { /* sessionStorage unavailable */ }
    }

    const prevAlive = prevAliveRef.current;
    if (alive > 0) prevAliveRef.current = alive;
    if (paid > 0 && alive > 0 && alive <= paid && prevAlive != null && prevAlive > paid && meAlive) {
      try {
        const k = `pss:cin:itm:${tid}`;
        if (!sessionStorage.getItem(k)) {
          sessionStorage.setItem(k, '1');
          const st: any[] = td.payoutStructure || [];
          const entry = st.find((r: any) => r?.position === alive);
          const amt = entry ? Math.round(((td.prizePool || 0) * (entry.percentage || 0) / 100) * 100) / 100 : 0;
          const money = td.currency === 'playcoins' ? `${amt.toLocaleString()} PC` : `$${amt.toLocaleString()}`;
          enqueueCinematic({ mode: 'inTheMoney', subtitle: amt > 0 ? `Guaranteed ${money}` : undefined });
        }
      } catch { /* sessionStorage unavailable */ }
    }
  }, [tournamentData, myTournamentRank, table, currentUserId, spectatorMode]);

  // BUST MODAL AUTO-DISMISS: a rebuy (manual or the sim's comped one) puts
  // the player back among the living - the Busted Out modal must get out
  // of the way on its own instead of covering their freshly restored seat.
  useEffect(() => {
    if (!showTournamentBust || !currentUserId) return;
    const td: any = tournamentData;
    if (!td) return;
    const aliveAgain = Array.isArray(td.activePlayers) && td.activePlayers.includes(currentUserId)
      && !(td.eliminatedPlayers || []).includes(currentUserId);
    if (aliveAgain) setShowTournamentBust(false);
  }, [tournamentData, showTournamentBust, currentUserId]);

  // HAND FOR HAND rising edge: announce it loudly the moment the server
  // engages it, mid-hand or not - testers (and players) need to SEE that
  // hand-for-hand kicked in, not infer it from pacing.
  useEffect(() => {
    const td: any = tournamentData;
    const on = td?.handForHand === true;
    const was = prevH4hRef.current;
    prevH4hRef.current = on;
    if (on && !was) {
      const paid = Array.isArray(td.payoutStructure) ? td.payoutStructure.length : 0;
      const alive = Array.isArray(td.activePlayers) ? td.activePlayers.length : 0;
      setH4hBanner(td.h4hReason === 'final_table'
        ? {
            title: 'HAND FOR HAND',
            subtitle: `Final table bubble - ${alive} players left. All tables play one hand together, then wait for each other.`,
          }
        : {
            title: 'HAND FOR HAND',
            subtitle: `Money bubble - ${alive} players left, ${paid} get paid. All tables play one hand together, then wait for each other.`,
          });
    }
  }, [tournamentData]);

  // The banner's hide timer lives in ITS OWN effect: putting it in the
  // rising-edge effect meant the next tournament snapshot's cleanup
  // cancelled the timeout and the banner stayed on screen forever.
  useEffect(() => {
    if (!h4hBanner) return;
    const t = setTimeout(() => setH4hBanner(null), 6500);
    return () => clearTimeout(t);
  }, [h4hBanner]);

  // What the user's bust is worth: their recorded finishing position and
  // the payout for it (0 = out of the money). Feeds the bust modal and
  // the eliminated overlay so cashing players see what they made.
  const myBustInfo = useMemo(() => {
    const td: any = tournamentData;
    if (!td || !currentUserId) return null;
    const e = (td.eliminations || []).find((x: any) => x?.playerId === currentUserId);
    if (!e?.position) return null;
    const st: any[] = Array.isArray(td.payoutStructure) ? td.payoutStructure : [];
    const s = st.find((r: any) => r?.position === e.position);
    const amount = s ? Math.round(((td.prizePool || 0) * (s.percentage || 0) / 100) * 100) / 100 : 0;
    return { position: e.position as number, amount };
  }, [tournamentData, currentUserId]);

  // ===== TOURNAMENT TABLE-CLOSED DETECTION =====
  // Reasons:
  //   - 'eliminated' - user fully busted out (in tournament.eliminatedPlayers
  //     AND no longer has an active seat row).
  //   - 'merged'    - user is gone from the table between hands because the
  //     balancer moved them. ONLY valid when no hand is in progress.
  //   - 'ended'     - tournament itself completed.
  // HARD RULE: if the user still has a seat row at this table, the overlay
  // never shows. Period. A shove sets allIn=true / chips=0 but the seat
  // stays - the player MUST be able to watch the runout. Same for being
  // mid-fold-resolution. Only after the hand resolves and the seat is
  // actually gone do we consider closing the view.
  useEffect(() => {
    // spectatorMode STATE lags the URL by a render on navigation - reading
    // the param directly closes the race where a fresh ?spectate=1 visit
    // ran this effect as a "seated" view and follow-the-seat yanked the
    // viewer straight back to their own table.
    const spectateParam = (() => {
      try { return new URLSearchParams(window.location.search).get('spectate') === '1'; }
      catch { return false; }
    })();
    if (!table?.tournamentId || spectatorMode || spectateParam) {
      setTableClosedReason(null);
      return;
    }
    const gs = table.gameState as any;
    const players: any[] = gs?.players || [];
    const seatRow = players.find((p: any) => p && p.id === currentUserId);
    const td: any = tournamentData;
    const eliminatedList: string[] = Array.isArray(td?.eliminatedPlayers) ? td.eliminatedPlayers : [];
    const activeList: string[] = Array.isArray(td?.activePlayers) ? td.activePlayers : [];
    const isInEliminatedList = !!currentUserId && eliminatedList.includes(currentUserId);
    const isStillAliveInTournament = !!currentUserId && activeList.includes(currentUserId);
    const tournamentEnded = td?.status === 'completed' || td?.status === 'ended' || !!td?.winnerId;
    const handInProgress = gs?.status === 'playing' && gs?.round !== 'ended' && gs?.round !== 'showdown';

    // CANCELLED beats the seat-row guard below: a cancelled tournament has
    // no runout to watch (the server force-ends its tables), so a seated
    // player would otherwise stare at a dead felt forever with no
    // explanation - exactly the '20 minutes stuck' report.
    if (td?.status === 'cancelled' || (table as any)?.tournamentCancelled) {
      setTableClosedReason('cancelled');
      return;
    }

    // FOLLOW THE SEAT - checked BEFORE any hand-in-progress gating: if I'm
    // alive and the tournament maps my seat to a DIFFERENT table, route
    // there NOW. This used to live behind "!handInProgress" below, but in
    // turbo/hyper play the old table nearly always has a hand running, so
    // the auto-follow effectively never fired and movers had to click over
    // manually ("the rebuy took me to a new table but it didn't take me
    // there"). A dead row left behind by a bust doesn't block the follow -
    // only a LIVE seat here does (watching your shove resolve stays
    // non-negotiable via the guard just below).
    const mappedTableNow = td?.playerTableMap?.[currentUserId || ''];
    const seatRowDead = !!seatRow && (seatRow as any).eliminated === true;
    if ((!seatRow || seatRowDead)
        && isStillAliveInTournament
        && mappedTableNow && mappedTableNow !== tableId
        && !autoFollowedRef.current) {
      autoFollowedRef.current = true;
      console.log(`🧭 [FOLLOW_SEAT] Seat mapped to ${mappedTableNow} - following now`);
      // HARD load: SPA GamePage-to-GamePage swaps render the OLD table's
      // state under the new URL.
      try { sessionStorage.setItem(`pss:movedNotice:${mappedTableNow}`, '1'); } catch { /* noop */ }
      window.location.replace(`/game/${mappedTableNow}`);
      return;
    }

    // Hard guard - if the user still occupies a seat at this table, never
    // pop the overlay. Watching your shove resolve is non-negotiable.
    if (seatRow) {
      setTableClosedReason(null);
      return;
    }

    // Respect the user's "Watch the rest" choice from TournamentBustModal:
    // skip the Eliminated overlay as long as they're explicitly observing
    // this tournament. The flag is sessionStorage-scoped so it clears on
    // next browser session and also when they navigate to /tournaments
    // (where the auto-route cleans it up).
    let watchingAfterBust = false;
    try {
      const tid = (table as any)?.tournamentId;
      watchingAfterBust = tid ? sessionStorage.getItem(`pss:watchAfterBust:${tid}`) === '1' : false;
    } catch {/* sessionStorage may be unavailable */}

    if (tournamentEnded) {
      setTableClosedReason('ended');
    } else if (isInEliminatedList && !isStillAliveInTournament && !watchingAfterBust) {
      setTableClosedReason('eliminated');
    } else if (!handInProgress && isStillAliveInTournament) {
      // Gone from this table between hands but still alive: the follow-
      // the-seat check above already routed us if the map points
      // elsewhere. Move writes span two docs, so the seat row can vanish
      // a beat before the seat MAP updates - no mapping, or a mapping
      // still pointing HERE, means the move is mid-flight; wait for the
      // next snapshot instead of flashing the closed overlay.
      if (!mappedTableNow || mappedTableNow === tableId) return;
      setTableClosedReason('merged');
    } else {
      setTableClosedReason(null);
    }
  }, [table, currentUserId, spectatorMode, tournamentData]);

  // ===== SETUP TABLE & LISTENER =====
  useEffect(() => {
    // Per-run cancellation: setupTable is ASYNC (it awaits the username
    // before subscribing). Without this, switching tables while a setup
    // was in flight let the OLD table's listener attach AFTER cleanup ran,
    // overwrite the shared unsubscribe ref, and leak - two tables then
    // fought over setTable() and the view flickered between them
    // ("struggling between switching me at tables").
    let runCancelled = false;
    let runUnsub: (() => void) | null = null;
    const setupTable = async () => {
      if (!tableId || !currentUserId) {
        console.error('❌ Missing tableId or userId');
        setError('Invalid table or user');
        setLoading(false);
        return;
      }

      try {
        console.log(`🔗 Setting up table ${tableId} for player ${currentUserId}`);
        
        localStorage.setItem('activeTableId', tableId);
        
        const myUsername = await userService.getUsername(currentUserId);
        setCurrentUsername(myUsername || 'Player');

        console.log(`📡 Setting up table listener for ${tableId}`);

        if (runCancelled) return; // switched tables while awaiting - don't subscribe
        const unsubscribe = tableService.listenToTable(
          tableId,
          async (tableData: GameTableData) => {
            if (runCancelled) return; // stale listener - ignore
            //  GUARD: Table was deleted while listening

            if (!tableData || !tableData.id) {
              console.log('⚠️ [TABLE_LISTENER] Table was deleted or is null');
              setError('Table no longer exists');
              setLoading(false);
              return;
            }
            
            console.log('📊 Real-time table update received:', {
              id: tableData.id,
              players: tableData.players,
              playerBuyIns: tableData.playerBuyIns,
              status: tableData.status,
              readyPlayers: tableData.readyPlayers,
              //  DEBUG: Log both pot locations

              tablePot: tableData.pot,
              gameStatePot: tableData.gameState?.pot,
            });

            let validPlayers: string[] = [];
            if (Array.isArray(tableData.players)) {
              validPlayers = tableData.players.filter(
                (p: any) => typeof p === 'string' && p.length > 0
              );
            }

            setTable(tableData);

            //  Bot auto-leave is now handled server-side in secureJoinTable

            
            // ============================================================================
            //  GEO RESTRICTION CHECK: Block PSS Coins tables for restricted state users

            //  ADMIN BYPASS: Admins and users with bypassGeoRestriction can access

            // ============================================================================
            if (tableData.currency === 'pkcash') {
              const geoResult = geoService.getCachedResult();
              const stateCode = geoResult?.region?.toUpperCase();
              const RESTRICTED_STATES = ['WA', 'ID', 'CA', 'CT', 'MT', 'NJ', 'MI', 'NV', 'NY', 'IN', 'LA', 'ME', 'OK', 'TN', 'DE', 'MS', 'WV', 'MD', 'AZ', 'KY', 'PA'];
              
              if (stateCode && RESTRICTED_STATES.includes(stateCode)) {
                // Check for admin/bypass flags before blocking
                const userProfile = await userService.getUserProfile(currentUserId).catch(() => null);
                const hasAdminBypass = userProfile?.isAdmin === true || userProfile?.bypassGeoRestriction === true;
                
                if (!hasAdminBypass) {
                  const STATE_NAMES: Record<string, string> = {
                    'WA': 'Washington', 'ID': 'Idaho', 'CA': 'California', 'CT': 'Connecticut',
                    'MT': 'Montana', 'NJ': 'New Jersey', 'MI': 'Michigan', 'NV': 'Nevada', 'NY': 'New York'
                  };
                  console.log(`🚫 [GEO_BLOCK] User in ${stateCode} blocked from PSS Coins table`);
                  setError(`PSS Coins tables are not available in ${STATE_NAMES[stateCode] || stateCode}. Please use PlayCoins tables.`);
                  setLoading(false);
                  return;
                } else {
                  console.log('🌎 [GEO] Admin/bypass flag detected - PSS Coins access granted');
                }
              }
            }
            
            //  iOS Native App: Block joining voice/video tables in native iOS app

            // Agora native plugin doesn't work - Safari/Chrome WebRTC is fine
            if (tableData.voiceChatEnabled && isIOSVoiceVideoBlocked()) {
              const isAlreadyPlaying = Array.isArray(tableData.players) && tableData.players.includes(currentUserId);
              if (!isAlreadyPlaying) {
                console.log('🚫 [iOS_BLOCK] Native iOS app blocked from voice/video table');
                setError('Voice & Video tables are not yet supported in the iOS app. Please open this table in Safari or Chrome on your device.');
                setLoading(false);
                return;
              }
            }

            //  Track pending bomb pot

            setPendingBombPot(tableData.pendingBombPot === true);
            setBombPotAnte(tableData.pendingBombPotAnte || 0);

            //  Track ready players from Firestore (now properly typed)

            const tableReadyPlayers = tableData.readyPlayers || [];
            console.log('📋 Ready players from Firestore:', tableReadyPlayers);
            setReadyPlayers(tableReadyPlayers);
            setIsReady(tableReadyPlayers.includes(currentUserId));

            const userIsHost = tableData.ownerId === currentUserId;
            setIsHost(userIsHost);

            //  Check if current user has set their buy-in

            const myBuyIn = tableData.playerBuyIns?.[currentUserId];
            
            //  CRITICAL: Only show modal if user is actually in the players list

            // AND they have a reason to be there (have chips or game is waiting for buy-ins)
            const isUserInGame = validPlayers.includes(currentUserId);
            
            //  Additional check: If game is NOT waiting and user has no buy-in, they probably busted

            // Don't show modal for busted players
            const gameIsWaitingForBuyIns = tableData.status === 'waiting' || !tableData.gameState;
            const userHasChipsInGame = tableData.gameState?.players?.find(
              (p: any) => p?.id === currentUserId && p?.chips > 0
            );
            
            // Show modal ONLY if:
            // 1. User wants to JOIN (not in game yet, game is waiting, table has space)
            // 2. OR user is in game but needs to set buy-in
            // 3. Not confirmed, not leaving
            //  FIX: Don't auto-show modal if table full - let user spectate and click empty seat

            //  TOURNAMENT FIX: Never show the cash-table buy-in modal on a

            //    tournament table. Tournament players paid at registration,
            //    were seated with startingChips by startTournament, and
            //    should never see a buy-in prompt.
            const isTournamentTable = (tableData as any).isTournament === true || !!(tableData as any).tournamentId;
            const tableIsFull = validPlayers.length >= (tableData.maxSeats || 6);
            const wantsToJoin = !isTournamentTable && !isUserInGame && gameIsWaitingForBuyIns && !tableIsFull;
            const needsBuyInAmount = !isTournamentTable && isUserInGame && !myBuyIn && (gameIsWaitingForBuyIns || userHasChipsInGame);

            const shouldShowModal = (wantsToJoin || needsBuyInAmount) &&
              !hasBuyInConfirmed &&
              !isLeavingTableRef.current;
              
            if (shouldShowModal) {
              console.log('📝 Showing buy-in modal - player wants to join or needs buy-in');
              setShowBuyInModal(true);
            } else if (showBuyInModal && (isLeavingTableRef.current || hasBuyInConfirmed)) {
              // User leaving or already confirmed - close modal
              console.log('📝 Closing buy-in modal - user left or confirmed');
              setShowBuyInModal(false);
            }

            //  Build player list with ready status AND photos

            try {
              //  Separate real players from bots for profile lookups

              const humanPlayers = validPlayers.filter((p: string) => !p.startsWith('house_bot_'));
              const botPlayers = validPlayers.filter((p: string) => p.startsWith('house_bot_'));
              
              const usernames = await userService.getUsernames(humanPlayers);
              
              // Inject bot display names from metadata (multi-bot support)
              const houseBots = (tableData as any).houseBots || ((tableData as any).houseBotMetadata ? [{ ...(tableData as any).houseBotMetadata }] : []);
              for (const bot of houseBots) {
                if (bot.botId) usernames[bot.botId] = bot.botName || ' House Bot';

              }
              // Fallback for any bots not in metadata
              for (const botId of botPlayers) {
                if (!usernames[botId]) usernames[botId] = ' House Bot';

              }
              setUsernameMap(usernames);
              
              //  NEW: Fetch user photos for avatars (skip bots)

              const photosMap: { [uid: string]: string | undefined } = {};
              for (const uid of humanPlayers) {
                try {
                  const displayInfo = await userService.getUserDisplayInfo(uid);
                  photosMap[uid] = displayInfo.photoURL;
                } catch (e) {
                  // Ignore photo fetch errors
                }
              }
              setUserPhotosMap(photosMap);
              
              const displayPlayers: PlayerDisplay[] = validPlayers.map((uid) => ({
                uid,
                username: usernames[uid] || `Player ${uid.slice(0, 8)}`,
                isHost: uid === tableData.ownerId,
                chips: tableData.playerBuyIns?.[uid] || tableData.minBuyIn || 1000,
                isCurrentUser: uid === currentUserId,
                isReady: tableReadyPlayers.includes(uid),
              }));
              setPlayerList(displayPlayers);
            } catch (err) {
              console.error('❌ Failed to fetch usernames:', err);
              setPlayerList(
                validPlayers.map((uid) => ({
                  uid,
                  username: `Player ${uid.slice(0, 8)}`,
                  isHost: uid === tableData.ownerId,
                  chips: tableData.playerBuyIns?.[uid] || tableData.minBuyIn || 1000,
                  isCurrentUser: uid === currentUserId,
                  isReady: tableReadyPlayers.includes(uid),
                }))
              );
            }

            if (tableData.gameState) {
              //  SAFETY: Filter out null/invalid players from Firestore data

              const sanitizedGameState = {
                ...tableData.gameState,
                players: (tableData.gameState.players || []).filter((p: any) => p && p.id),
              };
              //  DEBUG: Log pot value being set

              console.log('💰 [POT_DEBUG] Setting gameState with pot:', sanitizedGameState.pot);
              setGameState(sanitizedGameState);
              //  FIX: Only mark game in progress when hand is actually playing

              // Having players in gameState doesn't mean a hand started - they may just be seated
              const isHandActive = sanitizedGameState.status === 'playing' ||
                (sanitizedGameState.round && sanitizedGameState.round !== 'waiting' && sanitizedGameState.round !== 'ended');
              setGameInProgress(!!isHandActive);

              //  If a hand has already been played, skip the bot prompt

              if (sanitizedGameState.handNumber > 0) {
                botChoiceMadeRef.current = true;
              }

              //  7-2 Bounty display tracking

              const bountyResult = sanitizedGameState.sevenTwoBountyResult;
              if (bountyResult && bountyResult.handNumber !== lastBountyHandRef.current) {
                lastBountyHandRef.current = bountyResult.handNumber;
                setBountyWinner(bountyResult.winnerId);
                setBountyAmount(bountyResult.totalBounty || 0);
                setShowBountyDisplay(true);
                // Auto-hide after 5 seconds
                setTimeout(() => setShowBountyDisplay(false), 5000);
              }
            } else if (tableData.status === 'playing') {
              setGameInProgress(true);
            } else {
              setGameInProgress(false);
              setGameState(null);
            }

            setLoading(false);
          },
          (error) => {
            console.error('❌ Listener error:', error);
            setError(error.message);
            setLoading(false);
          }
        );

        if (runCancelled) { unsubscribe(); return; }
        runUnsub = unsubscribe;
        unsubscribeRef.current = unsubscribe;
        gameProgressionService.startMonitoring(tableId);
        console.log('✅ Game progression service started');

      } catch (err: any) {
        console.error('❌ Error in setupTable:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    setupTable();

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasBuyInConfirmed) {
        e.preventDefault();
        e.returnValue = 'You have chips at the table. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      console.log(`🧹 Cleaning up table listener`);
      runCancelled = true;
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Unsubscribe THIS run's listener specifically - the shared ref can
      // already point at a newer run's listener by the time cleanup fires.
      if (runUnsub) {
        runUnsub();
        if (unsubscribeRef.current === runUnsub) unsubscribeRef.current = null;
      } else if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (readyCountdownRef.current) {
        clearTimeout(readyCountdownRef.current);
      }
      if (tableId) {
        gameProgressionService.stopMonitoring(tableId);
      }
    };
  }, [tableId, currentUserId, hasBuyInConfirmed]);

  // ============================================================================
  //  CRASH RECOVERY: Sync chips when tab becomes visible again

  // ============================================================================
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && tableId && hasBuyInConfirmed) {
        console.log('👁️ Tab visible - syncing chips...');
        gameApiService.syncTableChips(tableId).catch(err => {
          console.warn('Could not sync chips:', err);
        });
        
        //  SESSION: Also sync session stacks when tab becomes visible

        if (session && session.status === 'active') {
          sessionService.syncAllStacks(session.id, tableId).catch(err => {
            console.warn('Could not sync session stacks:', err);
          });
        }
      } else if (document.visibilityState === 'hidden' && session && session.status === 'active' && tableId && table?.gameState?.players) {
        //  SESSION: Snapshot stacks when tab is hidden (potential crash prevention)

        const playerStacks: { [odD: string]: { displayName: string; chips: number } } = {};
        for (const player of table.gameState.players) {
          if (player && player.id) {
            playerStacks[player.id] = {
              displayName: usernameMap[player.id] || 'Player',
              chips: player.chips || 0
            };
          }
        }
        if (Object.keys(playerStacks).length > 0) {
          sessionService.snapshotAllStacks(session.id, tableId, playerStacks, 'table_crash').catch(err => {
            console.warn('Could not snapshot session stacks:', err);
          });
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [tableId, hasBuyInConfirmed, session?.id, session?.status, table?.gameState?.players, usernameMap]);

  // ============================================================================
  //  SESSION: Load and subscribe to session if table has one linked

  // ============================================================================
  useEffect(() => {
    const loadSession = async () => {
      if (!tableId || !table) return;
      
      // Check if table has a linked session
      const sessionLink = (table as any).sessionLink;
      if (!sessionLink?.sessionId) {
        setSession(null);
        return;
      }
      
      try {
        // Subscribe to session updates
        if (sessionUnsubscribeRef.current) {
          sessionUnsubscribeRef.current();
        }
        
        sessionUnsubscribeRef.current = sessionService.subscribeToSession(
          sessionLink.sessionId,
          (sessionData) => {
            setSession(sessionData);
            if (sessionData) {
              console.log(`📋 [SESSION] Subscribed to session: ${sessionData.name}`);
            }
          }
        );
      } catch (error) {
        console.error('Failed to load session:', error);
      }
    };
    
    loadSession();
    
    return () => {
      if (sessionUnsubscribeRef.current) {
        sessionUnsubscribeRef.current();
      }
    };
  }, [tableId, (table as any)?.sessionLink?.sessionId]);

  //  Ready countdown effect - starts when 2+ players ready

  // ── Run It Twice hold ─────────────────────────────────────────────────
  // The server settles a multi-run hand instantly (status 'ended'), but the
  // table plays both boards out street by street, which takes up to half a
  // minute. Every path that deals the next hand (auto-ready, the ready
  // countdown, the bot auto-restart) waits for this hold, so the second run
  // is never wiped by the next deal.
  const ritAnimationMs = (dealtBeforeAllIn: number, players: number) => {
    const reveal = Math.max(2, players) * (2 * 600 + 800) + 500;
    const perRun = (dealtBeforeAllIn < 3 ? 2500 : 0) + (dealtBeforeAllIn < 4 ? 2500 : 0) + (dealtBeforeAllIn < 5 ? 2000 : 0) + 2500;
    return reveal + 2500 + perRun * 2 + 1500 + 3000; // equity-before, both runs, transition, buffer
  };
  const [ritHoldUntil, setRitHoldUntil] = useState<number>(0);
  // The ref is set synchronously so effects that run in the SAME commit as
  // the results arrive (the bot auto-restart) already see the hold.
  const ritHoldUntilRef = useRef<number>(0);
  const ritHoldHandRef = useRef<number>(-1);
  useEffect(() => {
    const rr = (gameState as any)?.runItTwiceResults;
    if (!rr || !gameState) return;
    const n = gameState.handNumber || 0;
    if (ritHoldHandRef.current === n) return;
    ritHoldHandRef.current = n;
    const dealt = typeof (gameState as any).cardsDealtBeforeAllIn === 'number' ? (gameState as any).cardsDealtBeforeAllIn : 0;
    const live = (gameState.players || []).filter((p: any) => p && !p.folded && !p.eliminated).length;
    const until = Date.now() + ritAnimationMs(dealt, live);
    ritHoldUntilRef.current = until;
    setRitHoldUntil(until);
    const t = setTimeout(() => { ritHoldUntilRef.current = 0; setRitHoldUntil(0); }, until - Date.now() + 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(gameState as any)?.runItTwiceResults, gameState?.handNumber]);
  const ritHolding = ritHoldUntil > Date.now();

  //  FIX: Removed readyCountdown from deps to prevent re-trigger loop

  useEffect(() => {
    // 🏆 Tournament tables: the server's tournament scheduler owns ALL
    // dealing. The cash-lobby ready-countdown looped forever on cancelled
    // tournaments ("2+ players ready" → /startHand → refused → repeat).
    if ((table as any)?.tournamentId || (table as any)?.isTournament) return;
    const readyCount = readyPlayers.length;

    // 🤖 A hand needs at least one HUMAN among the ready players - a
    // countdown driven by bot-only readiness is how bots ended up dealing
    // to each other on abandoned tables (the server refuses those deals
    // now too, but there is no reason to even count down).
    const humanReady = readyPlayers.some((id: string) => !houseBotService.isHouseBot(id));

    const holdingNow = ritHolding || ritHoldUntilRef.current > Date.now();
    if (humanReady && readyCount >= 2 && !gameInProgress && table?.status !== 'playing' && !holdingNow) {
      setReadyCountdown((prev) => {
        if (prev === null) {
          // After a real showdown, hold longer so players can take in the
          // result before the next deal; fold wins move on quickly.
          const gsNow: any = (table as any)?.gameState;
          const showdownDwell = !!(gsNow?.showdownComplete && !gsNow?.handResult?.allOthersFolded);
          const secs = showdownDwell ? 9 : 5;
          console.log(`🎯 2+ players ready, starting ${secs}-second countdown`);
          return secs;
        }
        return prev; // Don't reset if already counting
      });
    } else if (readyCount < 2 || !humanReady || holdingNow) {
      setReadyCountdown((prev) => {
        if (prev !== null) {
          console.log('⏸️ Less than 2 ready (or no human ready, or boards still running), canceling countdown');
          if (readyCountdownRef.current) {
            clearTimeout(readyCountdownRef.current);
          }
          return null;
        }
        return prev;
      });
    }
  }, [readyPlayers.length, gameInProgress, table?.status, ritHolding]);

  //  Countdown timer tick

  useEffect(() => {
    if (readyCountdown === null || readyCountdown <= 0) return;

    readyCountdownRef.current = setTimeout(() => {
      const newCount = readyCountdown - 1;
      if (newCount <= 0) {
        console.log('⏱️ Countdown finished, starting game with ready players');
        handleStartGameWithReadyPlayers();
        setReadyCountdown(null);
      } else {
        setReadyCountdown(newCount);
      }
    }, 1000);

    return () => {
      if (readyCountdownRef.current) {
        clearTimeout(readyCountdownRef.current);
      }
    };
  }, [readyCountdown]);

  //  HEADS-UP FIX: Auto-restart game when gameState.status === 'ready' after rebuy

  useEffect(() => {
    if (!gameState || !tableId || !table || isStartingGame) return;
    // 🏆 Tournament tables: never run the cash rebuy-restart flow (it can
    // even resetTableToLobby, which would wreck a tournament table).
    if ((table as any)?.tournamentId || (table as any)?.isTournament) return;

    // Check if gameState signals ready to restart (set by tableService after rebuy)
    if (gameState.status === 'ready' && table.status === 'playing') {
      console.log('🎮 [AUTO-RESTART] Detected ready status after rebuy - starting new hand');
      
      // Get playable players from gameState
      const playablePlayers = (gameState.players || []).filter(
        (p: any) => p && p.id && !p.eliminated && !p.isSittingOut && !p.waitingForNextHand && p.chips > 0
      );
      
      if (playablePlayers.length >= 2) {
        console.log(`🎮 [AUTO-RESTART] ${playablePlayers.length} playable players - starting new hand via API`);
        
        //  ENTERPRISE: Use server API to start new hand (secure card dealing)

        gameApiService.startHand(tableId).then(() => {
          console.log('🎮 [AUTO-RESTART] New hand started via server API');
        }).catch(err => {
          console.error('❌ [AUTO-RESTART] Failed to start new hand:', err);
        });
      } else {
        console.log(`⏸️ [AUTO-RESTART] Not enough players (${playablePlayers.length}) - resetting to waiting`);
        tableServiceFns.resetTableToLobby(tableId);
      }
    }
  }, [gameState?.status, table?.status, tableId, isStartingGame]);

  //  BOT GAME: Auto-start next hand after hand ends

  useEffect(() => {
    if (!gameState || !tableId || !table) return;
    if (gameState.status !== 'ended') return;
    // 🏆 Tournament tables: the tournament scheduler deals - this cash
    // bot-game path just spammed refused /startHand calls.
    if ((table as any)?.tournamentId || (table as any)?.isTournament) return;

    // Only for bot games
    const hasBotAtTable = (table.players || []).some((p: string) => p.startsWith('house_bot_'));
    if (!hasBotAtTable) return;

    // Don't schedule twice for the same hand
    const handNum = gameState.handNumber || 0;
    if (botAutoRestartHandRef.current === handNum) return;
    botAutoRestartHandRef.current = handNum;

    // Check enough players have chips for next hand
    //  FIX: For bot tables, the server re-funds the bot automatically.

    // Don't bail early if only 1 player has chips - wait and let startHand handle it.
    const playableCount = (gameState.players || []).filter(
      (p: any) => p && p.chips > 0 && !p.leavingTable
    ).length;

    // Only skip if NO players have chips at all (shouldn't happen)
    if (playableCount < 1) return;

    //  FIX: Use longer delay when a player busted (bot needs time to be re-funded by server)

    const hasBustedPlayer = (gameState.players || []).some(
      (p: any) => p && (p.chips <= 0 || p.needsRebuy) && !p.leavingTable
    );
    // A run-it-twice hand holds the table until both boards have played out
    const baseDelay = hasBustedPlayer ? 10000 : 6000;
    const delay = Math.max(baseDelay, ritHoldUntilRef.current - Date.now() + 500);

    console.log(`🤖 [BOT_NEXT_HAND] Hand #${handNum} ended, scheduling next hand in ${delay / 1000}s... (playable=${playableCount}, busted=${hasBustedPlayer})`);

    const timer = setTimeout(async () => {
      try {
        console.log('🤖 [BOT_NEXT_HAND] Starting next hand...');
        const result = await gameApiService.startHand(tableId);
        if (result.success) {
          console.log('🤖 [BOT_NEXT_HAND] ✅ New hand started');
        } else {
          console.log('🤖 [BOT_NEXT_HAND] ⚠️ Skipped:', (result as any).reason || result.error);
        }
      } catch (err) {
        console.error('🤖 [BOT_NEXT_HAND] ❌ Error:', err);
      }
    }, delay);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState?.status, gameState?.handNumber, tableId]);

  // 💣 Bomb pot banner: announce for 3.5 seconds at the start of a bomb pot
  // hand, then get out of the way (the header badge stays for the hand).
  const [showBombBanner, setShowBombBanner] = useState(false);
  const bombBannerHandRef = useRef<number | null>(null);
  useEffect(() => {
    if (!gameState?.isBombPot) return;
    const hn = gameState.handNumber || 0;
    if (bombBannerHandRef.current === hn) return;
    bombBannerHandRef.current = hn;
    setShowBombBanner(true);
    const t = setTimeout(() => setShowBombBanner(false), 3500);
    return () => clearTimeout(t);
  }, [gameState?.isBombPot, gameState?.handNumber]);

  // AUTO-CONTINUE: after the first hand, cash hands flow like a real table.
  // The Ready tap is a LOBBY gesture (choosing when the game starts) - once
  // play is underway nobody re-consents to every deal, so between hands the
  // seated-with-chips player is auto-readied and the big button stays hidden
  // (only the countdown pill shows). Sitting out / away / busted players are
  // never auto-readied, and tournaments have their own scheduler.
  const autoContinueActive = !((table as any)?.tournamentId || (table as any)?.isTournament)
    && ((gameState?.handNumber || (table as any)?.lastHandNumber || 0) >= 1);

  // Judged against the SERVER's ready list, not the local flag: one attempt
  // per hand left the table stuck whenever the server dropped the flag
  // afterwards (a refused deal clears the list) - the button was hidden and
  // nothing ever re-readied the player. Now: not on the list, eligible, and
  // no attempt in the last few seconds means try again.
  const autoReadyAtRef = useRef<number>(0);
  const serverSaysReady = !!currentUserId && readyPlayers.includes(currentUserId);
  useEffect(() => {
    if (!tableId || !currentUserId || !table || spectatorMode) return;
    if (!autoContinueActive || gameInProgress || !hasBuyInConfirmed) return;
    if (table?.status === 'playing') return;
    if (ritHolding || ritHoldUntilRef.current > Date.now()) return; // both boards are still playing out
    if (serverSaysReady) return;
    if (!currentPlayer || (currentPlayer.chips || 0) <= 0) return;
    const away = (currentPlayer as any)?.isAway || (table as any)?.awayPlayers?.[currentUserId];
    const sitOut = (currentPlayer as any)?.sittingOut || (currentPlayer as any)?.isSittingOut
      || currentPlayer.needsRebuy;
    if (away || sitOut) return;
    const nowMs = Date.now();
    if (nowMs - autoReadyAtRef.current < 4000) return;
    autoReadyAtRef.current = nowMs;
    setIsReady(true);
    tableServiceFns.updatePlayerReady(tableId, currentUserId, true).catch(() => {
      setIsReady(false);
    });
  }, [autoContinueActive, gameInProgress, gameState?.handNumber, hasBuyInConfirmed, serverSaysReady,
    readyPlayers.length, currentPlayer, table, tableId, currentUserId, spectatorMode, ritHolding]);
  // Keep the local flag honest with the server list
  useEffect(() => {
    if (!currentUserId) return;
    if (serverSaysReady !== isReady) setIsReady(serverSaysReady);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverSaysReady]);

  // ===== HANDLERS =====

  //  Toggle ready status

  const handleToggleReady = async () => {
    if (!tableId || !currentUserId) return;
    
    try {
      const newReady = !isReady;
      console.log(`🎯 Toggling ready: ${isReady} -> ${newReady} for ${currentUserId.substring(0, 8)}`);
      
      //  Optimistic UI update - change button immediately

      setIsReady(newReady);
      
      await tableServiceFns.updatePlayerReady(tableId, currentUserId, newReady);
      console.log(`✅ Ready status sent to Firestore`);
    } catch (err) {
      console.error('Failed to update ready status:', err);
      // Revert on error
      setIsReady(!isReady);
    }
  };

  //  Start game with only ready players

  const handleStartGameWithReadyPlayers = async () => {
    console.log('🎮 [handleStartGameWithReadyPlayers] Starting with ready players only');

    if (!tableId || !table) return;
    if ((table as any)?.tournamentId || (table as any)?.isTournament) return; // server deals tournaments
    if (readyPlayers.length < 2) return;
    if (isStartingGame) return;

    try {
      setIsStartingGame(true);

      // EVERY cash hand is dealt by the server (/startHand): it shuffles,
      // stores the deck and hole cards in the secure subcollections, and
      // writes the hidden state. The old human-only path dealt the first
      // hand in the owner's browser and posted every player's cards up -
      // a cheating vector, and the server then had no deck for the hand
      // ("Table sync error"). Several clients may call this on the same
      // countdown; the server deals once and answers the rest gracefully.
      const result = await gameApiService.startHand(tableId);
      if (!result.success) {
        const reason = (result as any).reason || (result as any).error || 'refused';
        console.log(`⏸️ [START_HAND] Server did not deal: ${reason}`);
        return;
      }
      console.log('🎉 Game started via server');
    } catch (err: any) {
      console.error('❌ Failed to start game:', err);
    } finally {
      setIsStartingGame(false);
    }
  };

  //  Handle buy-in confirmation


  // ============================================================================
  //  ENTERPRISE SECURITY: All handlers now use server API

  // ============================================================================

  //  SECURE: Handle buy-in confirmation via server API

  const handleBuyInConfirm = async (amount: number) => {
    if (!tableId || !currentUserId || !table) return;

    //  FIX: Prevent double buy-in race condition

    if (isBuyingInRef.current) {
      console.log('⚠️ [SECURE] Buy-in already in progress, ignoring duplicate call');
      return;
    }
    isBuyingInRef.current = true;

    try {
      console.log(`💰 [SECURE] Player buying in for ${amount}`);
      
      //  FIX: Check multiple sources to determine if player is at table

      //  REMOVED hasBuyIn - busted players have buyIn but need seat selection, not rebuy-only path

      const inPlayersArray = table.players?.includes(currentUserId);
      const inGameState = table.gameState?.players?.some((p: any) => p?.id === currentUserId);
      const isAlreadyAtTable = inPlayersArray || inGameState;
      
      console.log(`🔍 [CHECK] inPlayers=${inPlayersArray}, inGameState=${inGameState} → isAlreadyAtTable=${isAlreadyAtTable}`);
      
      //  Get stateCode for geo restriction check on PSS Coins tables

      const geoResult = geoService.getCachedResult();
      const stateCode = geoResult?.region;
      
      let result;
      if (isAlreadyAtTable) {
        // Player is at table - use rebuy
        console.log(`🔄 [SECURE] Player already at table - using rebuy`);
        result = await gameApiService.secureRebuy(tableId, amount, stateCode);
      } else {
        // New player joining
        console.log(`🪑 [SECURE] New player - using join`);
        // No rebuy fallback here. "Already at table" means the seat and the
        // buy-in already exist, so following it with a rebuy would charge the
        // player a second time for a seat they are sitting in. secureJoinTable
        // reports that case as seated, which is the truth.
        result = await gameApiService.secureJoinTable(tableId, amount, undefined, stateCode);
      }
      
      //  Handle geo block response

      if ((result as any).geoBlocked) {
        alert(result.error || 'PSS Coins tables are not available in your location. Please use PlayCoins tables.');
        return;
      }
      
      if (!result.success) {
        alert(result.error || 'Failed to join table. Please try again.');
        return;
      }
      
      console.log(`✅ [SECURE] ${isAlreadyAtTable ? 'Rebuy' : 'Join'} complete with ${result.chips || result.newChips || result.newChipCount} chips`);
      
      //  SESSION: Record buy-in to session if active

      if (session && session.status === 'active') {
        try {
          await sessionService.recordBuyIn(
            session.id,
            tableId,
            currentUserId,
            currentUsername,
            amount,
            isAlreadyAtTable ? 'rebuy' : 'initial'
          );
          console.log(`📋 [SESSION] Recorded ${isAlreadyAtTable ? 'rebuy' : 'buy-in'} to session`);
        } catch (sessionErr) {
          console.warn('Failed to record buy-in to session:', sessionErr);
          // Don't block game flow if session tracking fails
        }
      }
      
      setHasBuyInConfirmed(true);
      setShowBuyInModal(false);
      
    } catch (err: any) {
      console.error('Failed to join/rebuy:', err);
      alert(`Failed: ${err.message}`);
    } finally {
      isBuyingInRef.current = false;
    }
  };

  //  Trigger Bomb Pot (host only)

  const handleTriggerBombPot = async () => {
    if (!tableId || !isHost) return;
    
    try {
      setIsTriggingBombPot(true);
      
      // No multiplier: the server uses the table's configured bombPotAnte
      const result = await gameApiService.triggerBombPot(tableId);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to trigger bomb pot');
      }
      
      console.log('💣 Bomb pot triggered:', result);
      // UI will update via Firestore listener
      
    } catch (err: any) {
      console.error('Failed to trigger bomb pot:', err);
      alert(`Failed: ${err.message}`);
    } finally {
      setIsTriggingBombPot(false);
    }
  };

  //  Table Reset handlers (host only)

  const handleInitiateReset = async () => {
    if (!tableId || !isHost) return;
    
    try {
      setIsInitiatingReset(true);
      
      const result = await gameApiService.initiateTableReset(tableId);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to initiate reset');
      }
      
      console.log('🔄 Reset initiated:', result);
      setShowResetModal(false);
      // pendingReset will update via Firestore listener
      
    } catch (err: any) {
      console.error('Failed to initiate reset:', err);
      alert(`Failed: ${err.message}`);
    } finally {
      setIsInitiatingReset(false);
    }
  };

  const handleVoteOnReset = async (approve: boolean) => {
    if (!tableId) return;
    
    try {
      const result = await gameApiService.voteOnReset(tableId, approve);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to vote');
      }
      
      console.log('🗳️ Vote recorded:', result);
      
      if (result.status === 'vetoed') {
        alert('Reset was vetoed!');
      } else if (result.status === 'reset_complete') {
        alert('Table has been reset. Chips refunded.');
      }
      
    } catch (err: any) {
      console.error('Failed to vote on reset:', err);
      alert(`Failed: ${err.message}`);
    }
  };

  const handleExecuteReset = async () => {
    if (!tableId || !isHost) return;
    
    try {
      const result = await gameApiService.executeTableReset(tableId);
      
      if (!result.success) {
        if (result.status === 'vetoed') {
          alert('Reset was vetoed by a player and cannot be executed.');
        } else {
          throw new Error(result.error || 'Failed to execute reset');
        }
        return;
      }
      
      console.log('✅ Reset executed:', result);
      alert(`Table reset complete! ${result.refundedPot || 0} chips refunded.`);
      
    } catch (err: any) {
      console.error('Failed to execute reset:', err);
      alert(`Failed: ${err.message}`);
    }
  };

  //  Track pending reset from table data

  useEffect(() => {
    if (table?.pendingReset) {
      setPendingReset(table.pendingReset);
      
      // Start countdown
      const updateCountdown = () => {
        const remaining = Math.max(0, Math.ceil((table.pendingReset.expiresAt - Date.now()) / 1000));
        setResetCountdown(remaining);
        
        if (remaining <= 0 && isHost) {
          // Voting period ended - host can execute
          console.log('🔄 Reset voting period ended');
        }
      };
      
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    } else {
      setPendingReset(null);
      setResetCountdown(0);
    }
  }, [table?.pendingReset, isHost]);

  //  SECURE: Host can force start game via server API

  const handleStartGame = async () => {
    console.log('🎮 [SECURE] HOST START GAME CLICKED');

    if (!tableId || !table) {
      alert('Table data not loaded');
      return;
    }

    if (!Array.isArray(table.players) || table.players.length < 2) {
      alert('Need at least 2 players to start');
      return;
    }

    const playersWithoutBuyIn = table.players.filter(
      uid => !table.playerBuyIns?.[uid]
    );
    
    if (playersWithoutBuyIn.length > 0) {
      alert(`${playersWithoutBuyIn.length} player(s) haven't selected their buy-in yet`);
      return;
    }

    if (isStartingGame) return;

    try {
      setIsStartingGame(true);

      //  ENTERPRISE: Server handles all game initialization

      const result = await gameApiService.startHand(tableId);
      
      if (!result.success) {
        throw new Error('Failed to start hand');
      }
      
      // Clear ready players after successful start
      await tableServiceFns.clearReadyPlayers(tableId);

      console.log('🎉 [SECURE] Game started via server!');
    } catch (err: any) {
      console.error('❌ Failed to start game:', err);
      alert(`Failed to start game: ${err.message || 'Unknown error'}`);
    } finally {
      setIsStartingGame(false);
    }
  };

  //  Check if reload is allowed (only between hands or when waiting/folded)

  const canReload = useMemo(() => {
    if (!gameState || !currentPlayer) return true; // No game = can reload
    if (gameState.status !== 'playing') return true; // Not playing = can reload
    if (gameState.round === 'ended' || gameState.round === 'waiting') return true; // Between hands
    if (currentPlayer.folded || currentPlayer.waitingForNextHand || currentPlayer.needsRebuy) return true; // Out of hand
    return false; // In active hand = cannot reload
  }, [gameState, currentPlayer]);

  // 🏠 HOME GAME: my pending "chips please" ping to the host (cleared when
  // the host assigns or dismisses), and whether the reload-shaped button is
  // usable - requesting chips is just a message, so it has no hand gate.
  const myChipRequestPending = isSessionTable && !!currentUserId
    && !!(table as any)?.chipRequests?.[currentUserId];
  const reloadBtnEnabled = isSessionTable || canReload;

  // ============================================================================
  //  BUST DETECTION - Auto-show reload modal when player loses all chips

  // ============================================================================
  // Detects when:
  // 1. Player's chips reach 0
  // 2. Hand has ended (round === 'ended' or status !== 'playing')
  // 3. Player was not already showing the modal
  // This handles the case where player goes all-in, loses, and needs to rebuy
  // ============================================================================
  const [hasBusted, setHasBusted] = useState(false);

  useEffect(() => {
    if (!table || !currentUserId) return;
    // Spectators (?spectate=1) are observers, not entrants. Their own
    // bust state is irrelevant on a table they're just watching  -  popping

    // the Busted Out modal over a spectate URL strands them on an empty
    // table with no way to keep watching. Skip the whole effect.
    if (spectatorMode) return;
    const isTournamentTable = (table as any)?.isTournament === true || !!(table as any)?.tournamentId;

    // ───────────────────────────────────────────────────────────────────
    // TOURNAMENT BUST DETECTION (sticky)
    // ───────────────────────────────────────────────────────────────────
    // The engine REMOVES eliminated players from gameState.players when the
    // next hand starts, so a check that depends on `currentPlayer` (derived
    // from gameState.players) misses the bust the moment the new hand deals.
    // Use the authoritative tournament doc instead: if the user appears in
    // tournament.eliminatedPlayers, they busted - regardless of whether
    // gameState.players still has their row.
    if (isTournamentTable && tournamentData) {
      const eliminatedList: string[] = (tournamentData as any).eliminatedPlayers || [];
      const isEliminated = eliminatedList.includes(currentUserId);
      if (isEliminated && !hasBusted && !showTournamentBust) {
        console.log(`💸 [BUST] Tournament elimination detected for ${currentUserId.substring(0,8)} - opening rebuy modal`);
        setHasBusted(true);
        (async () => {
          try {
            const wallet = await walletService.getWallet(currentUserId);
            const balance = (tournamentData as any).currency === 'pkcash' ? wallet.pkCash : wallet.playCoins;
            setWalletBalance(balance);
          } catch (err) {
            console.error('Failed to fetch wallet for tournament bust modal:', err);
          }
          setShowTournamentBust(true);
        })();
        return;
      }
      // Clear bust flag if user is no longer eliminated (rebought successfully).
      if (!isEliminated && hasBusted) {
        setHasBusted(false);
      }
    }

    // ───────────────────────────────────────────────────────────────────
    // LEGACY BUST DETECTION (cash tables + tournament fallback)
    // ───────────────────────────────────────────────────────────────────
    if (!gameState || !currentPlayer) return;
    const playerChips = currentPlayer.chips || 0;
    // Bust only fires on a truly FINISHED hand. status !== 'playing' used
    // to count 'showdown' - an all-in player's chips are 0 while their
    // stack sits in the pot, so the BUSTED popup covered the showdown
    // reveal... for the eventual WINNER of the hand. 'showdown' means the
    // pot is not distributed yet; wait for 'ended'.
    const handEnded = gameState.round === 'ended'
      || gameState.status === 'ended' || gameState.status === 'waiting';
    // An all-in player is not busted - their chips are in the pot. After
    // distribution the engine clears allIn and stamps needsRebuy on real
    // busts, so this never masks a genuine bust.
    const playerNeedsRebuy = currentPlayer.needsRebuy === true
      || (playerChips <= 0 && !currentPlayer.allIn);

    if (handEnded && playerNeedsRebuy && !hasBusted && !showReloadModal && !showTournamentBust) {
      console.log(`💸 [BUST] Player busted! Chips: ${playerChips} (tournament=${isTournamentTable})`);
      setHasBusted(true);

      // Timer lives in a ref, NOT in this effect's cleanup: setHasBusted
      // above re-runs this effect (hasBusted is a dep), and an effect-owned
      // cleanup would clear the timer milliseconds after scheduling it -
      // the auto reload / bust modal would never fire. The ref survives
      // dep re-runs and is cleared only on unmount.
      if (bustTimerRef.current) clearTimeout(bustTimerRef.current);
      bustTimerRef.current = setTimeout(async () => {
        try {
          const wallet = await walletService.getWallet(currentUserId);
          const balance = table.currency === 'pkcash' ? wallet.pkCash : wallet.playCoins;
          setWalletBalance(balance);
          if (isTournamentTable) {
            setShowTournamentBust(true);
          } else if (isSessionTable) {
            // 🏠 Host-banked home game: no wallet reload exists. The bust
            // grace holds the seat as SITTING OUT until the host assigns
            // chips (which clears the hold server-side).
            return;
          } else {
            // AUTO RELOAD: opted-in players top straight back up at the
            // table minimum - no modal, no lost seat, blink and you're
            // back in. Any failure (balance, geo, server) falls through
            // to the normal reload modal.
            const reloadAmt = table.minBuyIn || table.maxBuyIn || 200;
            if (autoReload && tableId && balance >= reloadAmt) {
              try {
                const geo = geoService.getCachedResult();
                const r = await gameApiService.secureRebuy(tableId, reloadAmt, geo?.region);
                if (r?.success && !(r as any).geoBlocked) {
                  console.log(`💰 [AUTO_RELOAD] Topped up ${reloadAmt} automatically`);
                  setHasBusted(false);
                  return;
                }
              } catch (arErr) {
                console.warn('[AUTO_RELOAD] failed, falling back to modal:', arErr);
              }
            }
            setShowReloadModal(true);
          }
        } catch (err) {
          console.error('Failed to fetch wallet for bust modal:', err);
        }
      }, 2000); // 2-second delay for showdown to display

      return;
    }

    // Reset bust flag when player has chips again (rebuyed)
    if (playerChips > 0 && hasBusted) {
      console.log(`✅ [REBUY] Player rebuyed, clearing bust flag`);
      setHasBusted(false);
    }
  }, [gameState?.round, gameState?.status, currentPlayer?.chips, currentPlayer?.needsRebuy, hasBusted, showReloadModal, showTournamentBust, currentUserId, table, tournamentData, spectatorMode, autoReload, tableId]);

  //  Handle reload chips modal

  const handleOpenReloadModal = async () => {
    if (!currentUserId || !table) return;

    // 🏠 HOME GAME: no wallet reload exists. The host banks the chips, so
    // the host jumps straight to Assign Chips and everyone else sends the
    // host a live "chips please" request instead.
    if (isSessionTable) {
      if (isTableHost) { setShowAssignChips(true); return; }
      const r = await gameApiService.requestSessionChips(tableId!);
      if (r.success) {
        alert('Chip request sent to the host.');
      } else {
        alert(r.error || 'Could not send the chip request.');
      }
      return;
    }

    //  Prevent reload during active hand

    if (!canReload) {
      alert('Cannot reload during an active hand. Wait for the hand to finish.');
      return;
    }
    
    try {
      const wallet = await walletService.getWallet(currentUserId);
      const balance = table.currency === 'pkcash' ? wallet.pkCash : wallet.playCoins;
      setWalletBalance(balance);  //  FIX: Was setAvailableBalance (doesn't exist)

      setShowReloadModal(true);
    } catch (err) {
      console.error('Failed to fetch wallet:', err);
    }
  };

  //  SECURE: Handle reload chips via server API

  const handleReloadChips = async () => {
    if (!tableId || !currentUserId || !table || reloadAmount <= 0) return;
    
    try {
      console.log(`💰 [SECURE] Reloading ${reloadAmount} chips`);
      
      //  Get stateCode for geo restriction check on PSS Coins tables

      const geoResult = geoService.getCachedResult();
      const stateCode = geoResult?.region;
      
      //  ENTERPRISE: Single atomic API call handles wallet deduction + chip addition

      const result = await gameApiService.secureRebuy(tableId, reloadAmount, stateCode);
      
      //  Handle geo block response

      if ((result as any).geoBlocked) {
        alert(result.error || 'PSS Coins tables are not available in your location.');
        setShowReloadModal(false);
        return;
      }
      
      if (result.success) {
        setShowReloadModal(false);
        console.log(`✅ [SECURE] Reload successful - new chip count: ${result.newChipCount}`);
      } else {
        alert(result.error || 'Failed to reload chips. Please try again.');
      }
    } catch (err: any) {
      console.error('Failed to reload:', err);
      alert(`Failed to reload: ${err.message}`);
    }
  };

  //  Handle "I'm Back" button

  const handleImBack = async () => {
    if (!tableId || !currentUserId) return;
    
    try {
      console.log('🔙 Player coming back');
      await tableServiceFns.setPlayerWaiting(tableId, currentUserId, false);
    } catch (err) {
      console.error('Failed to rejoin:', err);
    }
  };

  //  Handle seat click - either change seat or join game

  const handleSeatClick = async (seatIndex: number) => {
    if (!tableId || !currentUserId || !table) return;
    
    console.log(`🪑 Seat ${seatIndex} clicked`);
    
    // Check if player is already in the game
    const isInGame = gameState?.players?.some((p: any) => p.id === currentUserId);
    const isInTablePlayers = table.players?.includes(currentUserId);
    const playerBuyIn = table.playerBuyIns?.[currentUserId];
    
    // Check if there's an active hand in progress
    const handInProgress = gameState && gameState.round && gameState.round !== 'ended' && gameState.round !== 'waiting';
    
    console.log(`📊 Player status: inGame=${isInGame}, inTable=${isInTablePlayers}, buyIn=${playerBuyIn}, handInProgress=${handInProgress}`);
    
    try {
      if (isInGame) {
        // Player is in active game - try to change seat
        const result = await tableServiceFns.changeSeat(tableId, currentUserId, seatIndex);
        if (!result.success) {
          alert(result.error || 'Cannot change to this seat');
        }
      } else if (playerBuyIn && playerBuyIn > 0) {
        // Player has bought in but not in active hand
        console.log(`🎮 Joining game at seat ${seatIndex} with ${playerBuyIn} chips`);
        const result = await tableServiceFns.joinGameAtSeat(
          tableId,
          currentUserId,
          currentUsername,
          seatIndex,
          playerBuyIn,
          handInProgress //  Pass flag - if hand in progress, mark as waiting

        );
        if (!result.success) {
          alert(result.error || 'Cannot join at this seat');
        }
      } else {
        // Player needs to buy in first - but NEVER on a tournament table.
        // Tournament players are seated by the server with startingChips when
        // startTournament runs; if they don't see themselves in a seat they
        // either busted (use TournamentBustModal) or were rebalanced (auto-
        // route handles that). Showing a cash buy-in dialog here is wrong.
        const isTournamentTable = (table as any)?.isTournament === true || !!(table as any)?.tournamentId;
        if (isTournamentTable) {
          console.log('🏆 [TOURNAMENT] Empty-seat click ignored - tournament tables manage seating server-side');
          return;
        }
        console.log(`💰 Player needs to buy in first`);
        setShowBuyInModal(true);
      }
    } catch (err) {
      console.error('Failed to handle seat click:', err);
      alert('Failed to sit at this seat');
    }
  };

  //  SECURE: Handle leave table via server API

  // Leaving is ALWAYS immediate for the player - navigate away, done. The
  // server handles the money logically. CASH tables: between hands = full
  // cashout; mid-hand = fold + uncommitted stack back; at an unresolved
  // showdown = seat stays in to settle and the hand-end processor returns
  // the settled stack. TOURNAMENT tables: no cashout EVER - the seat is
  // held, the stack stays and blinds off (sit-out auto-fold), and the
  // player returns via MY TABLES; handleLeaveTable shows a confirm first
  // so that's never a surprise.
  const doLeaveTable = async () => {
    if (!tableId || !currentUserId || !table) return;

    //  CRITICAL: Prevent multiple calls

    if (isLeavingTable || isLeavingTableRef.current) {
      console.log('⚠️ Already leaving table, ignoring duplicate call');
      return;
    }

    setIsLeavingTable(true);
    isLeavingTableRef.current = true; //  Synchronous - Firestore listener will see this immediately


    console.log(`👋 [SECURE] Player ${currentUserId} leaving table ${tableId}`);
    localStorage.removeItem('activeTableId');

    // Route based on table type:
    //   - Tournament tables -> /tournaments (lobby for events).
    //   - Cash tables       -> /tables (cash lobby).
    // Also stamp a sessionStorage "left" flag so the tournament auto-route
    // in TournamentsPage doesn't immediately pull a busted player right
    // back into the table they just left. The flag clears when they
    // explicitly click "Join my table" or "Spectate" from the lobby.
    const tid = (table as any)?.tournamentId;
    if (tid) {
      try { sessionStorage.setItem(`pss:leftTournament:${tid}`, '1'); } catch {/* noop */}
      navigate('/tournaments');
    } else {
      navigate('/tables');
    }

    // API call runs in background - don't block navigation. But a failed
    // leave must NEVER be silent: the seat stays held, the table shows a
    // ghost player, and the user has no idea. Retry, then say so.
    let result: any = null;
    try {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          result = await gameApiService.secureLeaveTable(tableId);
          if (result && result.success !== false) break;
        } catch (attemptErr) {
          if (attempt === 2) throw attemptErr;
        }
        if (!result || result.success === false) {
          if (attempt === 2) throw new Error(result?.error || 'leave_failed');
          await new Promise(r => setTimeout(r, (attempt + 1) * 1500));
        }
      }

      //  SESSION: Record cash-out to session if active (cash tables only -
      //  tournament leaves hold the seat and move no money)

      if (session && session.status === 'active' && result.chipsReturned !== undefined
          && !(result as any).tournamentSitOut) {
        try {
          await sessionService.recordCashOut(
            session.id,
            tableId,
            currentUserId,
            currentUsername,
            result.chipsReturned,
            'leave'
          );
          console.log(`📋 [SESSION] Recorded cash-out to session: ${result.chipsReturned}`);
        } catch (sessionErr) {
          console.warn('Failed to record cash-out to session:', sessionErr);
        }
      }

      if (result.chipsReturned > 0) {
        console.log(`💰 [SECURE] Returned ${result.chipsReturned} chips to wallet`);
      }
    } catch (err: any) {
      console.error('❌ Failed to leave table after retries:', err);
      // Already navigated away - but the seat may still be held. SAY SO.
      alert('Leaving the table did not complete - your seat may still be held. '
        + 'Open the table from MY TABLES and press Leave again.');
    }
  };

  // Entry point for the Leave Table button. On a tournament table where
  // the player still has a live stack, confirm first - "leave" there means
  // sit out (stack blinds off), NOT cash out, and that must never be a
  // surprise. Busted/eliminated players and cash tables leave directly.
  const handleLeaveTable = async () => {
    if (!tableId || !currentUserId || !table) return;
    const tid = (table as any)?.tournamentId;
    const me: any = (gameState?.players || []).find((p: any) => p && p.id === currentUserId);
    if (tid && me && !me.eliminated) {
      setConfirmTourneyLeave(true);
      return;
    }
    await doLeaveTable();
  };

  // ============================================================================
  //  SESSION: Session management handlers

  // ============================================================================
  
  // End session and export results (host only)
  const handleEndSession = async () => {
    if (!session || !isHost) return;
    
    const confirmEnd = window.confirm(
      `End session "${session.name}"?\n\nThis will finalize all buy-ins and cash-outs for the session summary.`
    );
    if (!confirmEnd) return;
    
    setIsEndingSession(true);
    try {
      const exportData = await sessionService.endSession(session.id);
      if (exportData) {
        setSessionExport(exportData);
        console.log('📋 [SESSION] Session ended, export ready');
      }
    } catch (err) {
      console.error('Failed to end session:', err);
      alert('Failed to end session. Please try again.');
    } finally {
      setIsEndingSession(false);
    }
  };
  
  // Export session without ending (for mid-session check)
  const handleExportSession = async () => {
    if (!session) return;
    
    try {
      const exportData = await sessionService.exportSession(session.id, false);
      if (exportData) {
        // Download as JSON file
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${session.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        console.log('📋 [SESSION] Export downloaded');
      }
    } catch (err) {
      console.error('Failed to export session:', err);
      alert('Failed to export session data.');
    }
  };
  
  // Download the final session export
  const handleDownloadExport = () => {
    if (!sessionExport) return;
    
    const blob = new Blob([JSON.stringify(sessionExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sessionExport.session.name.replace(/[^a-z0-9]/gi, '_')}_FINAL_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ===== RENDER =====
  if (loading) {
    return (
      <div className="game-page">
        <div className="loading-state">Loading table...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="game-page">
        <div className="error-state">
          Error: {error}
          <button onClick={() => navigate('/tables')}>Back to Tables</button>
        </div>
      </div>
    );
  }

  if (!table) {
    return (
      <div className="game-page">
        <div className="error-state">
          Table not found
          <button onClick={() => navigate('/tables')}>Back to Tables</button>
        </div>
      </div>
    );
  }

  return (
    <div className="game-page">
      {/* HEADER */}
      <div className="game-header">
        <div className="header-left">
          <h1>{table.name}</h1>
          <span className="blinds">
            ${table.smallBlind}/${table.bigBlind}
          </span>
          {(table as any)?.tournamentId && tournamentData && (
            <div style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center' }}>
              <LateRegBadge
                lateRegEndAt={(tournamentData?.lateRegistrationEndTime as any)?.toDate
                  ? (tournamentData.lateRegistrationEndTime as any).toDate()
                  : tournamentData?.lateRegistrationEndTime}
                currentBlindLevel={tournamentData?.currentBlindLevel || 1}
                lateRegLevels={tournamentData?.lateRegLevels || 4}
                paused={(tournamentData as any)?.onBreak === true}
              />
            </div>
          )}
        </div>
        <div className="header-right">
          {/* HAND HISTORY - PokerIntel drawer, every table type */}
          <button
            onClick={() => setShowHandHistory(true)}
            title="Hand history (last 21 hands)"
            style={{
              background: 'rgba(255,255,255,0.08)',
              color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '8px', padding: '8px 12px', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600, marginRight: '8px',
            }}
          >
            History
          </button>
          {/* 🏠 HOME GAME: waitlist for unseated viewers when full */}
          {isSessionTable && !isSeatedHere && isTableFull && (
            <button
              onClick={handleJoinWaitlist}
              style={{
                background: 'rgba(139,92,246,0.15)', color: '#a78bfa',
                border: '1px solid rgba(139,92,246,0.5)', borderRadius: '8px',
                padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                fontWeight: 600, marginRight: '8px',
              }}
            >
              {waitlistPos ? `On waitlist (#${waitlistPos})` : 'Join waitlist'}
            </button>
          )}
          {/* 🏠 HOME GAME: host chip assignment */}
          {isSessionTable && isTableHost && (
            <button
              onClick={() => setShowAssignChips(true)}
              title="Assign session chips to a player (host only)"
              style={{
                background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                color: '#fff', border: 'none', borderRadius: '8px',
                padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                fontWeight: 600, marginRight: '8px',
              }}
            >
              Assign Chips
            </button>
          )}
          {/* REAL TIME RESULTS - session buy-in vs winnings (cash only) */}
          {!((table as any)?.isTournament || (table as any)?.tournamentId) && (
            <button
              onClick={() => setShowRealTimeResults(true)}
              title="Session results for every player at the table"
              style={{
                background: 'rgba(255,255,255,0.08)',
                color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '8px', padding: '8px 12px', cursor: 'pointer',
                fontSize: '13px', fontWeight: 600, marginRight: '8px',
              }}
            >
              Results
            </button>
          )}
          {/* 👥 GROUPS: rally a group to this table */}
          {!((table as any)?.isTournament || (table as any)?.tournamentId) && (
            <button
              onClick={openGroupInvite}
              title="Invite one of your groups to this table"
              style={{
                background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                color: '#fff', border: 'none', borderRadius: '8px',
                padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                fontWeight: 600, marginRight: '8px',
              }}
            >
              Group
            </button>
          )}
          {/* TOURNAMENT SIDEBAR TOGGLE - only on tournament tables */}
          {(table as any)?.tournamentId && (
            <button
              onClick={() => setShowTournamentSidebar(true)}
              title="Tournament info"
              style={{
                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                color: '#fff', border: 'none', borderRadius: '8px',
                padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                fontWeight: 600, marginRight: '8px',
              }}
            >
              Tournament
            </button>
          )}
          {/* TOURNAMENT REBUY - only on tournament tables when busted + in
              window. Closes hard on hand-for-hand, in the money, and at the
              final table (a bust inside the payout zone is a FINISH). */}
          {(table as any)?.tournamentId && currentPlayer &&
            ((currentPlayer.chips || 0) <= 0 || currentPlayer.needsRebuy) &&
            tournamentData &&
            (tournamentData.rebuyAllowed !== false) &&
            ((tournamentData.currentBlindLevel || 1) <= (tournamentData.lateRegLevels || 4)) &&
            tournamentData.handForHand !== true &&
            tournamentData.finalTableFormed !== true &&
            tournamentData.finalTableForming !== true &&
            !(Array.isArray(tournamentData.payoutStructure) && tournamentData.payoutStructure.length > 0
              && Array.isArray(tournamentData.activePlayers) && tournamentData.activePlayers.length > 0
              && tournamentData.activePlayers.length <= tournamentData.payoutStructure.length) && (
            <button
              onClick={() => {
                if (!tableId) return;
                setRebuyError(null);
                setRebuyConfirm({
                  cost: tournamentData.buyInAmount || 0,
                  chips: tournamentData.startingChips || 5000,
                  currency: tournamentData.currency === 'playcoins' ? 'PlayCoins' : 'PSS Coins',
                });
              }}
              style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: '#fff',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '14px',
                marginRight: '6px',
              }}
              title="Rebuy into the tournament (rebuy window only)"
            >
              Tournament Rebuy
            </button>
          )}
          {/* TOURNAMENT ADD-ON - within window, not yet used by this player.
              Closes hard on the money bubble (hand-for-hand), in the money,
              and at the final table - the level cutoff alone kept selling
              chips to a final table already inside the payouts. */}
          {(table as any)?.tournamentId && currentPlayer &&
            tournamentData &&
            (tournamentData.addOnAllowed !== false) &&
            ((tournamentData.currentBlindLevel || 1) <= (tournamentData.lateRegLevels || 4)) &&
            tournamentData.handForHand !== true &&
            tournamentData.finalTableFormed !== true &&
            tournamentData.finalTableForming !== true &&
            !(Array.isArray(tournamentData.payoutStructure) && tournamentData.payoutStructure.length > 0
              && Array.isArray(tournamentData.activePlayers) && tournamentData.activePlayers.length > 0
              && tournamentData.activePlayers.length <= tournamentData.payoutStructure.length) &&
            !((tournamentData.addOnsByUser || {})[currentUserId]) && (
            <button
              onClick={() => {
                if (!tableId) return;
                setAddOnError(null);
                setAddOnConfirm({
                  cost: tournamentData.addOnCost ?? tournamentData.buyInAmount,
                  chips: tournamentData.addOnChips ?? tournamentData.startingChips,
                });
              }}
              style={{
                background: 'linear-gradient(135deg, #a855f7 0%, #7e22ce 100%)',
                color: '#fff',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '14px',
                marginRight: '6px',
              }}
              title="One-time stack boost - available during the add-on window"
            >
              Tournament Add-On
            </button>
          )}
          {/* RELOAD BUTTON - cash tables only. Tournaments use Tournament Rebuy
              / Add-On instead. On home games this becomes Request Chips (the
              host banks the chips, so it pings the host); the host already
              has Assign Chips, so it hides for them. */}
          {!((table as any)?.isTournament || (table as any)?.tournamentId)
            && !(isSessionTable && isTableHost) && (
          <button
            onClick={handleOpenReloadModal}
            disabled={!reloadBtnEnabled}
            className="btn-reload"
            title={isSessionTable
              ? 'Ask the host to top up your chips'
              : canReload ? 'Add chips to your stack' : 'Cannot reload during active hand'}
            style={{
              background: reloadBtnEnabled
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : 'linear-gradient(135deg, #4b5563 0%, #374151 100%)',
              color: reloadBtnEnabled ? '#fff' : '#9ca3af',
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              fontWeight: 'bold',
              cursor: reloadBtnEnabled ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
              opacity: reloadBtnEnabled ? 1 : 0.6,
            }}
          >
             {isSessionTable ? (myChipRequestPending ? 'Chips Requested' : 'Request Chips') : 'Reload'}

          </button>
          )}
          {/*  SOUND TOGGLE (SFX) - Cyan color */}

          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="btn-sound"
            title={soundEnabled ? 'Mute sound effects' : 'Enable sound effects'}
            style={{
              background: soundEnabled ? 'rgba(0, 217, 217, 0.2)' : 'rgba(255, 255, 255, 0.1)',
              border: `2px solid ${soundEnabled ? '#00D9D9' : '#666'}`,
              borderRadius: '8px',
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: soundEnabled ? '#00D9D9' : '#666',
              fontSize: '14px',
              fontWeight: 'bold',
              transition: 'all 0.2s',
            }}
          >
            {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
          {/*  VOICE TOGGLE (TTS) - Purple color */}

          <button
            onClick={() => setVoiceEnabled(!voiceEnabled)}
            className="btn-voice"
            title={voiceEnabled ? 'Mute voice announcements' : 'Enable voice announcements'}
            style={{
              background: voiceEnabled ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255, 255, 255, 0.1)',
              border: `2px solid ${voiceEnabled ? '#8b5cf6' : '#666'}`,
              borderRadius: '8px',
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: voiceEnabled ? '#8b5cf6' : '#666',
              fontSize: '14px',
              fontWeight: 'bold',
              transition: 'all 0.2s',
            }}
          >
            {voiceEnabled ? <Mic size={16} /> : <MicOff size={16} />}
          </button>
          <RecordingBadge table={table} />
          <RecordingCardsToggle table={table} currentUserId={currentUserId} />
          {!spectatorMode && !((table as any)?.tournamentId || (table as any)?.isTournament) && (
            <RecordingButton tableId={tableId || ''} table={table} currentUserId={currentUserId} />
          )}
          {/* SIT OUT / I'M BACK - was only in the phone menu; desktop players
              had no way to sit out short of leaving the table. */}
          {(currentPlayer || (table as any)?.awayPlayers?.[currentUserId]) && !((table as any)?.tournamentId || (table as any)?.isTournament) && (
            ((currentPlayer as any)?.isAway || (table as any)?.awayPlayers?.[currentUserId]) ? (
              <button
                onClick={async () => { if (tableId) await gameApiService.imBack(tableId); }}
                title="Deal me back in"
                style={{
                  background: 'rgba(34, 197, 94, 0.2)', border: '2px solid #22c55e', borderRadius: '8px',
                  padding: '8px 12px', cursor: 'pointer', color: '#22c55e', fontSize: '14px', fontWeight: 'bold',
                }}
              >
                I'm back
              </button>
            ) : (
              <button
                onClick={async () => { if (tableId) await gameApiService.sitOut(tableId); }}
                title="Sit out the next hands. Your seat is held for 5 minutes."
                style={{
                  background: 'rgba(255, 255, 255, 0.1)', border: '2px solid #94a3b8', borderRadius: '8px',
                  padding: '8px 12px', cursor: 'pointer', color: '#cbd5e1', fontSize: '14px', fontWeight: 'bold',
                }}
              >
                Sit out
              </button>
            )
          )}
          <button onClick={() => navigate('/tables')} className="btn-back">
            ← Back
          </button>
          <button onClick={handleLeaveTable} className="btn-leave">
            Leave Table
          </button>
        </div>
      </div>

      {/* MOBILE CHROME - phones only (CSS swaps this in for .game-header).
          One compact bar: menu, table identity, hand history. Everything
          else lives in the menu sheet so nothing overlaps the felt. */}
      <div className="pss-mobile-chrome">
        <button className="pss-mc-btn" onClick={() => setShowMobileMenu(true)} aria-label="Table menu">
          <MenuIcon size={20} />
        </button>
        <div className="pss-mc-title">
          <div className="pss-mc-name">{table.name}</div>
          <div className="pss-mc-stakes">${table.smallBlind}/${table.bigBlind} · NLH</div>
        </div>
        <button className="pss-mc-btn" onClick={() => setShowHandHistory(true)} aria-label="Hand history">
          <HistoryIcon size={20} />
        </button>
      </div>

      {showMobileMenu && (
        <div className="pss-mc-overlay" onClick={() => setShowMobileMenu(false)}>
          <div className="pss-mc-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="pss-mc-sheet-head">
              <span className="pss-mc-sheet-name">{table.name}</span>
              <span className="pss-mc-sheet-stakes">${table.smallBlind}/${table.bigBlind}</span>
            </div>
            {!spectatorMode && !((table as any)?.tournamentId || (table as any)?.isTournament) && (
              <RecordingMenuItem tableId={tableId || ''} table={table} currentUserId={currentUserId} onClose={() => setShowMobileMenu(false)} />
            )}
            {!spectatorMode && (
              <RecordingCardsToggle table={table} currentUserId={currentUserId} asMenuItem />
            )}
            <button className="pss-mc-item" onClick={() => { setShowMobileMenu(false); setShowHandHistory(true); }}>
              Hand history
            </button>
            {!((table as any)?.isTournament || (table as any)?.tournamentId) && (
              <button className="pss-mc-item" onClick={() => { setShowMobileMenu(false); setShowRealTimeResults(true); }}>
                Real time results
              </button>
            )}
            {!((table as any)?.isTournament || (table as any)?.tournamentId) && (
              <button className="pss-mc-item" onClick={() => { setShowMobileMenu(false); openGroupInvite(); }}>
                Invite a group
              </button>
            )}
            {isSessionTable && isTableHost && (
              <button className="pss-mc-item" onClick={() => { setShowMobileMenu(false); setShowAssignChips(true); }}>
                Assign chips (host)
              </button>
            )}
            {isSessionTable && isTableHost && (
              <button className="pss-mc-item" onClick={() => { setShowMobileMenu(false); setShowKickSheet(true); }}>
                Kick a player (host)
              </button>
            )}
            {isSessionTable && isTableHost && (
              <button className="pss-mc-item" onClick={() => { setShowMobileMenu(false); handleHostPause(); }}>
                {(table as any)?.sessionPaused ? 'Resume game (host)' : 'Pause game (host)'}
              </button>
            )}
            {isSessionTable && isTableHost && (
              <button className="pss-mc-item" onClick={() => {
                setShowMobileMenu(false);
                setBlindsSB(table?.smallBlind || 1);
                setBlindsBB(table?.bigBlind || 2);
                setShowBlindsSheet(true);
              }}>
                Change blinds (host)
              </button>
            )}
            {isSessionTable && isTableHost && !(table as any)?.sessionEnded && (
              <button className="pss-mc-item" style={{ color: '#f87171' }}
                onClick={() => { setShowMobileMenu(false); setShowEndConfirm(true); }}>
                End game (host)
              </button>
            )}
            {isSessionTable && !isSeatedHere && isTableFull && (
              <button className="pss-mc-item" onClick={() => { setShowMobileMenu(false); handleJoinWaitlist(); }}>
                {waitlistPos ? `On waitlist (#${waitlistPos})` : 'Join waitlist'}
              </button>
            )}
            {isSessionTable && !isTableHost && isSeatedHere && !(table as any)?.sessionEnded && (
              <button className="pss-mc-item" onClick={() => { setShowMobileMenu(false); handleOpenReloadModal(); }}>
                {myChipRequestPending ? 'Chips requested (waiting on host)' : 'Request chips from host'}
              </button>
            )}
            {(table as any)?.tournamentId && (
              <button className="pss-mc-item" onClick={() => { setShowMobileMenu(false); setShowTournamentSidebar(true); }}>
                Tournament info
              </button>
            )}
            {!((table as any)?.isTournament || (table as any)?.tournamentId) && !isSessionTable && (
              <button className="pss-mc-item" disabled={!canReload}
                onClick={() => { setShowMobileMenu(false); handleOpenReloadModal(); }}>
                Reload chips{!canReload ? ' (not during a hand)' : ''}
              </button>
            )}
            {!((table as any)?.isTournament || (table as any)?.tournamentId) && !isSessionTable && (
              <button className="pss-mc-item" onClick={toggleAutoReload}>
                Auto reload on bust: {autoReload ? 'On' : 'Off'}
              </button>
            )}
            {isHost && (table as any)?.specialRules?.bombPotEnabled && !gameInProgress && (
              <button className="pss-mc-item" disabled={isTriggingBombPot}
                onClick={() => { setShowMobileMenu(false); handleTriggerBombPot(); }}>
                Trigger bomb pot next hand
              </button>
            )}
            <button className="pss-mc-item" onClick={() => setSoundEnabled(!soundEnabled)}>
              Sound effects: {soundEnabled ? 'On' : 'Off'}
            </button>
            <button className="pss-mc-item" onClick={() => setVoiceEnabled(!voiceEnabled)}>
              Voice announcer: {voiceEnabled ? 'On' : 'Off'}
            </button>
            {(currentPlayer || (table as any)?.awayPlayers?.[currentUserId]) && !((table as any)?.tournamentId || (table as any)?.isTournament) && (
              ((currentPlayer as any)?.isAway || (table as any)?.awayPlayers?.[currentUserId]) ? (
                <button className="pss-mc-item" onClick={async () => {
                  setShowMobileMenu(false);
                  if (tableId) await gameApiService.imBack(tableId);
                }}>
                  I'm back - deal me in
                </button>
              ) : (
                <button className="pss-mc-item" onClick={async () => {
                  setShowMobileMenu(false);
                  if (tableId) await gameApiService.sitOut(tableId);
                }}>
                  Sit out (5 minutes max, then your seat opens)
                </button>
              )
            )}
            <button className="pss-mc-item" onClick={() => navigate('/tables')}>
              Back to tables
            </button>
            <button className="pss-mc-item pss-mc-danger" onClick={() => { setShowMobileMenu(false); handleLeaveTable(); }}>
              Leave table
            </button>
          </div>
        </div>
      )}

      {/*  SESSION: Session tracking panel */}

      {session && session.status === 'active' && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.05) 100%)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: '8px',
          padding: '10px 16px',
          margin: '0 16px 10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '18px' }}></span>

            <div>
              <div style={{ fontWeight: 'bold', color: '#fff', fontSize: '14px' }}>
                {session.name}
                <span style={{ 
                  marginLeft: '8px', 
                  padding: '2px 6px', 
                  background: 'rgba(139, 92, 246, 0.3)', 
                  borderRadius: '4px',
                  fontSize: '11px',
                  color: '#a78bfa'
                }}>
                  {session.code}
                </span>
              </div>
              <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                {Object.keys(session.players || {}).length} players • {session.tableIds?.length || 1} table(s) • {session.totalHands || 0} hands
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button onClick={() => setShowSessionPanel(!showSessionPanel)} style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: '#fff', fontSize: '12px', cursor: 'pointer' }}>
              {showSessionPanel ? '▲ Hide' : '▼ Details'}
            </button>
            <button onClick={handleExportSession} style={{ padding: '6px 12px', background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(16, 185, 129, 0.4)', borderRadius: '6px', color: '#10b981', fontSize: '12px', cursor: 'pointer' }}>
               Export

            </button>
            {isHost && (
              <button onClick={handleEndSession} disabled={isEndingSession} style={{ padding: '6px 12px', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '6px', color: '#ef4444', fontSize: '12px', cursor: isEndingSession ? 'not-allowed' : 'pointer', opacity: isEndingSession ? 0.6 : 1 }}>
                {isEndingSession ? '⏳...' : ' End'}

              </button>
            )}
          </div>
        </div>
      )}
      
      {/*  SESSION: Expanded details panel */}

      {session && showSessionPanel && (
        <div style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(139, 92, 246, 0.2)', borderRadius: '8px', padding: '16px', margin: '0 16px 10px 16px' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff', marginBottom: '12px' }}>Session Ledger</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
            {Object.values(session.players || {}).map((player: any) => (
              <div key={player.odD} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '6px', padding: '10px', border: `1px solid ${player.netResult > 0 ? 'rgba(16, 185, 129, 0.3)' : player.netResult < 0 ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255,255,255,0.1)'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontWeight: 'bold', color: '#fff', fontSize: '13px' }}>{player.displayName}{player.isActive && <span style={{ marginLeft: '4px', color: '#10b981' }}>●</span>}</span>
                  <span style={{ fontWeight: 'bold', color: player.netResult > 0 ? '#10b981' : player.netResult < 0 ? '#ef4444' : '#9CA3AF', fontSize: '13px' }}>{player.netResult > 0 ? '+' : ''}{player.netResult?.toFixed(2) || '0.00'}</span>
                </div>
                <div style={{ fontSize: '11px', color: '#6B7280' }}>In: {player.totalBuyIn?.toFixed(2)} • {player.isActive ? `Stack: ${player.lastKnownStack?.toFixed(2)}` : `Out: ${player.totalCashOut?.toFixed(2)}`}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#9CA3AF' }}>
            <span>Total In: {session.totalChipsIn?.toFixed(2)}</span>
            <span>Total Out: {session.totalChipsOut?.toFixed(2)}</span>
          </div>
        </div>
      )}
      
      {/*  SESSION: Export modal */}

      {sessionExport && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#1a1a2e', borderRadius: '12px', padding: '24px', maxWidth: '500px', width: '90%', maxHeight: '80vh', overflow: 'auto', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
            <h2 style={{ color: '#fff', marginBottom: '16px' }}> Session Complete</h2>

            <div style={{ fontSize: '14px', color: '#9CA3AF', marginBottom: '12px' }}>{sessionExport.session.name} • {sessionExport.session.duration}</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#fff', marginBottom: '8px' }}>Final Results</div>
            {sessionExport.results.map((r, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <span style={{ color: '#fff' }}>{r.player}</span>
                <span style={{ fontWeight: 'bold', color: r.net > 0 ? '#10b981' : r.net < 0 ? '#ef4444' : '#9CA3AF' }}>{r.net > 0 ? '+' : ''}{r.net.toFixed(2)}</span>
              </div>
            ))}
            {sessionExport.settlements.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff', marginBottom: '8px' }}> Session Winners & Losers</div>

                <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '8px' }}>For bragging rights only - no real money exchanged</div>
                {sessionExport.settlements.map((s, i) => (
                  <div key={i} style={{ background: 'rgba(255,255,255,0.05)', padding: '8px 12px', borderRadius: '6px', marginBottom: '6px', fontSize: '14px', color: '#fff' }}>
                    <strong>{s.fromName}</strong> lost to <strong>{s.toName}</strong>: <span style={{ color: '#10b981' }}>{s.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={handleDownloadExport} style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}> Download JSON</button>

              <button onClick={() => setSessionExport(null)} style={{ flex: 1, padding: '12px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* STATS */}
      <div className="stats-row">
        <div className="stat">
          <span>Players:</span>
          <strong>{playerList.length}/{table.maxSeats}</strong>
        </div>
        <div className="stat">
          <span>Pot:</span>
          <strong>{table.currency === 'playcoins' ? '' : ''} {gameState?.pot || 0}</strong>

        </div>
        <div className="stat">
          <span>Status:</span>
          <strong>{gameInProgress ? ' Playing' : '⏳ Waiting'}</strong>

        </div>
        <div className="stat">
          <span>Buy-in:</span>
          <strong>{table.minBuyIn} - {table.maxBuyIn}</strong>
        </div>
        {/* Ready count in waiting mode */}
        {!gameInProgress && (
          <div className="stat">
            <span>Ready:</span>
            <strong style={{ color: readyPlayers.length >= 2 ? '#10b981' : '#f59e0b' }}>
              {readyPlayers.length}/{playerList.length}
            </strong>
          </div>
        )}
      </div>

      {/*  SPECIAL RULES BADGES (small indicators) */}

      {table?.specialRules && (Object.values(table.specialRules).some(v => v)) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '6px 12px',
          background: 'rgba(0, 0, 0, 0.3)',
          borderRadius: '8px',
          margin: '4px auto',
          maxWidth: 'fit-content',
          flexWrap: 'wrap',
        }}>
          {table.specialRules.sevenTwoGame && (
            <span style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: '#000',
              padding: '3px 8px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: 'bold',
            }}>
               7-2 (${table.specialRules.sevenTwoBounty || 5})

            </span>
          )}
          {table.specialRules.bombPotEnabled && (
            <span style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#fff',
              padding: '3px 8px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: 'bold',
            }}>
               Bomb Pot

            </span>
          )}
          {table.specialRules.straddleEnabled && (
            <span style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: '#fff',
              padding: '3px 8px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: 'bold',
            }}>
               Straddle

            </span>
          )}
          {table.specialRules.runItTwiceEnabled && (
            <span style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff',
              padding: '3px 8px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: 'bold',
            }}>
               RIT

            </span>
          )}
          {table.specialRules.allInOrFold && (
            <span style={{
              background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
              color: '#fff',
              padding: '3px 8px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: 'bold',
            }}>
               AoF

            </span>
          )}
        </div>
      )}

      {/* Bomb pots run AUTOMATICALLY every Nth hand server-side now - the
          big red TRIGGER BOMB POT banner that used to park itself at the
          top of the page is gone. Hosts who want one sooner still have the
          manual trigger in the table menu. */}

      {/*  PENDING BOMB POT ALERT */}

      {pendingBombPot && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          margin: '8px auto',
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
            color: '#fff',
            padding: '12px 30px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: 'bold',
            boxShadow: '0 4px 15px rgba(239, 68, 68, 0.5)',
            animation: 'pulse 1s infinite',
            textAlign: 'center',
          }}>
             BOMB POT NEXT HAND! All players ante {bombPotAnte}

          </div>
        </div>
      )}

      {/*  HOST RESET TABLE BUTTON - only when the hand has genuinely stalled */}

      {isHost && gameInProgress && handStalled && stallDismissedFor !== (gameState?.handNumber ?? -1) && gameState?.round !== 'ended' && gameState?.status !== 'ended' && !pendingReset && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          margin: '8px auto',
        }}>
          <button
            onClick={() => setShowResetModal(true)}
            style={{
              background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 'bold',
              border: '1px solid #9ca3af',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: 0.8,
            }}
          >
             Reset Table (Stuck?)

          </button>
          <button
            onClick={() => setStallDismissedFor(gameState?.handNumber ?? -1)}
            title="Hide for this hand"
            style={{ marginLeft: 6, background: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 14 }}
          >
            ×
          </button>
        </div>
      )}

      {/*  PENDING RESET ALERT - Shows for ALL players */}

      {pendingReset && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '8px auto',
          padding: '15px 25px',
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(245, 158, 11, 0.5)',
          maxWidth: '400px',
        }}>
          <div style={{ color: '#000', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
             TABLE RESET VOTE

          </div>
          <div style={{ color: '#000', fontSize: '14px', marginBottom: '10px', textAlign: 'center' }}>
            Host initiated a table reset. Chips will be refunded.
            <br />
            <strong>Time remaining: {resetCountdown}s</strong>
          </div>
          
          {/* Show vote buttons for non-host players who haven't voted */}
          {!isHost && !pendingReset.votes?.[currentUserId] && (
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => handleVoteOnReset(true)}
                style={{
                  background: '#10b981',
                  color: '#fff',
                  padding: '8px 20px',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                 Approve

              </button>
              <button
                onClick={() => handleVoteOnReset(false)}
                style={{
                  background: '#ef4444',
                  color: '#fff',
                  padding: '8px 20px',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                 Veto

              </button>
            </div>
          )}
          
          {/* Show execute button for host after countdown */}
          {isHost && resetCountdown === 0 && (
            <button
              onClick={handleExecuteReset}
              style={{
                background: '#10b981',
                color: '#fff',
                padding: '10px 25px',
                borderRadius: '8px',
                fontWeight: 'bold',
                border: 'none',
                cursor: 'pointer',
                marginTop: '10px',
              }}
            >
               Execute Reset Now

            </button>
          )}
          
          {/* Show already voted message */}
          {!isHost && pendingReset.votes?.[currentUserId] !== undefined && (
            <div style={{ color: '#000', fontSize: '12px', marginTop: '5px' }}>
              You voted: {pendingReset.votes[currentUserId] ? ' Approve' : ' Veto'}

            </div>
          )}
        </div>
      )}

      {/*  RESET CONFIRMATION MODAL */}

      {showResetModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#1f2937',
            padding: '25px',
            borderRadius: '16px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
          }}>
            <h3 style={{ color: '#fff', marginTop: 0, marginBottom: '15px' }}>
               Reset Table?

            </h3>
            <p style={{ color: '#9ca3af', marginBottom: '15px', fontSize: '14px' }}>
              This will cancel the current hand and refund all chips in the pot back to players.
            </p>
            <p style={{ color: '#f59e0b', marginBottom: '20px', fontSize: '13px' }}>
               Players will have 30 seconds to approve or veto. Any single veto will block the reset.

            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowResetModal(false)}
                style={{
                  background: '#374151',
                  color: '#fff',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleInitiateReset}
                disabled={isInitiatingReset}
                style={{
                  background: isInitiatingReset ? '#6b7280' : '#f59e0b',
                  color: '#000',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: isInitiatingReset ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                }}
              >
                {isInitiatingReset ? 'Starting Vote...' : 'Start Reset Vote'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/*  ALWAYS SHOW POKER TABLE - with real or pre-game state */}

      <div className="game-container" style={{ position: 'relative' }}>
        {/* I'm Back overlay for waiting players during game - CASH ONLY.
            On tournaments, waitingForNextHand means "just seated by a move
            or rebuy, dealt in next hand" - the SEATED pill explains that,
            and darkening the felt with a wrong 'sitting out' message while
            the player is supposed to WATCH the hand was nonsense. */}
        {gameInProgress && isWaitingForNextHand && !(table as any)?.tournamentId && (
          <div className="im-back-overlay" style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}>
            <p style={{ color: '#fff', fontSize: '18px', marginBottom: '20px' }}>
              You are sitting out
            </p>
            <button
              onClick={handleImBack}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#fff',
                padding: '16px 32px',
                borderRadius: '12px',
                border: 'none',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
               I'm Back!

            </button>
          </div>
        )}

        {/*  HOUSE BOT PROMPT - Show ONCE when first sitting down, never again after choice */}

        {!gameInProgress && hasBuyInConfirmed && table && !botChoiceMadeRef.current && (
          <HouseBotPrompt
            tableId={tableId || ''}
            currency={table.currency || 'playcoins'}
            playerCount={(table.players || []).filter((p: string) => !p.startsWith('house_bot_')).length}
            hasActiveBot={houseBotService.hasActiveBot(table)}
            botCount={houseBotService.getBotCount(table)}
            onBotSummoned={() => { /* Don't set botChoiceMade - let user add more bots */ }}
            onReadyUp={() => { botChoiceMadeRef.current = true; handleToggleReady(); }}
            onDismissed={() => { botChoiceMadeRef.current = true; }}
          />
        )}

        {/*  READY OVERLAY - Show on table when waiting for game */}

        {!gameInProgress && hasBuyInConfirmed && (
          <div className="ready-overlay" style={{
            position: 'absolute',
            // Phones: the hero seat straddles the bottom felt edge, so the
            // 20px anchor parked the Ready button right on top of the
            // avatar and chip count. Lift it above the seat - the felt
            // there is empty between hands.
            bottom: isPhoneLayout ? '170px' : '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px',
          }}>
            {/* Countdown display */}
            {readyCountdown !== null && (
              <div style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#fff',
                padding: '12px 24px',
                borderRadius: '12px',
                fontSize: '20px',
                fontWeight: 'bold',
                animation: 'pulse 1s infinite',
              }}>
                Starting in {readyCountdown}...
              </div>
            )}
            
            {/* Ready button - a LOBBY control only. Once a hand has been
                played, auto-continue readies the player silently and this
                button stays hidden between hands (the countdown pill is the
                only thing worth screen space there). */}
            {(!autoContinueActive || !serverSaysReady) && !(isPhoneLayout && readyCountdown !== null) && (
            <button
              onClick={handleToggleReady}
              style={{
                background: isReady 
                  ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' 
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#fff',
                padding: '16px 48px',
                borderRadius: '12px',
                border: 'none',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
              }}
            >
              {isReady ? ' Not Ready' : autoContinueActive ? ' Deal me in' : ' Ready'}

            </button>
            )}

            {(!autoContinueActive || !serverSaysReady) && !(isPhoneLayout && readyCountdown !== null) && (
            <p style={{
              color: '#94a3b8',
              margin: 0,
              fontSize: '14px',
              background: 'rgba(0, 0, 0, 0.5)',
              padding: '4px 12px',
              borderRadius: '8px',
            }}>
              {readyPlayers.length >= 2
                ? `${readyPlayers.length} players ready!`
                : `Need ${2 - readyPlayers.length} more ready`}
            </p>
            )}

            {/* Host force start */}
            {isHost && playerList.length >= 2 && (
              <button
                onClick={handleStartGame}
                disabled={isStartingGame}
                style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: '#fff',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  fontWeight: 'bold',
                  cursor: isStartingGame ? 'not-allowed' : 'pointer',
                  opacity: isStartingGame ? 0.6 : 1,
                  fontSize: '12px',
                }}
              >
                {isStartingGame ? '⏳...' : ' Force Start All'}

              </button>
            )}
          </div>
        )}

        {/* Invite button overlay - phones use the table's own round invite
            icon; this desktop overlay landed on top of the mobile chrome */}
        {!gameInProgress && !isPhoneLayout && (
          <div style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            zIndex: 100,
          }}>
            <button
              onClick={() => setShowInviteModal(true)}
              style={{
                background: 'linear-gradient(135deg, #00D9D9 0%, #0D4F4F 100%)',
                color: '#0D1B2A',
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
               Invite

            </button>
          </div>
        )}

        {/* Watch-invite button - host generates a code for friends/family to
            spectate. TOURNAMENT TABLES ONLY (product decision): cash tables
            keep their normal invite flow; the watch-code system is a
            tournament sweat feature. Visible whenever the user has a seat. */}
        {!spectatorMode && currentPlayer && !!(table as any)?.tournamentId && (
          <div style={{
            position: 'absolute',
            top: gameInProgress ? '20px' : '70px',
            right: '20px',
            zIndex: 100,
          }}>
            <button
              onClick={() => setShowWatchInviteModal(true)}
              title="Generate a code so a friend or family member can spectate live"
              style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #4c1d95 100%)',
                color: '#fff',
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
              }}
            >
              Share Watch Code
            </button>
          </div>
        )}

        {/* ⏸️ TOURNAMENT BREAK OVERLAY + CENTER-FELT INFO (CoinPoker-style).
            Rendered at GamePage level so both table renderers get it. */}
        {/* 🔀 TABLE MOVE notice - shown on arrival after follow-the-seat */}
        {movedNotice && (
          <div style={{
            position: 'fixed', top: '20%', left: '50%', transform: 'translateX(-50%)',
            zIndex: 1295, pointerEvents: 'none', maxWidth: '94vw', textAlign: 'center',
          }}>
            <div style={{
              background: 'rgba(6, 10, 18, 0.96)', border: '1.5px solid rgba(34, 211, 238, 0.7)',
              borderRadius: 16, padding: 'clamp(10px, 3vw, 14px) clamp(18px, 5vw, 28px)',
              boxShadow: '0 0 40px rgba(34, 211, 238, 0.25), 0 10px 34px rgba(0,0,0,0.6)',
            }}>
              <div style={{ color: '#7dd3fc', fontSize: 'clamp(14px, 4vw, 17px)', fontWeight: 900, letterSpacing: '0.06em' }}>
                🔀 YOU'VE BEEN MOVED TO THIS TABLE
              </div>
              <div style={{ color: '#94a3b8', fontSize: 'clamp(11px, 3.2vw, 12.5px)', marginTop: 4, fontWeight: 600 }}>
                Tables are being balanced - your stack came with you. You'll be dealt in next hand.
              </div>
              {(() => { try { return sessionStorage.getItem('pss:mediaCarry') ? true : false; } catch { return false; } })() && (
                <div style={{ color: '#6ee7b7', fontSize: 'clamp(11px, 3.2vw, 12.5px)', marginTop: 4, fontWeight: 700 }}>
                  🎙 Your voice came with you.
                </div>
              )}
            </div>
          </div>
        )}

        {/* 👑 CHIP LEAD CHANGE announcement (~5s) */}
        {chipLeadBanner && (
          <div style={{
            position: 'fixed', top: '14%', left: '50%', transform: 'translateX(-50%)',
            zIndex: 1290, pointerEvents: 'none', maxWidth: '94vw',
          }}>
            <div style={{
              background: 'rgba(6, 10, 18, 0.95)', border: '1.5px solid rgba(212, 175, 55, 0.7)',
              borderRadius: 20, padding: 'clamp(8px, 2.4vw, 11px) clamp(16px, 4.5vw, 26px)',
              boxShadow: '0 0 40px rgba(212, 175, 55, 0.25), 0 10px 34px rgba(0,0,0,0.6)',
              display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap',
            }}>
              <span style={{ fontSize: 'clamp(16px, 4.5vw, 20px)' }}>👑</span>
              <span style={{ color: '#fde68a', fontSize: 'clamp(12.5px, 3.5vw, 15px)', fontWeight: 800, letterSpacing: '0.04em' }}>
                {chipLeadBanner}
              </span>
            </div>
          </div>
        )}

        {/* HAND FOR HAND announcement banner (rising edge, ~6s) */}
        {h4hBanner && (
          <div style={{
            position: 'fixed', top: '22%', left: '50%', transform: 'translateX(-50%)',
            zIndex: 1300, pointerEvents: 'none', textAlign: 'center', width: 'min(94vw, 640px)',
          }}>
            <div style={{
              background: 'rgba(6, 10, 18, 0.96)', border: '2px solid rgba(212, 175, 55, 0.7)',
              borderRadius: 16, padding: 'clamp(14px, 4vw, 20px) clamp(18px, 5vw, 36px)', display: 'inline-block',
              maxWidth: '94vw',
              boxShadow: '0 0 70px rgba(212, 175, 55, 0.25), 0 18px 60px rgba(0,0,0,0.7)',
            }}>
              <div style={{ color: '#fde68a', fontSize: 'clamp(19px, 5.5vw, 26px)', fontWeight: 900, letterSpacing: '0.22em' }}>
                {h4hBanner.title}
              </div>
              <div style={{ color: '#cbd5e1', fontSize: 'clamp(12px, 3.4vw, 14px)', marginTop: 8, fontWeight: 600 }}>
                {h4hBanner.subtitle}
              </div>
            </div>
          </div>
        )}

        {(table as any)?.tournamentId && tournamentData && (() => {
          const td: any = tournamentData;
          const breakEndsMs = typeof td.breakEndsAt === 'number'
            ? td.breakEndsAt
            : td.breakEndsAt?.toMillis?.() ?? 0;
          // The break overlay must NEVER cover a live hand: this table shows
          // it only once ITS hand is over (parked at the break). Before the
          // last table arrives the clock hasn't started - show the waiting
          // state instead of a countdown.
          const gsNow: any = (table as any)?.gameState || {};
          // A just-finished showdown keeps the felt for a display beat -
          // the curtain dropping the instant the DATA settled blacked out
          // the turn/river runout and winner reveal (the "black box" when
          // watching a table at break time). Mirrors the server's park
          // grace.
          const resultAtMs = gsNow.handResult?.completedAt || 0;
          const displayBeat = resultAtMs > 0 && (feltTick - resultAtMs) < 9_000
            && (gsNow.allInShowdown === true || gsNow.allInOccurred === true
              || (gsNow.winners || []).length > 0);
          const thisTableHandLive = (gsNow.status === 'playing' && gsNow.round
            && gsNow.round !== 'ended' && gsNow.round !== 'waiting'
            && gsNow.showdownComplete !== true) || displayBeat;
          const breakPendingPhase = td.onBreak && td.breakPending === true;
          const breakClockRunning = td.onBreak && !td.breakPending && feltTick < breakEndsMs;
          const onBreak = (breakPendingPhase || breakClockRunning) && !thisTableHandLive;

          // Spectators get the BREAK STATE, not the seated player's kit:
          // no "your seat is safe" copy, no PLAY CASH GAMES/LOBBY buttons
          // (the Spectating banner has Leave), and never a WATCH chip for
          // the very table they're looking at.
          const overlaySpectating = spectatorMode || (() => {
            try { return new URLSearchParams(window.location.search).get('spectate') === '1'; }
            catch { return false; }
          })();

          // Live "which tables are we waiting on" strip, shared by the
          // break-pending curtain, the hand-for-hand pill, and the
          // final-table forming curtain. Each entry is a WATCH chip
          // (same spectate route as the sidebar's Tables tab).
          const actTables: string[] = Array.isArray(td.activeTables) ? td.activeTables : [];
          const tableLabel = (id: string) => {
            const m = /_table_(\d+)$/.exec(id);
            return m ? `Table ${m[1]}` : `…${id.slice(-4)}`;
          };
          const waitingStrip = (waiting: string[], doneLabel: string) => (
            <div style={{ marginTop: 12, maxWidth: 560, pointerEvents: 'auto' }}>
              {waiting.length === 0 ? (
                <div style={{ color: '#4ade80', fontSize: 12.5, fontWeight: 700 }}>{doneLabel}</div>
              ) : (
                <>
                  <div style={{ color: '#94a3b8', fontSize: 11.5, letterSpacing: '0.08em', fontWeight: 700 }}>
                    STILL FINISHING · {waiting.length} OF {actTables.length} TABLES
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginTop: 6 }}>
                    {waiting.slice(0, 8).map(id => (
                      <button
                        key={id}
                        // SAME TAB, full page load: an in-place route swap
                        // kept rendering the OLD table's state, and a new
                        // tab is hostile on mobile. location.assign gives a
                        // clean mount of the watched table; Back returns.
                        onClick={() => window.location.assign(`/game/${id}?spectate=1&tournamentId=${(table as any).tournamentId}`)}
                        title="Watch this table finish its hand"
                        style={{
                          background: 'rgba(15, 23, 42, 0.9)', color: '#7dd3fc',
                          border: '1px solid rgba(34, 211, 238, 0.4)', borderRadius: 12,
                          padding: '4px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                        }}
                      >
                        {tableLabel(id)} · WATCH
                      </button>
                    ))}
                    {waiting.length > 8 && (
                      <span style={{ color: '#64748b', fontSize: 11, alignSelf: 'center' }}>
                        +{waiting.length - 8} more
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          );

          // 🫧 HAND FOR HAND banner: this table finished its synchronized
          // hand and waits for the others. A pill, not a curtain - players
          // should still see the hand's result on the felt.
          // ⏸️ SHORT-TABLE HOLD: the balancer paused this table instead of
          // letting it play short-handed - say so, instead of a silent felt.
          if ((table as any)?._waitingForPlayers && !thisTableHandLive && !td.onBreak) {
            return (
              <div style={{
                position: 'fixed', top: '64px', left: '50%', transform: 'translateX(-50%)',
                zIndex: 400, pointerEvents: 'none', textAlign: 'center',
              }}>
                <div style={{
                  background: 'rgba(10, 14, 22, 0.95)', border: '1px solid rgba(34, 211, 238, 0.5)',
                  borderRadius: 12, padding: '10px 22px', display: 'inline-block',
                }}>
                  <div style={{ color: '#7dd3fc', fontSize: 14, fontWeight: 800, letterSpacing: '0.18em' }}>
                    WAITING FOR PLAYERS
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 3 }}>
                    Balancing tables - new opponents are on their way from the bigger tables.
                  </div>
                </div>
              </div>
            );
          }

          if (td.handForHand === true && !thisTableHandLive && !td.onBreak) {
            const h4hArrived: Record<string, boolean> = td.h4hArrived || {};
            const h4hWaiting = actTables.filter(t2 => t2 !== tableId && !h4hArrived[t2]);
            // CoinPoker-style felt-center band: ✋ + "Prize Bubble" +
            // hand-for-hand status, laid over the middle of the table.
            const h4hPaid = Array.isArray(td.payoutStructure) ? td.payoutStructure.length : 0;
            const h4hAlive = Array.isArray(td.activePlayers) ? td.activePlayers.length : 0;
            const ftReason = td.h4hReason === 'final_table';
            const alsoMoney = ftReason && h4hPaid > 0 && h4hAlive > h4hPaid;
            return (
              <div style={{
                position: 'fixed', top: '36%', left: 0, right: 0,
                zIndex: 400, pointerEvents: 'none', textAlign: 'center',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
              }}>
                <div style={{
                  width: 'clamp(42px, 11vw, 52px)', height: 'clamp(42px, 11vw, 52px)', borderRadius: '50%',
                  background: 'rgba(6, 10, 18, 0.94)', border: '1px solid rgba(212, 175, 55, 0.5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 'clamp(20px, 5.5vw, 25px)', marginBottom: -12, zIndex: 2,
                  boxShadow: '0 6px 24px rgba(0,0,0,0.6)',
                }}>✋</div>
                <div style={{
                  width: '100%', maxWidth: 640,
                  background: 'linear-gradient(90deg, transparent, rgba(4, 8, 14, 0.9) 14%, rgba(4, 8, 14, 0.9) 86%, transparent)',
                  padding: 'clamp(16px, 4vw, 22px) 12px clamp(10px, 3vw, 14px)',
                }}>
                  <div style={{ color: '#fff', fontSize: 'clamp(16px, 4.4vw, 19px)', fontWeight: 800, letterSpacing: '0.02em' }}>
                    {ftReason ? 'Final Table Bubble' : 'Prize Bubble'}
                  </div>
                  <div style={{ color: '#e2e8f0', fontSize: 'clamp(12.5px, 3.6vw, 14.5px)', fontWeight: 600, marginTop: 5 }}>
                    Hand-for-hand play in progress
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: 'clamp(11px, 3.1vw, 12.5px)', marginTop: 3 }}>
                    Next hand will start after all tables finish their hands
                  </div>
                  {alsoMoney && (
                    <div style={{ color: '#fbbf24', fontSize: 'clamp(11px, 3.1vw, 12.5px)', marginTop: 3, fontWeight: 700 }}>
                      Money bubble is live too - {h4hAlive} left, {h4hPaid} get paid
                    </div>
                  )}
                  {waitingStrip(h4hWaiting, 'All tables done - next hand starting')}
                </div>
              </div>
            );
          }

          // 🏆 FINAL TABLE FORMING banner: the destination is parked while
          // the last feeder tables finish their hands and merge in. The
          // felt is cleared server-side (fresh table, no cards dealt) and
          // STAYS VISIBLE under a light dim - never a black box.
          if (td.finalTableForming === true && !td.onBreak && !thisTableHandLive) {
            return (
              <div style={{
                position: 'fixed', top: '96px', left: 0, right: 0, bottom: 0, zIndex: 1250, pointerEvents: 'none',
                background: 'rgba(3, 6, 11, 0.45)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', textAlign: 'center',
              }}>
                <div style={{
                  background: 'rgba(15,23,42,0.92)', borderRadius: 8,
                  padding: '4px 18px', display: 'inline-block',
                  color: '#fde68a', fontSize: 14, fontWeight: 800, letterSpacing: 3,
                }}>FINAL TABLE</div>
                <div style={{
                  marginTop: 6, background: 'rgba(10,14,22,0.95)',
                  border: '1px solid rgba(212,175,55,0.45)', borderRadius: 12,
                  padding: '10px 22px', color: '#cbd5e1', fontSize: 15, fontWeight: 600,
                }}>
                  Combining tables...
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                    The remaining tables are finishing their hands. Seats are
                    redrawn at random once everyone arrives.
                  </div>
                </div>
                {waitingStrip(
                  actTables.filter(t2 => t2 !== tableId),
                  'Everyone is here - setting up the final table',
                )}
                {!overlaySpectating && <BreakNavButtons navigate={navigate} tournamentId={(table as any)?.tournamentId} onSummary={() => setShowTournamentSidebar(true)} />}
              </div>
            );
          }

          // BREAK BANNER over the CLEARED FELT: the server parks the table
          // like a fresh hand with no cards dealt - show exactly that,
          // dimmed, with the break card floating over it. (The old opaque
          // curtain read as a black box, especially when spectating.)
          if (onBreak && breakPendingPhase) {
            return (
              <div style={{
                position: 'fixed', top: '96px', left: 0, right: 0, bottom: 0, zIndex: 1250, pointerEvents: 'none',
                background: 'rgba(3, 6, 11, 0.45)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', textAlign: 'center',
              }}>
                <div style={{
                  background: 'rgba(15,23,42,0.92)', borderRadius: 8,
                  padding: '4px 18px', display: 'inline-block',
                  color: '#e2e8f0', fontSize: 14, fontWeight: 700, letterSpacing: 1,
                }}>Break Time</div>
                <div style={{
                  marginTop: 6, background: 'rgba(10,14,22,0.95)',
                  border: '1px solid rgba(148,163,184,0.35)', borderRadius: 12,
                  padding: '10px 22px', color: '#cbd5e1', fontSize: 15, fontWeight: 600,
                }}>
                  Waiting for the other tables to finish their hand...
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                    The break clock starts when every table has stopped.
                  </div>
                </div>
                {waitingStrip(
                  actTables.filter(t2 => t2 !== tableId && !(td.tablesAtBreak || {})[t2]),
                  'All tables parked - break clock starting',
                )}
                {!overlaySpectating && <BreakNavButtons navigate={navigate} tournamentId={(table as any)?.tournamentId} onSummary={() => setShowTournamentSidebar(true)} />}
              </div>
            );
          }

          if (onBreak) {
            const msLeft = Math.max(0, breakEndsMs - feltTick);
            const mm = Math.floor(msLeft / 60000);
            const ss = Math.floor((msLeft % 60000) / 1000);
            const isFinalTableBreak = td.breakReason === 'final_table';
            return (
              <div style={{
                position: 'fixed', top: '96px', left: 0, right: 0, bottom: 0, zIndex: 1250, pointerEvents: 'none',
                background: 'rgba(3, 6, 11, 0.45)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', textAlign: 'center',
              }}>
                <div style={{
                  background: 'rgba(15,23,42,0.92)', borderRadius: 8,
                  padding: '4px 18px', display: 'inline-block',
                  color: isFinalTableBreak ? '#fde68a' : '#e2e8f0',
                  fontSize: 14, fontWeight: isFinalTableBreak ? 800 : 700,
                  letterSpacing: isFinalTableBreak ? 3 : 1,
                }}>{isFinalTableBreak ? 'FINAL TABLE BREAK' : 'Break Time'}</div>
                {isFinalTableBreak && (
                  <div style={{ color: '#94a3b8', fontSize: 12.5, marginTop: 6 }}>
                    Seats have been redrawn - play resumes when the clock hits zero.
                  </div>
                )}
                <div style={{
                  marginTop: 6, background: 'rgba(10,14,22,0.95)',
                  border: '1px solid rgba(148,163,184,0.35)', borderRadius: 12,
                  padding: '10px 30px', color: '#fff', fontSize: 42,
                  fontWeight: 800, fontFamily: 'ui-monospace, monospace', letterSpacing: 4,
                }}>
                  {mm.toString().padStart(2, '0')}:{ss.toString().padStart(2, '0')}
                </div>
                {!overlaySpectating && <BreakNavButtons navigate={navigate} tournamentId={(table as any)?.tournamentId} onSummary={() => setShowTournamentSidebar(true)} />}
              </div>
            );
          }

          // Center-felt info now renders INSIDE the table SVGs (via the
          // centerInfo prop computed in tournamentFeltInfo) so cards can
          // never collide with it. Only the break overlay renders here.
          return null;
        })()}

        {/* TOURNAMENT LEAVE CONFIRM - "leave" = sit out, never cash out */}
        {confirmTourneyLeave && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9200,
            background: 'rgba(0,0,0,0.65)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: 'rgba(13, 31, 46, 0.98)', borderRadius: 14,
              border: '1px solid rgba(212, 175, 55, 0.55)',
              padding: '22px 26px', maxWidth: 400, width: '90%',
              boxShadow: '0 12px 48px rgba(0,0,0,0.6)', color: '#e2e8f0',
            }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#fde68a', marginBottom: 10 }}>
                Leave the table?
              </div>
              <div style={{ fontSize: 13.5, color: '#cbd5e1', lineHeight: 1.7, marginBottom: 14 }}>
                You're still in the tournament - tournament chips can't be
                cashed out. Your seat is <b>held</b> and your stack stays on
                the table, paying blinds and folding while you're away.
                Return anytime from <b>MY TABLES</b>.
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setConfirmTourneyLeave(false)}
                  style={{
                    background: 'transparent', color: '#cbd5e1',
                    border: '1px solid rgba(148,163,184,0.45)', borderRadius: 8,
                    padding: '10px 18px', fontWeight: 700, cursor: 'pointer', fontSize: 14,
                  }}
                >
                  Keep Playing
                </button>
                <button
                  onClick={() => { setConfirmTourneyLeave(false); doLeaveTable(); }}
                  style={{
                    background: 'linear-gradient(135deg, #f5d976, #d4af37 60%, #a97f1f)',
                    color: '#181205', border: 'none', borderRadius: 8,
                    padding: '10px 18px', fontWeight: 800, cursor: 'pointer', fontSize: 14,
                  }}
                >
                  Sit Out & Leave
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TOURNAMENT REBUY CONFIRM - fixed price for a fixed stack */}
        {rebuyConfirm && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9200,
            background: 'rgba(0,0,0,0.65)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: 'rgba(13, 31, 46, 0.98)', borderRadius: 14,
              border: '1px solid rgba(245, 158, 11, 0.55)',
              padding: '22px 26px', maxWidth: 380, width: '90%',
              boxShadow: '0 12px 48px rgba(0,0,0,0.6)', color: '#e2e8f0',
            }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#fbbf24', marginBottom: 10 }}>
                Tournament Rebuy
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 6 }}>
                <div>Buy-in: <b>{rebuyConfirm.cost.toLocaleString()} {rebuyConfirm.currency}</b></div>
                <div>You receive: <b>{rebuyConfirm.chips.toLocaleString()} chips</b></div>
              </div>
              <div style={{ fontSize: 12.5, color: '#94a3b8', marginBottom: 14 }}>
                Adds another buy-in to the prize pool. You are dealt back in
                on the next hand.
              </div>
              {rebuyError && (
                <div style={{ fontSize: 13, color: '#f87171', marginBottom: 12, fontWeight: 600 }}>
                  {rebuyError}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setRebuyConfirm(null); setRebuyError(null); }}
                  disabled={rebuyBusy}
                  style={{
                    background: 'transparent', color: '#94a3b8',
                    border: '1px solid rgba(148,163,184,0.4)', borderRadius: 8,
                    padding: '9px 18px', fontWeight: 700, cursor: 'pointer', fontSize: 14,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!tableId || !(table as any)?.tournamentId) return;
                    setRebuyBusy(true);
                    const r = await gameApiService.tournamentRebuy((table as any).tournamentId, tableId);
                    setRebuyBusy(false);
                    if (r.success) {
                      setRebuyConfirm(null);
                    } else {
                      setRebuyError(r.error || 'Rebuy failed');
                    }
                  }}
                  disabled={rebuyBusy}
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: '#fff', border: 'none', borderRadius: 8,
                    padding: '9px 18px', fontWeight: 700,
                    cursor: rebuyBusy ? 'wait' : 'pointer', fontSize: 14,
                  }}
                >
                  {rebuyBusy ? 'Rebuying...' : `Rebuy for ${rebuyConfirm.cost.toLocaleString()}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TOURNAMENT ADD-ON CONFIRM - in-app dialog, styled like the rest */}
        {addOnConfirm && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9200,
            background: 'rgba(0,0,0,0.65)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: 'rgba(13, 31, 46, 0.98)', borderRadius: 14,
              border: '1px solid rgba(168, 85, 247, 0.5)',
              padding: '22px 26px', maxWidth: 380, width: '90%',
              boxShadow: '0 12px 48px rgba(0,0,0,0.6)', color: '#e2e8f0',
            }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#c084fc', marginBottom: 10 }}>
                Tournament Add-On
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 6 }}>
                <div>Cost: <b>{addOnConfirm.cost?.toLocaleString?.() ?? addOnConfirm.cost}</b></div>
                <div>Extra chips: <b>{addOnConfirm.chips?.toLocaleString?.() ?? addOnConfirm.chips}</b></div>
              </div>
              <div style={{ fontSize: 12.5, color: '#94a3b8', marginBottom: 14 }}>
                One per player, per tournament. Chips are added to your stack
                at the next hand.
              </div>
              {addOnError && (
                <div style={{ fontSize: 13, color: '#f87171', marginBottom: 12, fontWeight: 600 }}>
                  {addOnError}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setAddOnConfirm(null); setAddOnError(null); }}
                  disabled={addOnBusy}
                  style={{
                    background: 'transparent', color: '#94a3b8',
                    border: '1px solid rgba(148,163,184,0.4)', borderRadius: 8,
                    padding: '9px 18px', fontWeight: 700, cursor: 'pointer', fontSize: 14,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!tableId || !(table as any)?.tournamentId) return;
                    setAddOnBusy(true);
                    const r = await gameApiService.tournamentAddOn((table as any).tournamentId, tableId);
                    setAddOnBusy(false);
                    if (r.success) {
                      setAddOnConfirm(null);
                    } else {
                      setAddOnError(r.error || 'Add-on failed');
                    }
                  }}
                  disabled={addOnBusy}
                  style={{
                    background: 'linear-gradient(135deg, #a855f7 0%, #7e22ce 100%)',
                    color: '#fff', border: 'none', borderRadius: 8,
                    padding: '9px 18px', fontWeight: 700,
                    cursor: addOnBusy ? 'wait' : 'pointer', fontSize: 14,
                  }}
                >
                  {addOnBusy ? 'Buying...' : `Buy Add-On (${addOnConfirm.cost})`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* THE POKER TABLE - premium theme OR any phone gets the portrait
            racetrack; classic desktop keeps the classic table */}
        {safeGameState ? (
          (table?.tableTheme === 'premium' || isPhoneLayout) ? (
            <PremiumThemedTableSVG
              sessionMode={isSessionTable}
              // Table rules drive the straddle button, AoF indicator and
              // RIT gating - this prop was never passed, so those UI
              // pieces were dead on premium and every phone.
              specialRules={(table as any)?.specialRules}
              gameState={safeGameState}
              currentUserId={isRecorderView && recOwner.uid ? recOwner.uid : currentUserId}
              tableId={tableId || ''}
              usernames={usernameMap}
              userPhotos={userPhotosMap}
              onSeatClick={handleSeatClick}
              voiceEnabled={voiceEnabled}
              soundEnabled={soundEnabled}
              tableName={table?.name}
              // Tap a seat to open the player card (stats/VPIP) - classic
              // always had this; premium dropped the prop and phones had no
              // way to inspect anyone.
              onPlayerClick={(id: string, name: string) => setProfileTarget({ id, name })}
              // Sitting-out players (dealt out, seat held) render dimmed
              awaySeated={Object.keys(((table as any)?.awayPlayers) || {})
                .filter(uid => (table?.players || []).includes(uid)
                  && !(safeGameState?.players || []).some((p: any) => p && p.id === uid))
                .map(uid => ({ id: uid, name: usernameMap[uid] || 'Player' }))}
              maxSeats={table?.maxSeats || 8}
              // Dealer artwork belongs to premium-themed tables only. Classic
              // tables reach this component on phones (portrait racetrack for
              // every table) and must NOT inherit the component's default
              // Sophia image - empty string disables the dealer entirely.
              dealerImageUrl={table?.tableTheme === 'premium' ? table?.dealerImageUrl : ''}
              tableCurrency={table?.currency || 'playcoins'}
              onTipDealer={handleOpenTipModal}
              centerInfo={tournamentFeltInfo}
              tournamentInfo={(table as any)?.tournamentId && tournamentData ? (() => {
                // Build the on-table info strip from the live tournament doc.
                // levelStartedAtMs handles both shapes the doc carries: a raw
                // millisecond field, or a Firestore Timestamp under
                // currentLevelStartedAt.
                const td: any = tournamentData;
                const blindLevels: any[] = Array.isArray(td.blindLevels) ? td.blindLevels : [];
                const curLevel = td.currentBlindLevel || 1;
                const lvlCfg = blindLevels.find((l: any) => l?.level === curLevel) || blindLevels[0] || {};
                let levelStartedAtMs: number | undefined = typeof td.blindLevelStartedAt === 'number'
                  ? td.blindLevelStartedAt
                  : undefined;
                if (levelStartedAtMs === undefined && td.currentLevelStartedAt) {
                  const v: any = td.currentLevelStartedAt;
                  if (typeof v?.toMillis === 'function') levelStartedAtMs = v.toMillis();
                  else if (v?.seconds !== undefined) levelStartedAtMs = v.seconds * 1000;
                  else { const t = new Date(v).getTime(); if (!isNaN(t)) levelStartedAtMs = t; }
                }
                const active: any[] = Array.isArray(td.activePlayers) ? td.activePlayers : [];
                const registered: any[] = Array.isArray(td.registeredPlayers) ? td.registeredPlayers : [];
                // Prize ladder: "Next Prize" = what you win if you finish at
                // the current remaining-count position; "Pay Jump" = the next
                // rung up. Computed from payoutStructure percentages against
                // the live prize pool (which includes rebuys/add-ons).
                const pool = Math.max(td.guaranteedPrize || 0, td.prizePool || 0);
                const structure: any[] = Array.isArray(td.payoutStructure) ? td.payoutStructure : [];
                const remaining = active.length || 0;
                const prizeAt = (pos: number): number | undefined => {
                  const row = structure.find((s: any) => s?.position === pos);
                  return row ? Math.round(pool * (row.percentage / 100) * 100) / 100 : undefined;
                };
                const nextPrize = remaining > 0 ? prizeAt(remaining) : undefined;
                const payJump = remaining > 1 ? prizeAt(remaining - 1) : undefined;
                return {
                  name: td.name,
                  currentBlindLevel: curLevel,
                  smallBlind: lvlCfg.smallBlind ?? safeGameState.smallBlind,
                  bigBlind: lvlCfg.bigBlind ?? safeGameState.bigBlind,
                  ante: lvlCfg.ante,
                  levelStartedAtMs,
                  levelDurationMin: lvlCfg.duration || td.blindLevelDuration || 10,
                  playersRemaining: active.length || undefined,
                  totalPlayers: registered.length || undefined,
                  currency: td.currency || table?.currency || 'playcoins',
                  nextPrize,
                  payJump,
                  prizePool: pool || undefined,
                };
              })() : undefined}
            />
          ) : (
            <PokerTable
              sessionMode={isSessionTable}
              gameState={safeGameState}
              currentUserId={isRecorderView && recOwner.uid ? recOwner.uid : currentUserId}
              tableId={tableId || ''}
              usernames={usernameMap}
              userPhotos={userPhotosMap}
              onReload={handleOpenReloadModal}
              onSeatClick={handleSeatClick}
              voiceEnabled={voiceEnabled}
              soundEnabled={soundEnabled}
              specialRules={table?.specialRules}
              tableName={table?.name}
              maxSeats={table?.maxSeats || 8}
              ownerId={table?.ownerId}
              centerInfo={tournamentFeltInfo}
              leaderBadges={topLeaders}
              onPlayerClick={(id, name) => setProfileTarget({ id, name })}
              seatTags={(() => {
                void tagVersion; // re-read tags after edits in the sheet
                const tags: { [id: string]: string } = {};
                (safeGameState?.players || []).forEach((p: any) => {
                  if (!p?.id) return;
                  const t = getSeatTag(p.id);
                  if (t) tags[p.id] = t;
                });
                return tags;
              })()}
            />
          )
        ) : (
          <div className="waiting-for-gamestate">
            <h2>⏳ Setting up table...</h2>
            <p>Waiting for players to join.</p>
          </div>
        )}

        {/*  MEDIA CHAT BAR (Voice + Video) */}

        <RecordingConsentGate table={table} currentUserId={currentUserId} seated={!!currentPlayer} onBlockedChange={setRecMediaBlocked} />
        {table?.voiceChatEnabled && (
          <MediaChatBar
            tableId={tableId || ''}
            odid={currentUserId}
            mediaBlocked={recMediaBlocked}
            recordingOffer={((table as any).tournamentId || (table as any).isTournament) ? null : recordingOffer}
            onJoinAndRecord={(showCards: boolean) => gameApiService.recordingsStart(tableId || '', showCards)}
            recorderMode={searchParams.get('rec') === '1'}
            mediaChatEnabled={table.voiceChatEnabled}
            usernames={usernameMap}
            isTournament={!!((table as any).isTournament || (table as any).tournamentId)}
            mediaMode={(table as any).mediaMode === 'voice' ? 'voice' : 'video'}
          />
        )}
      </div>

      {/* RELOAD MODAL */}
      {showReloadModal && table && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div className="reload-modal" style={{
            background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: '16px',
            padding: '24px',
            width: '90%',
            maxWidth: '360px',
            border: '1px solid #334155',
          }}>
            {/*  Show different header for bust vs reload */}

            {hasBusted ? (
              <>
                <h2 style={{ color: '#ef4444', margin: '0 0 8px', fontSize: '20px' }}>
                   You Busted!

                </h2>
                <p style={{ color: '#94a3b8', margin: '0 0 16px', fontSize: '14px' }}>
                  You lost all your chips. Rebuy to continue playing.
                </p>
              </>
            ) : (
              <h2 style={{ color: '#fff', margin: '0 0 16px', fontSize: '20px' }}>
                 Reload Chips

              </h2>
            )}
            
            <div style={{ color: '#94a3b8', marginBottom: '16px' }}>
              Wallet Balance: {walletBalance.toLocaleString()} {table.currency === 'pkcash' ? 'PSS Coins' : 'PlayCoins'}
            </div>
            
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setReloadAmount(table.minBuyIn || 100)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: reloadAmount === table.minBuyIn ? '#10b981' : '#334155',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                Min ({table.minBuyIn})
              </button>
              <button
                onClick={() => setReloadAmount(Math.round((table.minBuyIn + table.maxBuyIn) / 2))}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: reloadAmount === Math.round((table.minBuyIn + table.maxBuyIn) / 2) ? '#10b981' : '#334155',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                Mid ({Math.round((table.minBuyIn + table.maxBuyIn) / 2)})
              </button>
              <button
                onClick={() => setReloadAmount(Math.min(table.maxBuyIn, walletBalance))}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: reloadAmount === Math.min(table.maxBuyIn, walletBalance) ? '#10b981' : '#334155',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                Max ({Math.min(table.maxBuyIn, walletBalance)})
              </button>
            </div>
            
            <input
              type="number"
              value={reloadAmount}
              onChange={(e) => setReloadAmount(Math.max(0, Math.min(Number(e.target.value), walletBalance, table.maxBuyIn)))}
              style={{
                width: '100%',
                padding: '12px',
                marginBottom: '16px',
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '18px',
                textAlign: 'center',
              }}
            />
            
            <label style={{
              display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px',
              color: '#94a3b8', fontSize: '13px', cursor: 'pointer', userSelect: 'none',
            }}>
              <input
                type="checkbox"
                checked={autoReload}
                onChange={toggleAutoReload}
                style={{ width: '16px', height: '16px', accentColor: '#10b981', cursor: 'pointer' }}
              />
              Auto reload at min buy-in when I bust
            </label>

            <div style={{ display: 'flex', gap: '12px', flexDirection: hasBusted ? 'column' : 'row' }}>
              {/*  For busted players: Show Leave Table option */}

              {hasBusted && (
                <button
                  onClick={() => {
                    setShowReloadModal(false);
                    setHasBusted(false);
                    handleLeaveTable();
                  }}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                >
                   Leave Table

                </button>
              )}
              
              <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                <button
                  onClick={() => {
                    setShowReloadModal(false);
                    // Don't clear hasBusted - they're still busted, just watching
                  }}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: 'transparent',
                    border: '1px solid #475569',
                    borderRadius: '8px',
                    color: '#94a3b8',
                    fontSize: '16px',
                    cursor: 'pointer',
                  }}
                >
                  {hasBusted ? ' Watch' : 'Cancel'}

                </button>
                <button
                  onClick={handleReloadChips}
                  disabled={reloadAmount <= 0 || reloadAmount > walletBalance}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: reloadAmount > 0 && reloadAmount <= walletBalance
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : '#334155',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: reloadAmount > 0 && reloadAmount <= walletBalance ? 'pointer' : 'not-allowed',
                  }}
                >
                  {hasBusted ? ' Rebuy' : 'Reload'} {reloadAmount}

                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* INVITE PLAYERS MODAL */}
      <UserSearch
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        currentUid={currentUserId}
        currentUserName={currentUsername}
        tableId={tableId}
        tableName={table?.name || 'Poker Table'}
        isPrivate={table?.isPrivate || false}
      />

      {/* WATCH-INVITE MODAL (friends/family spectator code) */}
      <WatchInviteModal
        isOpen={showWatchInviteModal}
        onClose={() => setShowWatchInviteModal(false)}
      />

      {/* HAND HISTORY DRAWER (PokerIntel - last 21 hands + the live hand) */}
      {/* 🏠 HOME GAME: paused / ended banners visible to everyone */}
      {isSessionTable && (table as any)?.sessionPaused && !(table as any)?.sessionEnded && (
        <div style={{
          position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 8px)', left: '50%',
          transform: 'translateX(-50%)', zIndex: 8800, background: 'rgba(8,20,30,0.95)',
          border: '2px solid #fbbf24', borderRadius: '14px', padding: '8px 18px',
          color: '#fcd34d', fontSize: '13px', fontWeight: 700,
        }}>
          Game paused by the host. Hands resume when the host taps Resume.
        </div>
      )}
      {isSessionTable && (table as any)?.sessionEnded && (
        <div style={{
          position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 8px)', left: '50%',
          transform: 'translateX(-50%)', zIndex: 8800, background: 'rgba(8,20,30,0.95)',
          border: '2px solid #8B5CF6', borderRadius: '14px', padding: '8px 18px',
          color: '#c4b5fd', fontSize: '13px', fontWeight: 700, textAlign: 'center',
        }}>
          Game ended. The final ledger is saved in everyone's profile.
        </div>
      )}

      {/* 🏠 HOME GAME: pending chip requests, host's eyes only. Cleared
          automatically when the host assigns chips, or dismissed here. */}
      {isSessionTable && isTableHost && !(table as any)?.sessionEnded
        && Object.keys((table as any)?.chipRequests || {}).length > 0 && (
        <div style={{
          position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 52px)', left: '50%',
          transform: 'translateX(-50%)', zIndex: 8800, background: 'rgba(8,20,30,0.95)',
          border: '2px solid #10b981', borderRadius: '14px', padding: '10px 14px',
          color: '#e2e8f0', fontSize: '13px', maxWidth: '92vw',
        }}>
          {Object.entries(((table as any).chipRequests || {}) as Record<string, any>).map(([uid, req]) => (
            <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '3px 0' }}>
              <span style={{ fontWeight: 700 }}>{req?.name || 'A player'} is asking for chips</span>
              <button
                onClick={() => setShowAssignChips(true)}
                style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff',
                  border: 'none', borderRadius: '8px', padding: '5px 12px', fontWeight: 700,
                  fontSize: '12px', cursor: 'pointer' }}
              >
                Assign
              </button>
              <button
                onClick={() => tableId && gameApiService.hostDismissChipRequest(tableId, uid)}
                style={{ background: 'rgba(255,255,255,0.1)', color: '#cbd5e1',
                  border: '1px solid rgba(255,255,255,0.25)', borderRadius: '8px',
                  padding: '5px 12px', fontWeight: 600, fontSize: '12px', cursor: 'pointer' }}
              >
                Dismiss
              </button>
            </div>
          ))}
        </div>
      )}

      {/* 👥 GROUPS: pick a group to rally to this table */}
      {showGroupInvite && (
        <div onClick={() => setShowGroupInvite(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 9200, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: '380px', background: '#0f1722',
              border: '2px solid rgba(139,92,246,0.5)', borderRadius: '16px', padding: '20px' }}>
            <h3 style={{ color: '#fff', margin: '0 0 6px', fontSize: '17px' }}>Invite a group</h3>
            <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 14px' }}>
              Everyone in the group gets a push with a one-tap link to this table.
            </p>
            {groupInviteList === null ? (
              <p style={{ color: '#64748b', fontSize: '13px' }}>Loading your groups...</p>
            ) : groupInviteList.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: '13px' }}>
                You are not in any groups yet. Create one from the Groups button
                on the tables page.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '40vh', overflowY: 'auto', marginBottom: '6px' }}>
                {groupInviteList.map((g: any) => (
                  <button key={g.id} disabled={groupInviteBusy !== null}
                    onClick={() => handleGroupInvite(g.id)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      width: '100%', textAlign: 'left',
                      background: 'rgba(255,255,255,0.05)', border: '1px solid #333', borderRadius: '10px',
                      padding: '12px 14px', color: '#e2e8f0', fontSize: '14px', cursor: 'pointer' }}>
                    <span style={{ fontWeight: 600 }}>{g.name}</span>
                    <span style={{ color: '#94a3b8', fontSize: '12px' }}>
                      {groupInviteBusy === g.id ? 'Sending...' : `${g.memberCount} member${g.memberCount === 1 ? '' : 's'}`}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => setShowGroupInvite(false)}
              style={{ width: '100%', marginTop: '10px', padding: '10px 0', borderRadius: '10px',
                border: '1px solid #334155', background: 'transparent', color: '#cbd5e1',
                fontWeight: 600, cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* 🏠 HOME GAME: host kicks a player */}
      {showKickSheet && isSessionTable && isTableHost && (
        <div onClick={() => setShowKickSheet(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 9200, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: '360px', background: '#0f1722',
              border: '1px solid rgba(248,113,113,0.5)', borderRadius: '16px', padding: '20px' }}>
            <h3 style={{ color: '#fff', margin: '0 0 6px', fontSize: '17px' }}>Kick a player</h3>
            <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 14px' }}>
              Their final chips are recorded on the game's results sheet.
            </p>
            {(table?.players || []).filter((id: string) => id !== currentUserId).map((id: string) => (
              <button key={id} disabled={kickBusy}
                onClick={() => handleHostKick(id)}
                style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: '8px',
                  background: 'rgba(255,255,255,0.05)', border: '1px solid #333', borderRadius: '10px',
                  padding: '12px 14px', color: '#e2e8f0', fontSize: '14px', cursor: 'pointer' }}>
                {usernameMap[id] || 'Player'}
              </button>
            ))}
            {(table?.players || []).filter((id: string) => id !== currentUserId).length === 0 && (
              <p style={{ color: '#64748b', fontSize: '13px' }}>Nobody else is seated</p>
            )}
          </div>
        </div>
      )}

      {/* 🏠 HOME GAME: host changes blinds (between hands) */}
      {showBlindsSheet && isSessionTable && isTableHost && (
        <div onClick={() => setShowBlindsSheet(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 9200, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: '340px', background: '#0f1722',
              border: '1px solid rgba(0,217,217,0.5)', borderRadius: '16px', padding: '20px' }}>
            <h3 style={{ color: '#fff', margin: '0 0 6px', fontSize: '17px' }}>Change blinds</h3>
            <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 14px' }}>
              Takes effect on the next hand.
            </p>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>Small blind</div>
                <input type="number" value={blindsSB || ''} min={1}
                  onChange={e => setBlindsSB(Number(e.target.value))}
                  style={{ width: '100%', padding: '10px', background: '#1e293b', border: '1px solid #334155',
                    borderRadius: '8px', color: '#fff', fontSize: '16px' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>Big blind</div>
                <input type="number" value={blindsBB || ''} min={1}
                  onChange={e => setBlindsBB(Number(e.target.value))}
                  style={{ width: '100%', padding: '10px', background: '#1e293b', border: '1px solid #334155',
                    borderRadius: '8px', color: '#fff', fontSize: '16px' }} />
              </div>
            </div>
            <button disabled={blindsBusy || !(blindsSB > 0) || !(blindsBB > 0) || blindsSB > blindsBB}
              onClick={handleHostBlinds}
              style={{ width: '100%', padding: '13px 0', borderRadius: '12px', border: 'none',
                background: 'linear-gradient(135deg, #00d9d9, #0891b2)', color: '#062128',
                fontWeight: 800, fontSize: '15px', cursor: 'pointer',
                opacity: blindsBusy || !(blindsSB > 0) || !(blindsBB > 0) || blindsSB > blindsBB ? 0.5 : 1 }}>
              {blindsBusy ? 'Updating...' : 'Set blinds'}
            </button>
          </div>
        </div>
      )}

      {/* 🏠 HOME GAME: end game confirm */}
      {showEndConfirm && isSessionTable && isTableHost && (
        <div onClick={() => setShowEndConfirm(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 9200, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: '340px', background: '#0f1722',
              border: '1px solid rgba(248,113,113,0.5)', borderRadius: '16px', padding: '20px', textAlign: 'center' }}>
            <h3 style={{ color: '#fff', margin: '0 0 8px', fontSize: '17px' }}>End the game?</h3>
            <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 16px' }}>
              No more hands will be dealt. The final results ledger is saved
              to everyone's profile immediately.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowEndConfirm(false)}
                style={{ flex: 1, padding: '12px 0', borderRadius: '12px', border: '1px solid #334155',
                  background: 'transparent', color: '#cbd5e1', fontWeight: 700, cursor: 'pointer' }}>
                Keep playing
              </button>
              <button disabled={endBusy} onClick={handleHostEnd}
                style={{ flex: 1, padding: '12px 0', borderRadius: '12px', border: 'none',
                  background: 'linear-gradient(135deg, #ef4444, #b91c1c)', color: '#fff',
                  fontWeight: 800, cursor: 'pointer', opacity: endBusy ? 0.5 : 1 }}>
                {endBusy ? 'Ending...' : 'End game'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🏠 HOME GAME: host assigns session chips */}
      {showAssignChips && isSessionTable && isTableHost && (
        <div onClick={() => setShowAssignChips(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 9200, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: '420px', background: '#0f1722',
              border: '2px solid rgba(139,92,246,0.5)', borderRadius: '16px', padding: '22px' }}>
            <h2 style={{ color: '#fff', margin: '0 0 4px', fontSize: '18px', fontWeight: 700 }}>Assign Chips</h2>
            <p style={{ color: '#94a3b8', margin: '0 0 16px', fontSize: '13px' }}>
              Home game. Chips are session-only scorekeeping with no cash value.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '40vh', overflowY: 'auto', marginBottom: '14px' }}>
              {((table?.players || []) as string[]).map(pid => {
                const row = ((table?.gameState?.players || []) as any[]).find(rp => rp && rp.id === pid);
                const stack = row ? (row.chips || 0) : ((table as any)?.playerBuyIns?.[pid] || 0);
                return ({ id: pid, chips: stack, name: row?.name });
              }).map(p => (
                <button key={p.id}
                  onClick={() => setAssignTarget(p.id)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 14px', borderRadius: '10px', cursor: 'pointer',
                    background: assignTarget === p.id ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.05)',
                    border: assignTarget === p.id ? '2px solid #8B5CF6' : '2px solid #333',
                    color: '#e2e8f0', fontSize: '14px', fontWeight: 600,
                  }}>
                  <span>{usernameMap[p.id] || p.name || 'Player'}{p.id === currentUserId ? ' (you)' : ''}</span>
                  <span style={{ color: '#fbbf24', fontWeight: 800 }}>{Math.round(p.chips || 0).toLocaleString('en-US')}</span>
                </button>
              ))}
            </div>
            <input type="number" value={assignAmount || ''}
              onChange={e => setAssignAmount(Math.max(0, Math.floor(Number(e.target.value))))}
              placeholder="Amount"
              style={{ width: '100%', padding: '12px', marginBottom: '12px', background: '#1e293b',
                border: '1px solid #334155', borderRadius: '8px', color: '#fff', fontSize: '17px', textAlign: 'center' }} />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button disabled={assignBusy || !assignTarget || !assignAmount}
                onClick={() => handleAssignChips(1)}
                style={{ flex: 1, padding: '13px 0', borderRadius: '10px', border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: '#fff', fontWeight: 800, fontSize: '15px',
                  opacity: assignBusy || !assignTarget || !assignAmount ? 0.5 : 1 }}>
                Add
              </button>
              <button disabled={assignBusy || !assignTarget || !assignAmount}
                onClick={() => handleAssignChips(-1)}
                style={{ flex: 1, padding: '13px 0', borderRadius: '10px', border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg,#ef4444,#b91c1c)', color: '#fff', fontWeight: 800, fontSize: '15px',
                  opacity: assignBusy || !assignTarget || !assignAmount ? 0.5 : 1 }}>
                Remove
              </button>
              <button onClick={() => setShowAssignChips(false)}
                style={{ flex: 1, padding: '13px 0', borderRadius: '10px', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)',
                  color: '#e2e8f0', fontWeight: 700, fontSize: '15px' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REAL TIME RESULTS - per-player session buy-in vs winnings */}
      {showRealTimeResults && (() => {
        const sess = (((table as any)?.sessionBuyIns) || {}) as Record<string, number>;
        const isBotId = (id: string) => id.startsWith('house_bot_') || id.startsWith('bot_');
        const seen = new Set<string>();
        const rows: Array<{ id: string; name: string; buyIn: number | null; chips: number; cashedOut?: number; left?: boolean }> = [];
        for (const p of ((table?.gameState?.players || []) as any[])) {
          if (!p || !p.id) continue;
          seen.add(p.id);
          rows.push({
            id: p.id,
            name: usernameMap[p.id] || p.name || (isBotId(p.id) ? 'House Bot' : 'Player'),
            // Bots buy in at the table minimum by design; humans seated
            // before this shipped have no record - show a dash, not a guess.
            buyIn: typeof sess[p.id] === 'number' ? sess[p.id]
              : (isBotId(p.id) ? (table?.minBuyIn || null) : null),
            chips: p.chips || 0,
          });
        }
        // Seat-held players dealt out (sitting out / bust grace) have no
        // gameState row - their stack is 0 until they reload.
        for (const uid of Object.keys(((table as any)?.awayPlayers) || {})) {
          if (seen.has(uid) || !(table?.players || []).includes(uid)) continue;
          seen.add(uid);
          rows.push({
            id: uid,
            name: usernameMap[uid] || 'Player',
            buyIn: typeof sess[uid] === 'number' ? sess[uid] : null,
            chips: 0,
          });
        }
        // 🏠 Session ledger: departed players stay on the settle-up sheet
        // with the stack they left with. Rejoiners get their prior cashout
        // folded into their live row instead.
        const cashouts = (((table as any)?.sessionCashouts) || {}) as Record<string, number>;
        if (isSessionTable) {
          for (const r of rows) {
            if (typeof cashouts[r.id] === 'number') (r as any).cashedOut = cashouts[r.id];
          }
          for (const uid of Object.keys(cashouts)) {
            if (seen.has(uid)) continue;
            rows.push({
              id: uid,
              name: usernameMap[uid] || 'Player',
              buyIn: typeof sess[uid] === 'number' ? sess[uid] : null,
              chips: 0,
              cashedOut: cashouts[uid],
              left: true,
            } as any);
          }
        }
        return (
          <RealTimeResultsSheet
            isOpen={showRealTimeResults}
            onClose={() => setShowRealTimeResults(false)}
            rows={rows}
            currency={(table?.currency as 'playcoins' | 'pkcash') || 'playcoins'}
            sessionMode={isSessionTable}
          />
        );
      })()}

      <HandHistoryDrawer
        tableId={tableId || ''}
        isOpen={showHandHistory}
        onClose={() => setShowHandHistory(false)}
        currentUserId={currentUserId}
        usernames={{
          ...usernameMap,
          // The felt resolves bot names from table.houseBots meta and the
          // viewer's own name separately - the drawer needs both too, or
          // it prints truncated uids ("house_bo", "11tBjYey") for seats
          // the felt names perfectly well.
          ...Object.fromEntries(((table as any)?.houseBots || [])
            .filter((b: any) => b?.botId && b?.botName)
            .map((b: any) => [b.botId, b.botName])),
          ...(currentUserId ? { [currentUserId]: currentUsername || 'You' } : {}),
        }}
        tableCreatedAt={(table as any)?.createdAt}
        // safeGameState, not the raw broadcast: it has the viewer's OWN
        // securely-fetched hole cards grafted in. The raw state blanks
        // them, which showed the player card backs on their own live hand.
        liveGameState={safeGameState || gameState}
      />
      
      {/* BUY-IN MODAL */}
      <BuyInModal
        sessionMode={isSessionTable}
        isOpen={showBuyInModal && table !== null}
        onClose={async () => {
          //  FIX: Actually leave the table when canceling buy-in

          // This prevents the "trapped player" issue where they show up on table browser
          // but can't rejoin or properly leave
          if (isLeavingTableRef.current) {
            // Already leaving, just close modal
            setShowBuyInModal(false);
            return;
          }
          
          isLeavingTableRef.current = true;
          setShowBuyInModal(false);
          
          try {
            console.log(`👋 [BUY-IN CANCEL] Player canceling buy-in, leaving table ${tableId}`);
            await gameApiService.secureLeaveTable(tableId);
            localStorage.removeItem('activeTableId');
          } catch (err) {
            console.error('❌ [BUY-IN CANCEL] Failed to leave table:', err);
            // Still navigate away even if leave fails
          }
          
          navigate('/tables');
        }}
        onConfirm={handleBuyInConfirm}
        minBuyIn={table?.minBuyIn || 40}
        maxBuyIn={table?.maxBuyIn || 200}
        currency={table?.currency || 'playcoins'}
        userId={currentUserId}
        fixedStackNote={table?.specialRules?.allInOrFold
          ? `All-in or Fold: every hand you either move all-in or fold. Your stack is topped back up to ${(table?.specialRules as any)?.aofStackBB || 8} big blinds after each hand; winnings above that go to your balance.`
          : undefined}
      />
      
      {/*  7-2 BOUNTY CELEBRATION OVERLAY */}

      {showBountyDisplay && bountyWinner && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          animation: 'fadeIn 0.3s ease-out',
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            padding: '40px 60px',
            borderRadius: '20px',
            textAlign: 'center',
            boxShadow: '0 0 60px rgba(245, 158, 11, 0.5)',
            animation: 'pulse 1s infinite',
          }}>
            <div style={{ fontSize: '80px', marginBottom: '16px' }}></div>

            <h2 style={{ 
              fontSize: '32px', 
              fontWeight: 'bold', 
              color: '#000',
              marginBottom: '12px',
            }}>
              7-2 BOUNTY!
            </h2>
            <p style={{ 
              fontSize: '24px', 
              color: '#1a1a1a',
              marginBottom: '8px',
            }}>
              {usernameMap[bountyWinner] || 'Player'} wins with 7-2 offsuit!
            </p>
            <p style={{ 
              fontSize: '28px', 
              fontWeight: 'bold',
              color: '#000',
            }}>
              +${bountyAmount} Bounty Collected!
            </p>
          </div>
        </div>
      )}
      
      {/*  BOMB POT ANNOUNCEMENT OVERLAY - timed: it used to render for the
          entire flop street, parking a giant red card over the board. */}

      {showBombBanner && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
          padding: '30px 50px',
          borderRadius: '20px',
          textAlign: 'center',
          boxShadow: '0 0 60px rgba(239, 68, 68, 0.6)',
          zIndex: 9998,
          animation: 'bombPulse 0.5s ease-out',
          pointerEvents: 'none',
        }}>
          <div style={{ fontSize: '60px', marginBottom: '8px' }}></div>

          <h2 style={{ 
            fontSize: '28px', 
            fontWeight: 'bold', 
            color: '#fff',
            textTransform: 'uppercase',
            letterSpacing: '2px',
          }}>
            BOMB POT!
          </h2>
          <p style={{ 
            fontSize: '18px', 
            color: '#fecaca',
            marginTop: '8px',
          }}>
            Everyone antes {gameState.bombPotAnte} • Straight to flop!
          </p>
        </div>
      )}
      
      {/*  TIP DEALER MODAL - Only for premium tables */}

      {table?.tableTheme === 'premium' && (
        <>
          <TipDealerModal
            isOpen={showTipModal}
            onClose={handleCloseTipModal}
            tableId={tableId || ''}
            currentUserId={currentUserId}
            currentUserName={currentUsername}
            tableCurrency={(table?.currency as TipCurrency) || 'playcoins'}
            voiceEnabled={voiceEnabled}
          />
          
          {currentTipAnimation && (
            <TipAnimationOverlay
              isVisible={true}
              tipperName={currentTipAnimation.tipperName}
              amount={currentTipAnimation.amount}
              currency={currentTipAnimation.currency}
              emoji={currentTipAnimation.emoji}
              onComplete={handleTipAnimationComplete}
            />
          )}
        </>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes bombPulse {
          0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0; }
          50% { transform: translate(-50%, -50%) scale(1.1); }
          100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
        }
      `}</style>

      {/* TOURNAMENT SIDEBAR */}
      {(table as any)?.tournamentId && (
        <TournamentSidebar
          tournamentId={(table as any).tournamentId}
          currentUserId={currentUserId}
          isOpen={showTournamentSidebar}
          onClose={() => setShowTournamentSidebar(false)}
          usernames={usernameMap}
        />
      )}

      {/* CINEMATIC MOMENTS: FINAL TABLE / IN THE MONEY */}
      {cinematic && (
        <TournamentCinematic
          mode={cinematic.mode}
          subtitle={cinematic.subtitle}
          onDone={() => setCinematic(cinematicQueueRef.current.shift() || null)}
        />
      )}

      {/* TABLE CLOSED OVERLAY */}
      <TableClosedOverlay
        visible={tableClosedReason !== null}
        tournamentId={(table as any)?.tournamentId}
        reason={tableClosedReason || 'merged'}
        finishPosition={myBustInfo?.position}
        wonAmount={myBustInfo?.amount}
        currency={(tournamentData?.currency as string) || 'pkcash'}
      />

      {/* TAP-A-PLAYER PROFILE SHEET */}
      {profileTarget && (() => {
        const row: any = (gameState?.players || []).find((p: any) => p && p.id === profileTarget.id);
        return (
          <PlayerProfileSheet
            playerId={profileTarget.id}
            playerName={usernameMap[profileTarget.id] || profileTarget.name}
            photoURL={userPhotosMap[profileTarget.id]}
            chips={(row?.chips || 0) + (row?.currentBet || 0)}
            bigBlind={gameState?.bigBlind || table?.bigBlind || 0}
            onClose={() => setProfileTarget(null)}
            onTagChange={() => setTagVersion(v => v + 1)}
            tableId={tableId}
            viewerId={currentUserId || undefined}
            viewerName={currentUsername}
          />
        );
      })()}

      {/* SPECTATOR BANNER */}
      <SpectatorBanner
        visible={spectatorMode}
        tournamentId={(table as any)?.tournamentId}
        currentUserId={currentUserId}
        spectatingTableId={tableId}
        onLeave={() => {
          if (tableId) gameApiService.leaveSpectator(tableId).catch(() => {});
        }}
      />

      {/* TOURNAMENT START COUNTDOWN - only renders if handStartsAt is in
          the future. Self-removes when the countdown elapses. */}
      <TournamentStartCountdown
        handStartsAt={(gameState as any)?.handStartsAt}
      />

      {/* TOURNAMENT BUST MODAL - replaces cash Reload UI on tournament
          tables. Shows rebuy cost, chip restore, and late-reg countdown. */}
      {(table as any)?.tournamentId && (
        <TournamentBustModal
          tournamentId={(table as any).tournamentId}
          visible={showTournamentBust}
          tournamentName={tournamentData?.name}
          buyInAmount={tournamentData?.buyInAmount || 0}
          currency={(tournamentData?.currency as 'playcoins' | 'pkcash') || 'pkcash'}
          rebuyChips={tournamentData?.startingChips || 5000}
          walletBalance={walletBalance}
          lateRegEndAt={(tournamentData?.lateRegistrationEndTime as any)?.toDate
            ? (tournamentData.lateRegistrationEndTime as any).toDate()
            : tournamentData?.lateRegistrationEndTime}
          currentBlindLevel={tournamentData?.currentBlindLevel || 1}
          lateRegLevels={tournamentData?.lateRegLevels || 4}
          rebuysUsed={(tournamentData?.rebuysByUser || {})[currentUserId || ''] || 0}
          rebuysAllowed={tournamentData?.rebuyAllowed !== false
            && tournamentData?.handForHand !== true
            && tournamentData?.finalTableFormed !== true
            && tournamentData?.finalTableForming !== true
            && !(Array.isArray(tournamentData?.payoutStructure) && tournamentData.payoutStructure.length > 0
              && Array.isArray(tournamentData?.activePlayers) && tournamentData.activePlayers.length > 0
              && tournamentData.activePlayers.length <= tournamentData.payoutStructure.length)}
          rebuying={tournamentRebuying}
          finishPosition={myBustInfo?.position}
          wonAmount={myBustInfo?.amount}
          onRebuy={async () => {
            if (!tableId || !(table as any).tournamentId) return;
            setTournamentRebuying(true);
            try {
              const r = await gameApiService.tournamentRebuy((table as any).tournamentId, tableId);
              if (r.success) {
                setShowTournamentBust(false);
              } else {
                alert(r.error || 'Rebuy failed');
              }
            } catch (e: any) {
              alert(e?.message || 'Rebuy failed');
            } finally {
              setTournamentRebuying(false);
            }
          }}
          onLeave={() => {
            setShowTournamentBust(false);
            handleLeaveTable();
          }}
          onDismiss={() => setShowTournamentBust(false)}
        />
      )}

      {/* ADMIN TABLE CONTROLS - only on tournament tables, only for admins */}
      {isAdminUser && (table as any)?.tournamentId && (
        <AdminTableControls
          tournamentId={(table as any).tournamentId}
          tableId={tableId}
          tournamentStatus={tournamentData?.status}
          currentBlindLevel={tournamentData?.currentBlindLevel}
          simStage={(tournamentData as any)?.simPlan?.stage}
          currentPlayerName={
            (gameState?.players?.[gameState.currentPlayerIndex]?.name) || undefined
          }
        />
      )}
    </div>
  );
};

// Break-curtain navigation: a break must never trap the player. Their seat
// is safe while they browse or play cash - and if they're not back when
// play resumes, sit-out auto-fold keeps their table moving without them.
const BreakNavButtons: React.FC<{
  navigate: (p: string) => void;
  onSummary: () => void;
  tournamentId?: string;
}> = ({ navigate, onSummary, tournamentId }) => {
  // Leaving via the curtain is deliberate: stamp the same flag an explicit
  // leave uses so the tournament lobby doesn't instantly auto-route the
  // player straight back to this table. 'Join my table' clears it.
  const stampAndGo = (path: string) => {
    try {
      if (tournamentId) sessionStorage.setItem(`pss:leftTournament:${tournamentId}`, '1');
    } catch { /* noop */ }
    navigate(path);
  };
  return (
  <div style={{
    marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap',
    justifyContent: 'center', pointerEvents: 'auto', maxWidth: 480,
  }}>
    <button
      onClick={() => stampAndGo('/tables')}
      style={{
        background: 'linear-gradient(135deg, #f5d976, #d4af37 60%, #a97f1f)',
        border: 'none', color: '#181205', borderRadius: 18, padding: '10px 20px',
        fontWeight: 800, fontSize: 13, letterSpacing: '0.08em', cursor: 'pointer',
      }}
    >
      PLAY CASH GAMES
    </button>
    <button
      onClick={() => stampAndGo('/tournaments')}
      style={{
        background: 'transparent', border: '1px solid rgba(148,163,184,0.5)',
        color: '#cbd5e0', borderRadius: 18, padding: '10px 20px',
        fontWeight: 700, fontSize: 13, cursor: 'pointer',
      }}
    >
      LOBBY
    </button>
    <button
      onClick={onSummary}
      style={{
        background: 'transparent', border: '1px solid rgba(212,175,55,0.5)',
        color: '#fde68a', borderRadius: 18, padding: '10px 20px',
        fontWeight: 700, fontSize: 13, cursor: 'pointer',
      }}
    >
      SUMMARY
    </button>
    <div style={{ width: '100%', textAlign: 'center', color: '#64748b', fontSize: 11, marginTop: 6 }}>
      Your seat is safe. If you're not back when play resumes, you'll sit out
      and your hands fold automatically until you return.
    </div>
  </div>
  );
};

export default GamePage;